import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  hasReviewSupportingPagesGaps,
  reviewSupportingPagesColumns as buildReviewSupportingPagesColumns,
  type ReviewSupportingPagesColumn
} from '../../utils/review-supporting-pages';
import {
  supportingPagesColumnsReady,
  supportingPagesEmptyReady,
  supportingPagesSettling,
  type SupportingPagesGateState,
  type SupportingPagesLoadStatus
} from '../../utils/supporting-pages-render-gate';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  DataForSEOService,
  SupportingPagesV2EvalData,
  SupportingPagesV2RowResult,
  SupportingPagesByTopicSnapshotData,
  SerpRivalCoverageData,
  CompareSummary,
  CompareSummaryTarget,
  SectionCoverageRow,
  HeadingPathSegment,
  PageDemandSnapshot,
  PageHealthSnapshot,
  PageRealizationSnapshot,
  PageIntentDiagnosis,
  PageIntentDiagnosisBullet,
  PageSerpResultRow,
  PageSerpSnapshot,
  SecondaryPageHealthSnapshot
} from '../../services/dataforseo';
import { FeatureFlagService } from '../../services/feature-flag';
import { formatPageDemandSourceConfidence } from '../../utils/page-demand-display';
import {
  classifyPageHealth,
  effectiveHealthBandClass,
  effectiveHealthBandLabel,
  EffectiveHealthDisplayBand,
  pageHealthBandClass,
  pageHealthBandLabel,
  resolveEffectiveHealthBand
} from '../content-inventory/inventory-page-health';
import {
  formatInventoryRealizationNumber,
  formatInventoryRealizationRatio,
  inventoryRealizationGapTitle,
  inventoryRealizationRatioTitle
} from '../content-inventory/inventory-realization-sort';
import {
  buildPageSignalsForUrl,
  type InventoryPageSignal,
  type InventoryPageSignalKind
} from '../content-inventory/inventory-page-signals';
import {
  InventoryPlanClustersService,
  type ActionPlanCannibalizationRow,
  type ActionPlanInternalLinkRow,
  type ActionPlanUnclusteredPageRow,
  type ActionPlanV2Row
} from '../../services/inventory-plan-clusters.service';
import { PlanV2IssueQueueService } from '../../services/plan-v2-issue-queue.service';
import { PlanV2EmbedContextService } from '../../services/plan-v2-embed-context.service';
import { CrawlRunPeriodService, type CrawlRunPeriodView } from '../../services/crawl-run-period.service';
import { TechnicalRollupService } from '../../services/technical-rollup.service';
import {
  decidedSiteWideCodes,
  settledSiteWideLabel,
  splitSettledSiteWide
} from '../../utils/site-wide-suppression';
import { encodeWorkItemDetail } from '../../utils/plan-v2-work-item-display';
import { ReviewSelectionService } from '../../services/review-selection.service';
import {
  technicalEvidenceCopy,
  technicalReviewIssues,
  technicalScopeLabel
} from '../../utils/technical-evidence';
import {
  buildPlanV2ExpandPlaybook,
  findTopSerpRival,
  type PlanV2ExpandPlaybookStep
} from '../../utils/plan-v2-expand-playbook';
import { isNonCompetitorDomain } from '../../utils/non-competitor-domains';
import { compareDrawerTarget } from '../../utils/compare-evidence';
import {
  hasSectionGapsForV2Row,
  sectionGapLabel,
  sectionGapsForV2Row
} from '../../utils/section-gaps';
import {
  reviewMissingSections,
  type ReviewMissingSectionsView
} from '../../utils/review-missing-sections';
import {
  RANK_DRIVEN_GAPS_ENABLED,
  reviewRankGaps,
  type ReviewRankGapsView
} from '../../utils/review-rank-gaps';

/** Section headings are long; three is a glance, more is a list to read. */
const MAX_SECTION_CHIPS = 3;
/** Paths are short, so a couple more fit before the row stops being scannable. */
const MAX_INTERNAL_LINK_CHIPS = 4;
import {
  weakSerpCompareBadgeLabel,
  weakSerpCompareReason
} from '../../utils/weak-serp-compare-target';
import {
  comparePageFreshnessToSerp,
  formatFreshnessDateLabel,
  freshnessDiagnosisLabel as resolveFreshnessDiagnosisLabel,
  freshnessDiagnosisTone as resolveFreshnessDiagnosisTone,
  medianPublishedDate,
  pageContentLastModifiedLabel,
  parsePageContentLastModifiedDate,
  serpWinnerPublishedDatesFromResults
} from '../../utils/freshness-serp-compare';
import {
  buildFreshnessWorkItemContext,
  composeFreshnessWorkItem,
  composeInternalLinkWorkItem,
  composeInternalLinkWorkItemsFromSelection,
  buildInternalLinkRowDisplay,
  composeInternalLinksWorkItem,
  composeSerpIntentWorkItem,
  composeSerpPhraseWorkItem,
  composeInternalLinkWorkItemOnSourcePage,
  composeTechnicalIssuesWorkItem,
  internalLinkWorkItemIssueKey,
  formatInternalLinkRelevanceScore,
  internalLinkDirectionChipClass,
  internalLinkDirectionLabel,
  filterInternalLinksIncomingToPage,
  filterInternalLinksPendingIssuesTriage,
  internalLinkSuggestionKeysInWorkItems,
  listInternalLinkComposeOptions,
  splitPlacementByAnchor,
  listTechnicalIssueComposeOptions,
  buildTopicOwnershipComposeState,
  composeTopicOwnershipWorkItemFromState,
  topicOwnershipComposeHasSelection,
  topicOwnershipHeadingComposeId,
  type InternalLinkComposeOption,
  type TechnicalIssueComposeOption,
  type TopicOwnershipComposeState
} from '../../utils/plan-v2-work-item-compose';
import {
  buildPlanV2IssueKey,
  type PlanV2IssueCardPayload,
  type PlanV2IssueType
} from '../../utils/plan-v2-issues';
import {
  buildPageEvidencePanel,
  isPageEvidenceEmpty,
  type EvidenceTone,
  type PageEvidence,
  type PageEvidencePanel
} from '../../utils/page-evidence';
import { DomainContextService } from '../../services/domain-context';
import { CompareContextService } from '../../services/compare-context';
import { UrlSerpRefreshProgress, UrlSerpSyncService } from '../../services/url-serp-sync';
import {
  buildSerpFoldedTermSegments,
  buildSerpSnippetSegments,
  type SerpSnippetSegment
} from '../../utils/serp-snippet-segments';
import {
  buildCollapsedStructurePassageSegments,
  dedupeOverlappingStructurePassages,
  parseStructurePassageHeading
} from '../../utils/serp-structure-passage';
import {
  buildYourPageVocabularySegments,
  collectMisalignedPhraseLinks,
  foldLexicalToken,
  type MisalignedPhraseLink
} from '../../utils/serp-intent-highlight';
import { DomainTopicsService, ParentTopic } from '../../services/domain-topics';
import { InventoryQuickCompareDrawerComponent } from '../inventory-quick-compare-drawer/inventory-quick-compare-drawer';
import { PlanV2IssueCardActionsComponent } from '../plan-v2-issue-card-actions/plan-v2-issue-card-actions';
import { UrlRunComparisonComponent } from '../url-run-comparison/url-run-comparison';
import {
  buildPageTopicLabelsBySlug,
  extractTopicTaggedPagesFromDiagnostic,
  filterSupportingPagesByRelevance,
  filterCompetingEntityRowsFromRivalColumns,
  filterRowsByFocalAnchorTopics,
  formatSupportingPagePath,
  formatSupportingPageTraffic,
  getTopTopicsForSupportingPages,
  mergeTopicTaggedPagesByUrl,
  rowOverlapsFocalAnchorTopics,
  normalizeTopicSlug,
  normalizeUrlForCompare,
  SUPPORTING_PAGE_INITIAL_VISIBLE_COUNT,
  SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT,
  SUPPORTING_PAGE_MIN_RELEVANCE_SCORE,
  uniqueParentDiagnosticSlugs,
  compareSupportingPagesByRelevance,
  type DomainTopicUrlRow
} from '../../utils/topic-supporting-pages';
import { resolveFocalAnchorTopics } from '../../utils/supporting-pages-presenter';
import {
  orderTopicSignalsForClaim,
  topicSignalMatchesDemandClaim
} from '../../utils/page-demand-claim-alignment';
import {
  splitSectionTextByAbsentTerms,
  type SectionCompareHighlightSegment
} from '../../utils/section-compare-highlight';
import {
  buildSupportingPagesUnifiedColumns,
  buildSupportingPagesV2OnlyColumns,
  augmentSupportingPagesColumns,
  attachV2RowsToSupportingPageColumns,
  type SerpCompetitorColumnSeed,
  hasListInventoryGaps,
  hasMissingSectionGaps,
  hasRankDrivenGaps,
  type SupportingPagesUnifiedColumn
} from '../../utils/supporting-pages-unified-columns';
import {
  rankDrivenGapsForV2Row,
  type RankDrivenSectionGap
} from '../../utils/rank-driven-section-gaps';
import { splitPassageByKeywordTokens, rankDrivenHeadingTag } from '../../utils/keyword-passage-highlight';
import {
  attachSupportingPageSimilarityMatches,
  buildLexicalByNormalizedUrl,
  collectSupportingPageUrls,
  formatSupportingPageTokenPreview,
  PageLexicalFields,
  SUPPORTING_PAGE_SIMILARITY_MIN_SCORE
} from '../../utils/supporting-page-similarity';
import {
  buildOnpageTechnicalIssueViews,
  type OnpageIssueView
} from '../../utils/url-detail-onpage-issues';
import { catchError, forkJoin, map, of, Subscription, switchMap, take } from 'rxjs';
import { InFlightRequest } from '../../utils/in-flight-request';
import { reviewAnswerVocabGroups, type ReviewAnswerVocab } from '../../utils/review-answer-vocab';
import {
  sectionCompareNotice,
  type SectionCompareNotice
} from '../../utils/section-compare-unavailable';
import {
  serpPageTypeExamples,
  mixedSerpAlignment,
  type MixedSerpAlignment,
  type PageTypeExample
} from '../../utils/page-type-alignment';
import {
  missingSectionsCountLabel,
  missingSectionsFact
} from '../../utils/missing-sections-summary';

const DEMAND_DIAGNOSTICS_INFO: Record<string, string> = {
  'demand-and-realization-gap':
    'Dev-only breakdown of accountable demand vs how much visibility this URL still leaves on the table.',
  'demand-labeling':
    'Stored claim vs what rankings already associate with the page, plus ambient context when they disagree.',
  'realization-gap':
    'Health band and gap math comparing realized visibility to a keyword-backed demand ceiling.',
  claim: 'Subtopic demand this URL is accountable for—not the whole cluster or plan group.',
  'intent-tokens':
    'Slug/title modifiers (how, template, vs, etc.) that gate which keywords count toward the ceiling.',
  'claim-source': 'Where the claim came from and how confident we are (high, low, or none).',
  'claim-detail': 'Plain-language note from the labeling rule that chose this claim.',
  'market-realization': 'Best stored ranked keyword already aligned with the claim, if any.',
  'realization-source': 'Source for the realization line and its confidence.',
  'ambient-topic': 'Strongest on-page topic that conflicts with the claim; context only, not the claim.',
  'ambient-source': 'Source for the ambient topic signal and its confidence.',
  'split-reasons': 'Why claim, realization, or ambient topic diverge on this URL.',
  computed: 'When demand labels were last written for this URL.',
  'health-band':
    'Critical for HTTP errors; Weak, OK, or Best from scored gap; Unscored when gap is not scored.',
  'health-model': 'Realization-gap thresholds when scored; Unscored when gap is not scored; Eligibility for HTTP failures.',
  'health-reasons': 'Short rule lines showing which checks set the current band.',
  'gap-gate': 'Whether ratio and gap are scored, pending on confidence or immaterial ceiling, or withheld.',
  'realized-visibility':
    'ETV when rankings are missing or coherent with rank proxy; otherwise claim-aligned keyword traffic at actual positions.',
  'demand-ceiling': 'Max upside from aligned ranked keywords at the configured target rank.',
  'ceiling-used': 'Ceiling after floors—the denominator for the realization ratio.',
  'realization-ratio': 'Realized visibility divided by ceiling used; blank when gap is not scored.',
  gap: 'Ceiling used minus realized visibility; blank when gap is not scored.',
  'ceiling-driver': 'Aligned keyword with the largest upside at the target rank.',
  'driver-volume': 'Monthly search volume for the ceiling-driver keyword.',
  'driver-match-tokens': 'Claim intent tokens the driver keyword shares.',
  'upside-at-target-rank': 'Estimated traffic at the target rank (volume × CTR) for the driver.',
  'aligned-keywords-in-pool': 'Ranked keywords that pass claim and intent alignment for the ceiling.',
  'ranked-keywords': 'Stored Labs ranked keywords for this URL from the latest analysis snapshot.',
  'secondary-health':
    'When realization gap is unscored, a separate earned-visibility read without a demand ceiling.',
  'secondary-health-band': 'Weak, OK, or Best from claim-aligned earned visibility when secondary scoring applies.',
  'secondary-health-model': 'Whether secondary scoring is held, skipped, or uses earned visibility.',
  'secondary-health-reasons': 'Rule lines for the secondary model only; primary Health stays Unscored.',
  'secondary-unscored-segment': 'Why the primary gap is unscored (market data, ceiling pool, material floor).',
  'recently-updated-hold': 'Content modified within six months; secondary earned-visibility scoring is held.',
};

interface PageRankedKeywordRow {
  keyword: string;
  position: number;
  searchVolume: number | null;
}

/** Per-page topic signal (mirrors backend `TopicSignal`). */
interface PageTopicSignal {
  topicSlug: string;
  label: string;
  weight: number;
  salience: number;
  relativeDensity: number;
}

interface ContentElement {
  type: 'heading' | 'text' | 'link' | 'table';
  level?: number; // for headings (h1, h2, etc.)
  content: string;
  href?: string; // for links
  tableData?: any[]; // for table content
}

interface CompetitorTopicMatch {
  domain: string;
  authorityScore: number;
  pageCount: number;
  matchedParentTopicSlug: string;
}

/** Marked competitor ahead on a topic overlapping this page (for Improvements v1). */
interface ImprovementCompetitorLead {
  domain: string;
  authorityScore: number;
  pageCount: number;
  topicLabel: string;
  topicSlug: string;
}

interface CompetitorTopicUrlPreview {
  url: string;
  score: number;
  salience?: number;
  estimatedOrganicTraffic?: number;
}

interface CompetitorTopicTooltipState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  urls: CompetitorTopicUrlPreview[];
}

interface TopicOwnershipDomain {
  domain: string;
  isYourSite: boolean;
  rows: DomainTopicUrlRow[];
}

interface TopicOwnershipCompareOption {
  url: string;
  label: string;
  /**
   * Absent on the SERP-winner option, which does not come from the topic
   * snapshot and has no topical score — it is prepended rather than ranked.
   * Optional rather than 0 so it cannot be sorted or displayed as a measured
   * floor.
   */
  seoIntentScore?: number;
  focalMatchScore?: number;
}

interface SerpCompetitionMarkedAbove {
  position: number;
  domain: string;
  url: string;
  title: string;
}

interface SerpCompetitionTopLeader {
  position: number;
  domain: string;
  url: string;
  title: string;
  descriptionPreview: string;
  publishedLabel: string | null;
}

/** Top-3 SERP slot for Who's beating you? — includes social; sorted by position. */
interface SerpCompetitionDisplayRow extends SerpCompetitionTopLeader {
  isSocial: boolean;
  /** Homepage / login / app-shell — listed but not a compare target. */
  isWeakCompareTarget: boolean;
  weakCompareBadge: string | null;
  isMarked: boolean;
  intentLabel: string;
}

interface SerpCompetitionInsight {
  yourPosition: number | null;
  markedAboveYou: boolean;
  markedCompetitorsAbove: SerpCompetitionMarkedAbove[];
  topLeaders: SerpCompetitionTopLeader[];
}

type ClaimOverrideRefreshPhase = 'claim' | 'serp' | 'rival' | 'reload';

interface ClaimOverrideRefreshProgress {
  percent: number;
  statusLine: string;
  etaLabel: string | null;
  phase: ClaimOverrideRefreshPhase;
  indeterminate?: boolean;
}

export type UrlDetailEmbedPreset = 'planV2';

/**
 * The verdict a focus card's diagnosis chip reports.
 *
 * It drives two things that must not drift apart: the badge tone the chip
 * paints, and whether the Issues embed shows that card at all. Those used to
 * be one value -- a CSS class name -- and `embedDiagnosisIsGreen()` read the
 * verdict back out of it with `chipClass.includes('--ok')`. Renaming the class
 * during the component migration would have silently changed which cards an
 * operator sees in actions-only mode, with a green build and no failing test.
 * The tone is the value now; the class is derived from it.
 */
/**
 * Aliased onto `EvidenceTone` rather than re-declared. The two were the same
 * five words written twice, and the evidence panel SORTS on this union — a
 * tone added here and not there would order rows by a rank it does not have.
 */
type DiagnosisTone = EvidenceTone;

/** Tone -> the `.badge` modifier that paints it. `neutral` is the badge's own
 *  default, so it names nothing rather than naming a sixth tone.
 *
 *  `bad` is not a louder `warn`. A warning is "there is something to do here";
 *  `bad` is a measured loss — a rival is above you in the SERP right now. The
 *  badge has had a `--danger` tone since it was built and nothing reached for
 *  it, so every verdict on this screen read at one volume. */
const DIAGNOSIS_BADGE_CLASS: Record<DiagnosisTone, string> = {
  ok: 'badge--success',
  warn: 'badge--warning',
  bad: 'badge--danger',
  neutral: '',
  muted: 'badge--muted'
};

@Component({
  selector: 'app-url-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    InventoryQuickCompareDrawerComponent,
    PlanV2IssueCardActionsComponent,
    UrlRunComparisonComponent
  ],
  templateUrl: './url-detail.html',
  styleUrl: './url-detail.css'
})
export class UrlDetailComponent implements OnInit, OnChanges, OnDestroy, DoCheck {
  /** When set with `embedPreset`, renders URL focus cards inline (e.g. Plan v2 expand) instead of full page chrome. */
  @Input() embedUrl: string | null = null;
  @Input() embedPreset: UrlDetailEmbedPreset | null = null;
  /** Plan v2 expand: hide “all clear” cards (green diagnosis chips). Signals stay when present. */
  @Input() embedActionsOnly = false;
  @Input() embedPlanV2Domain: string | null = null;
  @Input() embedPlanV2Row: ActionPlanV2Row | null = null;
  /** 1-based rank within the active Issues queue (for playbook rationale). */
  @Input() embedPlanV2QueueRank: number | null = null;
  /** Plan v2 Issues drawer: full-height embed (no 70vh cap). */
  @Input() embedPlanV2InDrawer = false;

  /**
   * This host is a review surface: somewhere an operator decides, not digs.
   *
   * It turns on two families of behaviour. Claims are held back until nothing
   * can still change them, and the card is stripped to the verdict and the
   * decision — no methodology, no evidence columns, no controls that would take
   * the operator somewhere else to find out more.
   *
   * One flag rather than one per rule, because there is one distinction being
   * drawn and every rule follows from it. Set by review mode and the dashboard
   * peek; everything else keeps the deep-dive behaviour untouched.
   *
   * Deliberately NOT inferred from `embedPlanV2InDrawer`, which would have been
   * the convenient flag and is the wrong one: Content Inventory's Issues queue
   * opens that same drawer, and it is a place to dig.
   */
  @Input() embedReviewSurface = false;

  /**
   * Fires when this embed has finished the loading a reader would wait on.
   *
   * Exists so the review can prefetch the NEXT page once this one is done,
   * rather than starting both at once and making the visible page wait behind
   * the invisible one — the sidecar behind these evaluations is effectively
   * single-threaded (TECHNICAL_DEBT 0ad), so concurrent work is not free.
   *
   * An output rather than the host reaching in: the embed owns its loading,
   * and a host that re-derived "is it done" from the outside would be a second
   * definition of settled, drifting the moment either side changed.
   */
  @Output() embedSettledChange = new EventEmitter<boolean>();
  /**
   * Dashboard movers expand: same actionable Issue cards as the drawer, but
   * compacted (no triage buttons, no SERP tables / vocab grids). Implies
   * actions-only filtering when used with `embedPreset="planV2"`.
   */
  @Input() embedCompactPeek = false;

  /**
   * Whether the compact peek's evidence rows carry their own Dismiss / Work
   * item pair.
   *
   * Off on the Dashboard, where one Expand control replaces them: a peek that
   * offers triage on four rows without showing what is being triaged asks the
   * operator to act on a chip. Expanding first, then acting, is the same two
   * clicks with the evidence in between. Defaults to on so nothing that mounts
   * a peek without an opinion silently loses its actions.
   */
  @Input() embedPeekActions = true;
  /** Emitted after a successful intent-override cascade finishes and URL analysis is reloaded. */
  @Output() intentOverrideCompleted = new EventEmitter<{ urlId: string; url: string }>();

  @HostBinding('class.url-detail--embed-plan-v2')
  get hostEmbedPlanV2Class(): boolean {
    return this.isPlanV2Embed();
  }

  @HostBinding('class.url-detail--embed-plan-v2-drawer')
  get hostEmbedPlanV2DrawerClass(): boolean {
    return this.isPlanV2Embed() && this.embedPlanV2InDrawer;
  }

  @HostBinding('class.url-detail--embed-compact-peek')
  get hostEmbedCompactPeekClass(): boolean {
    return this.isCompactPeek();
  }

  /** Lets the stylesheet lay the review surface out without a flag per rule. */
  @HostBinding('class.url-detail--embed-review')
  get hostEmbedReviewClass(): boolean {
    return this.embedReviewSurface;
  }

  url: string | null = null;
  pageUrlId = '';
  planClusters: string[] = [];
  pageSignals: InventoryPageSignal[] = [];
  pageSignalsLoading = false;
  private cannibalSignalRows: ActionPlanCannibalizationRow[] = [];
  private internalLinkSignalRows: ActionPlanInternalLinkRow[] = [];
  private unclusteredSignalRows: ActionPlanUnclusteredPageRow[] = [];
  private signalsSubscription: Subscription | null = null;
  analysisData: any = null;
  pageDemand: PageDemandSnapshot | null = null;
  pageRealization: PageRealizationSnapshot | null = null;
  pageHealth: PageHealthSnapshot | null = null;
  pageSecondaryHealth: SecondaryPageHealthSnapshot | null = null;
  pageSerp: PageSerpSnapshot | null = null;
  pageIntentDiagnosis: PageIntentDiagnosis | null = null;
  /** Claim override modal (P2). */
  claimOverrideOpen = false;
  claimOverrideLabel = '';
  claimOverrideSaving = false;
  claimOverrideError: string | null = null;
  /** Shown on SERP card after override when claim saved but refresh partially failed. */
  claimOverrideNotice: string | null = null;
  /** Staged progress for intent override — surfaced on Expand playbook. */
  claimOverrideProgress: ClaimOverrideRefreshProgress | null = null;
  /** Vocabulary chip hover — highlights folded matches in winner snippets. */
  hoveredVocabChip: string | null = null;
  /** Winner excerpts: collapsed = top 5; expanded = full API pool (scrolls in the box). */
  winnerExcerptsExpanded = false;
  markedCompetitorDomains: string[] = [];
  urlSerpRefreshRunning = false;
  urlSerpForceRefresh = false;
  urlSerpRefreshProgress: UrlSerpRefreshProgress | null = null;
  pageRefetchRunning = false;
  pageRefetchStatus: string | null = null;
  private pageRefetchPollTimer: ReturnType<typeof setTimeout> | null = null;
  serpSectionExpanded = false;
  pageTopicsExpanded = false;
  topicOwnershipStatus: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  topicOwnershipDomains: TopicOwnershipDomain[] = [];
  /** Per-domain expand for supporting-page tables (dev view). */
  private topicOwnershipPagesExpandedByDomain = new Set<string>();
  /** User view: one expand control for the whole supporting-pages card. */
  topicOwnershipUserExpanded = false;
  topicOwnershipCompareUrl = '';
  private topicOwnershipLoadGen = 0;
  /**
   * Which analysis load is the current one (TECHNICAL_DEBT §0ai).
   *
   * The belt keeps ONE instance of this component and changes `embedUrl`, so
   * two steps taken inside one round-trip put two analysis requests in flight
   * on the same instance. Without this counter the LAST response to arrive
   * wins, and that can be the older one: page 8 rendered with page 9's SERP
   * and quoted page 9's text as "how you answer". It also silently suppressed
   * page 8's rival evaluation, because `ensureMarkedSerpCompetitorEvals`
   * seeds itself from `pageSerp`.
   *
   * The by-topic and opportunity loads have carried this guard for the same
   * reason; the analysis load — the one that fills the whole card — did not.
   */
  private analysisLoadGen = 0;
  /** Best-effort cancel of a superseded analysis request; the counter above is what makes it correct. */
  private analysisSubscription?: Subscription;
  /** True when no precomputed snapshot exists — build from Crawl Management (or dev rebuild). */
  topicOwnershipCacheMiss = false;
  topicOwnershipSnapshotGeneratedAt: string | null = null;
  supportingPagesV1RebuildRunning = false;
  supportingPagesOppRebuildRunning = false;
  supportingPagesRebuildStatus: string | null = null;
  /** Supporting-page opportunity scoring (embedding + section gaps). */
  topicOwnershipV2Status: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  topicOwnershipV2Data: SupportingPagesV2EvalData | null = null;
  topicOwnershipV2Error: string | null = null;
  /** On-demand section-gap eval for marked competitors on this page's SERP. */
  markedSerpCompetitorV2Rows: SupportingPagesV2RowResult[] = [];
  /**
   * A live marked-competitor evaluation is in flight.
   *
   * Tracked because it is a THIRD source for the Supporting pages card, behind
   * the snapshot and the opportunity snapshot, and unlike those two it lands
   * after the card has already reported itself ready. On a deep-dive surface
   * that is fine — rows arriving is the page filling in. On the review surface
   * it means a verdict changes while the operator is reading it.
   */

  /**
   * The in-flight marked-competitor evaluation, so leaving the page cancels it.
   *
   * It was untracked, which meant every page advance left its request running.
   * The belt destroys and rebuilds this component on every step — twice over,
   * once for the visible page and once for the prefetched one — so walking ten
   * pages quickly left a queue of abandoned evaluations against a sidecar that
   * is effectively single-threaded (TECHNICAL_DEBT §0ad), and the page actually
   * on screen waited behind all of them. Reported as "over 2-3 minutes".
   */
  /**
   * The Supporting pages opportunity evaluation — the third and slowest source
   * for that card, and the one the review surface waits on.
   *
   * `InFlightRequest` rather than a generation counter, a subscription and a
   * pending flag as three fields: those three desynced twice here, both times
   * leaving the surface waiting on a request that had been abandoned. See the
   * note on the class.
   */
  private readonly markedSerpEval = new InFlightRequest();
  topicOwnershipV2DetailUrl = '';
  /** Precomputed "Top SERP rival" coverage for this page (from the Setup step). */
  serpRivalCoverage: SerpRivalCoverageData | null = null;
  /** Resolved compare target + its facts, from `GET /urls/analysis`. */
  pageCompare: CompareSummary | null = null;
  topicOwnershipV2CoveredElsewhereUrl = '';
  topicOwnershipV2ListInventoryUrl = '';
  topicOwnershipV2RankDrivenUrl = '';
  topSerpRivalRankDrivenOpen = false;
  topicOwnershipV2TextUrl = '';
  topicOwnershipV2CalUrl = '';
  /** Collapsed side-by-side section compare (competitorUrl + sectionNumber); shown by default. */
  private topicOwnershipV2SectionCompareCollapsedKeys = new Set<string>();
  /** Compare detail filter for section coverage rows. */
  topicOwnershipV2CompareFilter: 'not_on_page' | 'gaps' | 'all' = 'not_on_page';
  private topicOwnershipV2LoadGen = 0;
  private supportingPageLexicalByUrl = new Map<string, PageLexicalFields>();
  /** Focal-page anchor topics — narrows your-site matcher pools (dev + opportunity scoring). */
  focalAnchorTopicSlugs = new Set<string>();
  focalAnchorTopicLabels: string[] = [];
  /** Documented in topic-supporting-pages.ts — exposed for template hint. */
  readonly supportingPageMinRelevance = SUPPORTING_PAGE_MIN_RELEVANCE_SCORE;
  readonly supportingPageSimilarityMin = SUPPORTING_PAGE_SIMILARITY_MIN_SCORE;
  analysisStatus: 'idle' | 'loading' | 'success' | 'error' = 'idle';
  contentSectionCollapsed: boolean = true;
  linksSectionCollapsed: boolean = true;
  /** Expanded on-page issue detail rows (key: `critical:0` / `warning:1`). */
  private technicalIssueDetailsExpanded = new Set<string>();
  technicalCriticalComposing = false;
  topicOwnershipComposing = false;
  topicOwnershipComposeState: TopicOwnershipComposeState | null = null;
  internalLinksComposing = false;
  @ViewChild('internalLinksSection') internalLinksSectionRef?: ElementRef<HTMLElement>;
  @ViewChild('serpCompareYours') serpCompareYoursRef?: ElementRef<HTMLElement>;
  @ViewChild('serpCompareWinners') serpCompareWinnersRef?: ElementRef<HTMLElement>;
  technicalWarningsComposing = false;
  contentElements: ContentElement[] = [];
  
  // Link data
  internalLinks: any[] = [];
  linkFilter: 'all' | 'structural' | 'contextual' | 'same_page' = 'all';
  linkSort: 'type' | 'confidence' | 'anchor' | 'url' = 'type';
  competitorCoverageStatus: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  competitorCoverageMessage: string = '';
  private competitorTopicMatchesBySlug: Record<string, CompetitorTopicMatch[]> = {};
  private competitorTopicTooltipCache: Record<string, CompetitorTopicTooltipState> = {};
  private activeCompetitorTooltipKey: string | null = null;
  private competitorTooltipHideTimer: ReturnType<typeof setTimeout> | null = null;

  quickCompareOpen = false;
  quickComparePrimaryUrl: string | null = null;
  quickCompareCompareUrl: string | null = null;

  planV2NextAction: ActionPlanV2Row | null = null;
  planV2NextActionLoading = false;
  planV2NextActionError: string | null = null;
  /**
   * The crawl pair the Next action is being read against (LB1) — the same
   * period string the dashboard and the Issues header show. Null when this
   * domain has no captured pair; the section then says nothing about periods
   * rather than dating advice it cannot date.
   */
  planV2Period: CrawlRunPeriodView | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataForSEOService: DataForSEOService,
    private compareContextService: CompareContextService,
    private domainTopicsService: DomainTopicsService,
    private featureFlags: FeatureFlagService,
    private urlSerpSync: UrlSerpSyncService,
    private inventoryPlanClusters: InventoryPlanClustersService,
    private domainContext: DomainContextService,
    readonly planV2IssueQueue: PlanV2IssueQueueService,
    private readonly reviewSelection: ReviewSelectionService,
    private readonly planV2EmbedContext: PlanV2EmbedContextService,
    private readonly crawlRunPeriod: CrawlRunPeriodService,
    private readonly technicalRollup: TechnicalRollupService
  ) {}

  isDevView(): boolean {
    return !this.featureFlags.isProduction() && !this.featureFlags.isUserView();
  }

  /** User-mode columns/links layout (also used for Plan v2 embed regardless of dev flag). */
  useTopicOwnershipUserLayout(): boolean {
    return !this.isDevView() || this.isPlanV2Embed();
  }

  isActionPlanV2Enabled(): boolean {
    return this.featureFlags.isEnabled('actionPlanV2');
  }

  isEmbedMode(): boolean {
    return this.embedPreset === 'planV2';
  }

  isPlanV2Embed(): boolean {
    return this.embedPreset === 'planV2';
  }

  isCompactPeek(): boolean {
    return this.isPlanV2Embed() && this.embedCompactPeek;
  }

  /**
   * Dashboard package row, expanded: one EVIDENCE row per diagnosis.
   *
   * This used to return a chip per signal — `GAP FOUND`, `NO RIVALS AHEAD` —
   * and nothing else, which meant the panel cost a click to open and answered
   * nothing. Every row now carries the number behind its verdict, and the ones
   * worth acting on carry the same Dismiss / Work item pair the Issues drawer
   * has, so triage happens here instead of three screens away.
   *
   * The wording is composed here, beside the accessors it reads.
   * `buildPageEvidencePanel` owns only the ordering, the cap and the bucketing.
   */
  compactPeekEvidence(): PageEvidence[] {
    if (!this.isCompactPeek() || this.analysisStatus !== 'success' || !this.analysisData) {
      return [];
    }
    const rows: PageEvidence[] = [];

    // There is no "Compare" row. Word counts and ranked-keyword totals are
    // things to read, not things to do — "their page is longer" leaves an
    // operator with no next move, and the Competition row already answers
    // rank. The comparison survives as an action (`compareRowTargetUrl`, the
    // button at the top of the panel) and as the one part of it that IS work:
    // the sections below, by name.
    const missingSections = this.evidenceMissingSections();
    if (missingSections) rows.push(missingSections);

    const competition = this.evidenceCompetition();
    if (competition) rows.push(competition);

    const structure = this.evidenceAnswerStructure();
    if (structure) rows.push(structure);

    const supporting = this.evidenceSupportingPages();
    if (supporting) rows.push(supporting);

    const pageType = this.evidencePageType();
    if (pageType) rows.push(pageType);

    const freshness = this.evidenceFreshness();
    if (freshness) rows.push(freshness);

    const links = this.evidenceInternalLinks();
    if (links) rows.push(links);

    const technical = this.evidenceTechnical();
    if (technical) rows.push(technical);

    return rows;
  }

  private peekPanelCache: PageEvidencePanel | null = null;
  private peekPanelKey = '';

  /**
   * Ordered, capped and bucketed. The template reads this, not the list.
   *
   * MEMOISED, and that is not an optimisation. A method returning a fresh
   * object on every call hands `*ngFor` a new array identity on every
   * change-detection pass, so every row re-renders on every pass — the same
   * trap the Dashboard's coverage series hit, where it hung the page. The key
   * below is every input the seven diagnoses read that can change after load;
   * when it is unchanged the previous panel object is returned by identity.
   */
  compactPeekPanel(): PageEvidencePanel {
    const key = [
      this.url,
      this.analysisStatus,
      this.analysisData ? 1 : 0,
      this.topicOwnershipStatus,
      this.topicOwnershipV2Status,
      this.markedCompetitorDomains.length,
      this.pageSerp?.fetchedAt ?? '',
      // Both parts matter: the state decides which of the seven sentences the
      // row shows, and the target URL decides which page it names.
      this.pageCompare?.state ?? '',
      this.pageCompare?.target?.url ?? '',
      // Arrives after first paint on its own request, and the Missing sections
      // row is built from it — without this the row never appears.
      this.serpRivalCoverage?.generatedAt ?? '',
      // Same again for page signals, which feed the Internal links row. This
      // was missing, which is why that row never showed in the peek even on
      // pages that had suggestions: the panel was memoised before the signals
      // request came back and nothing invalidated it afterwards.
      this.pageSignalsLoading ? 'loading' : 'loaded',
      this.internalLinkSignalRows.length,
      this.pageIntentDiagnosis ? 1 : 0,
      // A dismissed issue must drop its buttons on the next pass.
      this.planV2IssueQueue.records.size
    ].join('|');

    if (!this.peekPanelCache || key !== this.peekPanelKey) {
      this.peekPanelKey = key;
      this.peekPanelCache = buildPageEvidencePanel(this.compactPeekEvidence());
    }
    return this.peekPanelCache;
  }

  compactPeekPanelEmpty(): boolean {
    return isPageEvidenceEmpty(this.compactPeekPanel());
  }

  /* ── The seven diagnoses ────────────────────────────────────────────
     Each returns null when its section is not shown at all, and otherwise a
     row with a fact that names a number. "No gap" is a finding and gets one;
     so does "not built", which is a different finding wearing the same
     colour. */

  /**
   * The sections the rival covers and this page does not — by name.
   *
   * Split out of the Compare row, which used to end on "They cover 2 sections
   * yours never mentions" and leave the operator to go and find out which. A
   * count is a verdict; the headings are the work.
   *
   * The selection is `sectionGapsForV2Row`, the same function the Expand panel
   * and the work-item composer use, so the recap cannot name a different set
   * from the panel behind it. It is deliberately not `missingSectionCount` off
   * the stored row: that counts groups before the listicle and absent-term
   * filters, and disagrees on 22 of 326 live snapshots.
   */
  private evidenceMissingSections(): PageEvidence | null {
    const gaps = sectionGapsForV2Row(this.serpRivalEvalRow());
    // Rank-driven gaps too. The card on the belt has always shown both under
    // this title; this row counted only sections, so a page with three queries
    // and no section gaps showed a full card on the belt and NOTHING here —
    // the Dashboard being the first read of that page, its only content
    // finding was dropped exactly where it mattered most.
    const rankGaps = this.topSerpRivalRankDrivenGaps();
    if (gaps.length === 0 && rankGaps.length === 0) return null;

    const domain = this.pageCompare?.target?.domain || 'The top result';
    // The queries lead when there are no sections, because then they are the
    // finding; otherwise sections keep the front of a short chip row.
    const specifics = [
      ...gaps.map((s) => sectionGapLabel(s)),
      ...rankGaps.map((g) => (g.keyword || '').trim()).filter(Boolean)
    ];

    return {
      key: 'missing_sections',
      // Same rule as the belt's card — a row set that is all "you have this,
      // it is thin" is not a missing section on either surface.
      title:
        gaps.some((g) => !g.sectionCovered) || rankGaps.some((g) => !g.sectionCovered)
          ? 'Missing sections'
          : 'Thin sections',
      label: missingSectionsCountLabel(gaps.length, rankGaps.length) ?? '',
      tone: 'warn',
      fact: missingSectionsFact(domain, gaps.length, rankGaps.length),
      specifics: specifics
        .slice(0, MAX_SECTION_CHIPS)
        .concat(
          specifics.length > MAX_SECTION_CHIPS
            ? [`+${specifics.length - MAX_SECTION_CHIPS}`]
            : []
        ),
      // Triage lives on the topic-ownership issue, which is what a section gap
      // resolves into; a second payload here would double-count it in the queue.
      payload: null
    };
  }

  /** The page the peek's Compare button opens on, or null when there is none. */
  compareRowTargetUrl(): string | null {
    return compareDrawerTarget(this.pageCompare)?.url ?? null;
  }

  /** Names the rival on the button, so the recap still says who without a row. */
  compareRowTargetDomain(): string | null {
    return compareDrawerTarget(this.pageCompare)?.domain ?? null;
  }

  /**
   * Open the comparison already pointed at the resolved target (§3.1).
   *
   * The dropdown in the Supporting pages card becomes an override rather than a
   * prerequisite — the operator no longer has to know which of the rival's
   * pages is the real peer, which is the decision §2 says they are not well
   * placed to make.
   */
  openCompareFromPeek(): void {
    const url = this.compareRowTargetUrl();
    if (url) this.openQuickCompareWithUrl(url);
  }

  private evidenceCompetition(): PageEvidence | null {
    if (!this.showEmbedSerpCompetitionCard()) return null;
    const tone = this.serpCompetitionChipTone();
    const insight = this.serpCompetitionInsight();
    // Not `serpCompetitionYourRankLabel()`: that returns a CHIP value -- "#4"
    // or "Not in top 10" -- and "You hold Not in top 10" is what happens when
    // a label built for a table cell is dropped into a sentence.
    const pos = this.pageSerp?.yourPosition;
    const rank =
      pos === null || pos === undefined ? 'You are not in the top 10' : `You hold #${pos}`;
    const specifics: string[] = [];

    for (const row of this.serpCompetitionTopThreeRows().slice(0, 3)) {
      specifics.push(`${row.domain} #${row.position}`);
    }

    let fact: string;
    if (!this.markedCompetitorDomains.length) {
      // Not a result: nothing was marked, so nobody above you counts as a rival.
      fact = `${rank}. Nothing is marked as a competitor for this domain, so nobody above you counts as one.`;
    } else if (!insight) {
      fact = `${rank}. No SERP snapshot to compare marked rivals against.`;
    } else if (insight.markedAboveYou) {
      const above = insight.markedCompetitorsAbove?.length ?? 0;
      fact = `${rank}. ${above === 1 ? '1 marked rival is' : `${above} marked rivals are`} above you.`;
    } else if (insight.yourPosition === 1) {
      fact = `${rank}. No marked rival is above you.`;
    } else {
      const unmarked = this.serpUnmarkedDomainsAbove();
      fact =
        `${rank}. No marked rival is above you` +
        (unmarked > 0
          ? `, though ${unmarked === 1 ? '1 unmarked domain does' : `${unmarked} unmarked domains do`}.`
          : '.');
    }

    return {
      key: 'serp_competition',
      title: 'Competition',
      label: this.serpCompetitionChipLabel(),
      tone,
      fact,
      specifics,
      payload: tone === 'bad' || tone === 'warn' ? this.buildSerpCompetitionIssuePayload() : null
    };
  }

  /**
   * Domains ranking above you that are NOT marked competitors.
   *
   * The number that makes "no rivals ahead" mean something: it is only good
   * news if the field above you is empty too, and usually it is not.
   */
  private serpUnmarkedDomainsAbove(): number {
    const you = this.pageSerp?.yourPosition;
    if (you === null || you === undefined) return 0;
    const seen = new Set<string>();
    for (const row of this.pageSerp?.results || []) {
      if (row.position >= you) continue;
      if (this.isMarkedCompetitorDomain(row.domain)) continue;
      seen.add(this.normalizeSerpDomain(row.domain));
    }
    return seen.size;
  }

  private evidenceAnswerStructure(): PageEvidence | null {
    if (!this.showEmbedSerpPhraseCard()) return null;
    const d = this.pageIntentDiagnosis;
    if (!d) return null;

    const missing = d.missingTerms || [];
    const shared = d.serpLexicalTerms || [];
    const serpStructure = this.answerStructureFamilyLabel(d.answerStructureFamily);
    const pageStructure = this.answerStructureFamilyLabel(d.answerStructurePageFamily);
    const structureMismatch =
      Boolean(d.answerStructureFamily) &&
      d.answerStructureFamily !== 'unknown' &&
      d.answerStructureFamily !== 'fragmented' &&
      d.answerStructureAligned === false;

    let fact: string;
    if (structureMismatch) {
      fact = `The SERP answers in a ${serpStructure}; your page reads as ${
        pageStructure === 'unknown' ? 'none of the SERP\'s shapes' : `a ${pageStructure}`
      }.`;
    } else if (missing.length > 0) {
      fact = shared.length
        ? `${missing.length} of the SERP's ${shared.length} shared terms never appear on your page.`
        : `${missing.length} terms the SERP shares never appear on your page.`;
    } else if (this.showFragmentedSerpVocabularyNote()) {
      fact =
        'The top results share no vocabulary, so there is nothing to match against. Read the SERP before spending effort here.';
    } else {
      fact = shared.length
        ? `${serpStructure === 'unknown' ? 'Structure' : `A ${serpStructure}`}, matched. All ${shared.length} shared SERP terms are present.`
        : 'Structure and vocabulary match the SERP.';
    }

    const tone = this.serpPhraseMatchActionChipTone();
    return {
      key: 'serp_phrase',
      title: 'Answer structure',
      label: this.serpPhraseMatchActionChipLabel(),
      tone,
      fact,
      specifics: missing.slice(0, 3).concat(missing.length > 3 ? [`+${missing.length - 3}`] : []),
      payload: tone === 'bad' || tone === 'warn' ? this.buildSerpPhraseIssuePayload() : null
    };
  }

  private evidenceSupportingPages(): PageEvidence | null {
    if (!this.showEmbedSupportingPagesCard()) return null;

    // Never ran is not the same as passed, and shares a colour with it.
    if (this.topicOwnershipStatus !== 'ready' && this.topicOwnershipV2Status !== 'ready') {
      return {
        key: 'topic_ownership',
        title: 'Supporting pages',
        label: 'NOT BUILT',
        tone: 'muted',
        fact: 'No supporting-pages snapshot for this URL yet, so this check has not run.',
        specifics: [],
        payload: null
      };
    }

    const byDomain = this.supportingPagesGapCountsByDomain();
    const unmatched = byDomain.reduce((n, d) => n + d.count, 0);
    // This row is about pages, so its verdict is about pages. The broad
    // predicate counts sections and rank gaps too, which would badge GAP FOUND
    // over the sentence "Every competitor supporting page on this topic has a
    // match on your site" — a row contradicting itself.
    const gap = unmatched > 0;

    // Sections are not mentioned here. The peek has a "Missing sections" row of
    // its own directly above this one, and a supporting-pages fact that also
    // counts sections reports the same finding twice under two headings — the
    // conflation the review cards were just split to undo.
    const fact = gap
      ? `${unmatched === 1 ? '1 competitor page' : `${unmatched} competitor pages`} on this topic ` +
        `${unmatched === 1 ? 'has' : 'have'} no match on your site.`
      : 'Every competitor supporting page on this topic has a match on your site.';

    return {
      key: 'topic_ownership',
      title: 'Supporting pages',
      label: gap ? 'GAP FOUND' : 'NO GAP',
      tone: gap ? 'warn' : 'ok',
      fact,
      // "productive.io (2)" reads as a rank, an index, a version — anything but
      // a page count, and the sentence directly above it is counting pages.
      // The unit is cheap and the ambiguity is not.
      specifics: byDomain
        .slice(0, 3)
        .map((d) => `${d.domain} (${d.count} ${d.count === 1 ? 'page' : 'pages'})`),
      payload: gap ? this.buildTopicOwnershipIssuePayload() : null
    };
  }

  /**
   * Marked competitors with supporting pages you have no match for.
   *
   * Read from the same unified columns the card renders, rather than counted
   * again off the raw domain blocks. Counting twice is how the peek came to say
   * three pages where the card listed two: the raw block includes the domain's
   * HERO — its main competing page, the one the peer comparison is against —
   * and the columns exclude it, because a page you are already being compared
   * to is not a page you are missing.
   *
   * The peek and the card must agree about which pages you do not have. Two
   * surfaces disagreeing on that is worse than either of them being sparse, and
   * the only way to guarantee it is to have one of them do the arithmetic.
   */
  private supportingPagesGapCountsByDomain(): Array<{ domain: string; count: number }> {
    return this.reviewSupportingPagesColumns()
      .map((col) => ({ domain: col.domain, count: col.gaps.length }))
      .filter((row) => row.count > 0)
      .sort((a, b) => b.count - a.count || a.domain.localeCompare(b.domain));
  }

  private evidencePageType(): PageEvidence | null {
    if (!this.showEmbedSerpIntentCard()) return null;
    const d = this.pageIntentDiagnosis;
    if (!d) return null;

    const lines = this.pageTypeBriefLines();
    const counts = this.serpRowIntentCounts();
    const total = d.serpRowIntents?.length ?? 0;

    let fact: string;
    if (d.serpFamily === 'mixed' && this.pageTypeMixedAlignment()) {
      fact = lines[0] || this.pageIntentMatchActionChipLabel();
    } else if (d.serpFamily === 'mixed' && counts.length >= 2) {
      // `pageTypeBriefLines()` says "Pick one page type to align with" here,
      // which is an instruction rather than a finding -- and a mixed SERP is
      // filed under Clear, where an instruction reads as a contradiction. The
      // split is the finding.
      fact = `Top results disagree on page type — ${counts
        .slice(0, 3)
        .map((c) => `${c.n} ${this.pageIntentFamilyLabel(c.family)}`)
        .join(', ')}.`;
    } else {
      fact = lines[0] || this.pageIntentMatchActionChipLabel();
      if (d.serpFamily !== 'mixed' && d.serpFamily !== 'unknown' && total > 0 && counts.length) {
        fact = `${fact} ${counts[0].n} of ${total} results agree.`;
      }
    }

    const tone = this.pageIntentMatchActionChipTone();
    return {
      key: 'serp_intent',
      title: 'Page type',
      label: this.pageIntentMatchActionChipLabel(),
      tone,
      fact,
      // Empty on a mixed SERP: the fact above already lists the split, and a
      // chip row repeating it is the same sentence twice.
      specifics:
        d.serpFamily === 'mixed'
          ? []
          : counts.slice(0, 3).map((c) => `${c.n} ${this.pageIntentFamilyLabel(c.family)}`),
      payload: tone === 'bad' || tone === 'warn' ? this.buildSerpIntentIssuePayload() : null
    };
  }

  /** How the top results split across page types, commonest first. */
  private serpRowIntentCounts(): Array<{ family: string; n: number }> {
    const counts = new Map<string, number>();
    for (const row of this.pageIntentDiagnosis?.serpRowIntents || []) {
      if (!row.family || row.family === 'unknown') continue;
      counts.set(row.family, (counts.get(row.family) || 0) + 1);
    }
    return [...counts.entries()]
      .map(([family, n]) => ({ family, n }))
      .sort((a, b) => b.n - a.n || a.family.localeCompare(b.family));
  }

  private evidenceFreshness(): PageEvidence | null {
    if (!this.showEmbedFreshnessSection()) return null;
    const compare = comparePageFreshnessToSerp(
      this.pageContentLastModifiedDate(),
      this.serpWinnerPublishedDates()
    );
    const tone = this.freshnessDiagnosisTone();
    const specifics: string[] = [];
    const mine = this.pageContentLastModifiedDate();
    if (mine) specifics.push(`yours ${formatFreshnessDateLabel(mine)}`);
    if (compare.serpTypicalDateLabel) specifics.push(`SERP typical ${compare.serpTypicalDateLabel}`);

    return {
      key: 'freshness',
      title: 'Freshness',
      label: this.freshnessDiagnosisLabel(),
      tone,
      fact:
        compare.verdict ||
        'No last-modified date was found for this page, so freshness was not compared.',
      specifics,
      payload: tone === 'bad' || tone === 'warn' ? this.buildFreshnessIssuePayload() : null
    };
  }

  private evidenceInternalLinks(): PageEvidence | null {
    // Holds its place while the signals are in flight.
    //
    // The peek cannot start the signal calls until its own analysis call
    // returns, so this row used to appear a beat after the rest of the card and
    // shove Technical down under the reader's eyes. A row that is there from
    // the first paint, saying it is working, costs nothing and moves nothing.
    //
    // The Dashboard warms these before anyone opens a row, so on that screen
    // this state is usually skipped entirely — it is the honest fallback for a
    // cold cache, not the normal path.
    if (this.pageSignalsLoading && this.isPlanV2Embed()) {
      return {
        key: 'internal_link',
        title: 'Internal links',
        label: 'CHECKING',
        tone: 'muted',
        fact: 'Looking for related pages that could link here…',
        specifics: [],
        payload: null
      };
    }

    if (!this.showEmbedInternalLinksSection()) return null;
    const rows = this.pageSignalInternalLinkDetailsPendingTriage();
    if (rows.length === 0) return null;

    return {
      key: 'internal_link',
      title: 'Internal links',
      label: rows.length === 1 ? '1 LINK' : `${rows.length} LINKS`,
      tone: 'warn',
      fact: `We found ${
        rows.length === 1 ? '1 related page' : `${rows.length} related pages`
      } on your site that could link here and ${rows.length === 1 ? 'does' : 'do'} not.`,
      // Path only. The domain is your own on every one of these, so repeating
      // it costs the width the paths need.
      specifics: rows
        .slice(0, MAX_INTERNAL_LINK_CHIPS)
        .map((row) => this.compactPeekPath(row.sourceUrl))
        .concat(
          rows.length > MAX_INTERNAL_LINK_CHIPS
            ? [`+${rows.length - MAX_INTERNAL_LINK_CHIPS}`]
            : []
        ),
      payload: this.buildSignalsIssuePayload()
    };
  }

  /**
   * The Technical row.
   *
   * The wording lives in `utils/technical-evidence.ts` — it has three states
   * that turn on whether the domain rollup has answered and what is left of the
   * page's list once the template's own defects are set aside, which is worth a
   * test rather than a click-through. This picks the severity bucket and the
   * triage payload; that decides what the row says.
   */
  private evidenceTechnical(): PageEvidence | null {
    const critical = this.technicalCriticalIssueViews();
    const warnings = this.technicalWarningIssueViews();
    if (!critical.length && !warnings.length) return null;

    const useCritical = critical.length > 0;
    const copy = technicalEvidenceCopy({
      issues: useCritical ? critical : warnings,
      siteWideCodes: this.siteWideIssueCodes,
      severity: useCritical ? 'critical' : 'warning'
    });

    return {
      key: useCritical ? 'technical_critical' : 'technical_warnings',
      title: 'Technical',
      label: copy.label,
      tone: copy.tone,
      fact: copy.fact,
      specifics: copy.specifics,
      payload: useCritical
        ? this.buildTechnicalCriticalIssuePayload()
        : this.buildTechnicalWarningsIssuePayload()
    };
  }

  /** Path only, for a specifics chip. The origin is the page you are on. */
  private compactPeekPath(url: string): string {
    try {
      return new URL(url).pathname || '/';
    } catch {
      return url;
    }
  }

  compactPeekReady(): boolean {
    if (!this.isCompactPeek()) return false;
    if (this.analysisStatus === 'loading') return false;
    if (this.analysisStatus !== 'success' || !this.analysisData) return true;
    // Wait for supporting-pages / intent when still loading so chips don't flash empty.
    if (this.topicOwnershipStatus === 'loading' || this.topicOwnershipV2Status === 'loading') {
      return false;
    }
    return true;
  }

  embedActionsOnlyActive(): boolean {
    return this.isPlanV2Embed() && (this.embedActionsOnly || this.embedCompactPeek);
  }

  /** The badge tone class for a diagnosis chip. The template binds this; the
   *  visibility predicates below bind the tone itself. */
  diagnosisChipClass(tone: DiagnosisTone): string {
    return DIAGNOSIS_BADGE_CLASS[tone];
  }

  private embedDiagnosisIsGreen(tone: DiagnosisTone): boolean {
    return tone === 'ok';
  }

  /**
   * `bad` is here for the same reason `warn` is, and its absence would be a
   * silent regression: actions-only mode shows a card only when its diagnosis
   * is something to act on, so a tone that means "a rival is already ahead of
   * you" dropping out of this list would hide the card exactly when it matters
   * most. `ok` and `muted` stay out — nothing to do, and nothing measured.
   */
  private embedDiagnosisIsActionable(tone: DiagnosisTone): boolean {
    return tone === 'warn' || tone === 'bad' || tone === 'neutral';
  }

  showEmbedMiniOverviewSection(): boolean {
    return this.isPlanV2Embed() && !this.embedActionsOnlyActive();
  }

  showPlanV2ExpandPlaybook(): boolean {
    return (
      this.isPlanV2Embed() &&
      this.embedPlanV2Row?.recommendedAction.type === 'prioritize'
    );
  }

  planV2ExpandPlaybook() {
    if (!this.embedPlanV2Row) return null;
    return buildPlanV2ExpandPlaybook({
      row: this.embedPlanV2Row,
      pageRealization: this.pageRealization,
      pageSerp: this.pageSerp,
      pageIntentDiagnosis: this.pageIntentDiagnosis,
      freshnessShowsWarn: !this.embedDiagnosisIsGreen(this.freshnessDiagnosisTone()),
      selfDomain: this.extractDomainFromUrl(this.url || ''),
      queueRank: this.embedPlanV2QueueRank,
      serpRivalSectionGapsReady: this.hasActiveSerpRivalSectionGaps(),
      rankDrivenGaps: this.topSerpRivalRankDrivenGaps()
    });
  }

  onPlanV2PlaybookStep(step: PlanV2ExpandPlaybookStep): void {
    if (step.kind === 'quick_compare' && step.compareUrl) {
      this.openQuickCompareWithUrl(step.compareUrl);
      return;
    }
    if (step.kind === 'scroll' && step.scrollTargetId) {
      this.scrollToPlanV2PlaybookTarget(step.scrollTargetId);
    }
  }

  private scrollToPlanV2PlaybookTarget(elementId: string): void {
    document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  showEmbedContentSection(): boolean {
    if (!this.isPlanV2Embed()) return true;
    if (!this.embedActionsOnlyActive()) return true;
    // Two cards live under this heading on review now. A page whose only
    // content finding is a missing section still has content work, and gating
    // the whole section on the pages card would hide it.
    return this.showEmbedSupportingPagesCard() || Boolean(this.buildSectionGapIssuePayload());
  }

  showEmbedSupportingPagesCard(): boolean {
    let base = true;
    // Review: this card is now only about pages to write, so a page whose only
    // finding is a missing section must not render it — the Sections card below
    // is where that page's work is, and an empty "Supporting pages" beside it is
    // a card whose whole content is the word "Nothing".
    if (this.embedReviewSurface && this.supportingPagesColumnsReady()) {
      return this.embedIssueVisible(
        this.planV2IssueKey('topic_ownership'),
        this.hasReviewSupportingPagesGaps()
      );
    }
    if (this.isPlanV2Embed()) {
      if (!this.embedActionsOnlyActive()) base = true;
      else if (this.topicOwnershipStatus === 'loading' || this.topicOwnershipV2Status === 'loading') {
        base = true;
      } else if (this.topicOwnershipV2Status === 'ready' && (this.topicOwnershipV2Data?.rows?.length ?? 0) > 0) {
        base = this.hasSupportingPagesActionableGap();
      } else if (this.topicOwnershipStatus !== 'ready' || !this.topicOwnershipDomains.length) {
        base = false;
      } else {
        base = this.hasSupportingPagesActionableGap();
      }
    }
    return this.embedIssueVisible(this.planV2IssueKey('topic_ownership'), base);
  }

  showEmbedFreshnessSection(): boolean {
    if (!this.showContentFreshnessCard()) return false;
    let base = true;
    if (this.embedActionsOnlyActive()) {
      base = !this.embedDiagnosisIsGreen(this.freshnessDiagnosisTone());
    }
    return this.embedIssueVisible(this.planV2IssueKey('freshness'), base);
  }

  showEmbedSerpCompetitionCard(): boolean {
    // Review surface: this card is orientation — leaders, snippets and a
    // Quick Compare that spends money per click — and its one actionable
    // finding is already reported by the Sections card. Unchanged on Inventory.
    if (this.embedReviewSurface) return false;
    let base = Boolean(this.hasPageSerpSnapshot() && this.serpCompetitionInsight());
    if (base && this.embedActionsOnlyActive()) {
      base = this.embedDiagnosisIsActionable(this.serpCompetitionChipTone());
    }
    return this.embedIssueVisible(this.planV2IssueKey('serp_competition'), base);
  }

  showEmbedSerpPhraseCard(): boolean {
    // Nothing to compare against is not a decision.
    //
    // The Dashboard already files this under Clear — "the top results share no
    // vocabulary, so there is nothing to match against" — while the belt was
    // still asking the operator to REVIEW STRUCTURE against rivals it could
    // not show. Inventory and URL detail keep the card: reading why a SERP is
    // fragmented is exactly the kind of thing those screens are for.
    if (
      this.embedReviewSurface &&
      this.serpAnswerStructureIsFragmented() &&
      !this.hasLexicalVocabChips()
    ) {
      return false;
    }
    // Doing it right, and missing nothing, is not a decision either.
    //
    // The belt exists to hand off work. A card that says "you match" is a
    // congratulation, and the operator's rule for this surface is that a card
    // with nothing to send should not be there: *"If you're already doing it
    // right and not missing anything in the answer structure, then nothing
    // should be shown."* The vocabulary half is kept separate on purpose — a
    // page can deliver the right shape and still never use the words the SERP
    // uses, and that IS a work item. So this hides the card only when both
    // halves are clear.
    if (this.embedReviewSurface && this.reviewAnswerStructureHasNothingToSend()) {
      return false;
    }
    let base = this.showSerpPhraseMatchCard();
    if (base && this.embedActionsOnlyActive()) {
      base = !this.embedDiagnosisIsGreen(this.serpPhraseMatchActionChipTone());
    }
    return this.embedIssueVisible(this.planV2IssueKey('serp_phrase'), base);
  }

  showEmbedSerpDifficultyCard(): boolean {
    // Not on the belt. Difficulty is a property of the KEYWORD, not a finding
    // about this page: there is nothing to send and nothing to dismiss, which
    // is why the card is the only one on the screen without a Sending tick. It
    // is worth knowing before committing a writer, and that is what the header's
    // keyword line and the deep dive are for.
    if (this.embedReviewSurface) return false;
    let base = true;
    if (this.embedActionsOnlyActive()) {
      base = this.embedDiagnosisIsActionable(this.serpDifficultyActionChipTone());
    }
    return this.embedIssueVisible(this.planV2IssueKey('serp_difficulty'), base);
  }

  showEmbedSerpIntentCard(): boolean {
    let base = this.hasPageIntentDiagnosis();
    if (base && this.embedActionsOnlyActive()) {
      base = !this.embedDiagnosisIsGreen(this.pageIntentMatchActionChipTone());
    }
    return this.embedIssueVisible(this.planV2IssueKey('serp_intent'), base);
  }

  showEmbedSerpAnyCard(): boolean {
    return (
      this.showEmbedSerpCompetitionCard() ||
      this.showEmbedSerpPhraseCard() ||
      this.showEmbedSerpDifficultyCard() ||
      this.showEmbedSerpIntentCard()
    );
  }

  showEmbedSerpSection(): boolean {
    if (!this.isPlanV2Embed() || !this.embedActionsOnlyActive()) return true;
    if (!this.hasPageSerpSnapshot()) return true;
    return this.showEmbedSerpAnyCard();
  }

  showEmbedSerpResultsTable(): boolean {
    if (this.isCompactPeek()) return false;
    if (!this.serpSectionExpanded || !this.hasPageSerpSnapshot()) return false;
    if (!this.embedActionsOnlyActive()) return true;
    return this.showEmbedSerpAnyCard();
  }

  showEmbedTechnicalIssuesCard(): boolean {
    if (this.isCompactPeek()) return false;
    const base = !this.embedActionsOnlyActive() || this.hasTechnicalCriticalIssues();
    return this.embedIssueVisible(this.planV2IssueKey('technical_critical'), base);
  }

  showEmbedTechnicalWarningsCard(): boolean {
    if (this.isCompactPeek()) return false;
    const base = !this.embedActionsOnlyActive() || this.hasTechnicalWarnings();
    return this.embedIssueVisible(this.planV2IssueKey('technical_warnings'), base);
  }

  showEmbedTechnicalSection(): boolean {
    if (!this.isPlanV2Embed() || !this.embedActionsOnlyActive()) return true;
    return this.showEmbedTechnicalIssuesCard() || this.showEmbedTechnicalWarningsCard();
  }

  showEmbedSignalsSection(): boolean {
    let base = true;
    if (this.isPlanV2Embed() && this.embedActionsOnlyActive()) {
      if (this.pageSignalsLoading) base = true;
      else base = this.hasNonInternalLinkSignals();
    }
    return this.embedIssueVisible(this.planV2IssueKey('signals'), base);
  }

  /** Issues drawer: own focus card (same send animation as Technical). */
  showEmbedInternalLinksSection(): boolean {
    if (!this.isPlanV2Embed()) return false;
    const key = this.planV2IssueKey('internal_link');
    if (
      this.planV2IssueQueue.isCardExiting(key, 'work_item') ||
      this.planV2IssueQueue.isCardExiting(key, 'dismissed')
    ) {
      return true;
    }
    if (this.pageSignalsLoading) return false;
    return Boolean(this.internalLinksIssuesPayload());
  }

  /**
   * Empty because the operator emptied it, rather than because nothing is wrong.
   *
   * `/templates/standard-operating-procedure-template` carries five dismissals
   * and no work items — section gap, both technical cards, answer structure and
   * freshness — so the belt had nothing to render and told the operator
   * "everything looks clear". It was not clear; it was cleared, by them, and
   * saying so is the difference between a page they can trust and one they
   * have to go and check.
   */
  embedEmptyBecauseDismissed(): boolean {
    const urlId = (this.pageUrlId || '').trim();
    if (!urlId) return false;
    for (const record of this.planV2IssueQueue.records.values()) {
      if (String(record?.urlId || '') !== urlId) continue;
      if (record.queue === 'dismissed' && record.issueType !== 'check_muted') return true;
    }
    return false;
  }

  embedActionsOnlyEmpty(): boolean {
    if (!this.embedActionsOnlyActive() || this.analysisStatus !== 'success' || !this.analysisData) {
      return false;
    }
    return (
      !this.showEmbedMiniOverviewSection() &&
      !this.showEmbedContentSection() &&
      !this.showEmbedFreshnessSection() &&
      !this.showEmbedSerpSection() &&
      !this.showEmbedTechnicalSection() &&
      !this.showEmbedInternalLinksSection() &&
      !this.showEmbedSignalsSection()
    );
  }

  planV2IssueDomain(): string {
    const fromInput = (this.embedPlanV2Domain || '').trim();
    if (fromInput) return fromInput.toLowerCase().replace(/^www\./, '');
    return this.extractDomainFromUrl(this.url || '');
  }

  planV2IssueKey(issueType: PlanV2IssueType): string {
    return buildPlanV2IssueKey((this.pageUrlId || '').trim(), issueType);
  }

  private embedIssueVisible(issueKey: string, baseVisible: boolean): boolean {
    if (!baseVisible) return false;
    if (!this.isPlanV2Embed()) return baseVisible;
    return this.planV2IssueQueue.isVisibleInIssuesEmbed(issueKey);
  }

  planV2CardExitClass(issueKey: string): Record<string, boolean> {
    return {
      'plan-v2-card-exit-work': this.planV2IssueQueue.isCardExiting(issueKey, 'work_item'),
      'plan-v2-card-exit-dismiss': this.planV2IssueQueue.isCardExiting(issueKey, 'dismissed')
    };
  }

  buildTopicOwnershipIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const base: PlanV2IssueCardPayload = {
      issueKey: buildPlanV2IssueKey(urlId, 'topic_ownership'),
      urlId,
      url,
      issueType: 'topic_ownership',
      cardTitle: 'Supporting pages',
      changeSummary: 'Close topic coverage gaps on competitors (add or strengthen supporting URLs).',
      why: this.hasTopicOwnershipCoverageGap()
        ? 'Competitors have relevant supporting URLs without a strong match on your site.'
        : this.topicOwnershipGapChipLabel(),
      diagnosisLabel: this.showTopicOwnershipGapChip() ? this.topicOwnershipGapChipLabel() : null,
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
    return this.embedReviewSurface ? this.reviewSupportingPagesWorkItem(base) : base;
  }

  /** The gap URLs this card will send — the ticked ones, in card order. */
  private reviewSupportingPagesPicked(): string[] {
    if (!this.embedReviewSurface) return [];
    return this.reviewSelection.tickedItemIds(
      (this.pageUrlId || '').trim(),
      this.reviewCardKey('topic_ownership')
    );
  }

  /**
   * A supporting-pages work item that lists the pages, not just names them.
   *
   * Same composer Inventory's compose step ends in, fed a state whose only
   * selections are the ticked rows. The review card was sending a fixed
   * sentence and a `why` of up to six paths with no `actionDetail`, so what
   * reached a writer was a list of paths with no indication of what to do with
   * them — and only the first six of them.
   *
   * Tools and sections are deliberately empty. This card is pages now; sections
   * moved to their own card, and the list-tool gap has no representation on the
   * review surface at all. Passing them as unselected rather than omitting the
   * concept keeps this honest against the composer's own shape.
   */
  private reviewSupportingPagesWorkItem(base: PlanV2IssueCardPayload): PlanV2IssueCardPayload {
    const picked = new Set(this.reviewSupportingPagesPicked());
    if (!picked.size) return base;

    const pageGaps = this.reviewSupportingPagesColumns().flatMap((col) =>
      col.gaps.map((gap) => ({
        id: `page::${col.domain}::${gap.url}`,
        competitorDomain: col.domain,
        competitorUrl: gap.url,
        path: gap.path,
        topicLabel: 'Supporting page',
        selected: picked.has(gap.url)
      }))
    );

    return composeTopicOwnershipWorkItemFromState(base, {
      pageGaps,
      toolGaps: [],
      headingMetas: [],
      selectedHeadingIds: new Set<string>(),
      activeCompetitorUrl: '',
      activeListCompetitorUrl: '',
      sectionCompetitors: [],
      listCompetitors: []
    });
  }

  needsWorkItemCompose(issueType: PlanV2IssueType): boolean {
    return (
      issueType === 'serp_phrase' ||
      issueType === 'topic_ownership' ||
      issueType === 'internal_link' ||
      issueType === 'technical_critical' ||
      issueType === 'technical_warnings'
    );
  }

  enrichWorkItemPayload(payload: PlanV2IssueCardPayload): PlanV2IssueCardPayload {
    if (payload.issueType === 'serp_phrase') {
      return composeSerpPhraseWorkItem(
        payload,
        this.pageIntentDiagnosis,
        this.pageSerpTargetQueryLabel()
      );
    }
    if (payload.issueType === 'internal_link') {
      const pageUrlId = this.pageUrlId.trim();
      const legacyRow = this.internalLinkRowForPayload(payload);
      if (legacyRow) {
        return composeInternalLinkWorkItem(payload, legacyRow, pageUrlId);
      }
      const options = listInternalLinkComposeOptions(
        this.internalLinkSignalRows,
        pageUrlId,
        internalLinkSuggestionKeysInWorkItems(this.planV2IssueQueue.records)
      );
      return composeInternalLinksWorkItem(payload, options, pageUrlId);
    }
    if (payload.issueType === 'freshness') {
      return composeFreshnessWorkItem(payload, this.freshnessWorkItemContext());
    }
    if (payload.issueType === 'serp_intent') {
      return composeSerpIntentWorkItem(payload, this.pageIntentDiagnosis);
    }
    return payload;
  }

  private freshnessWorkItemContext() {
    return buildFreshnessWorkItemContext({
      lastContentModified: this.analysisData?.lastContentModified ?? null,
      serpResults: this.pageSerp?.results ?? null
    });
  }

  beginTopicOwnershipCompose(): boolean {
    const state = buildTopicOwnershipComposeState({
      // The columns the card renders, not the raw blocks. The blocks keep each
      // domain's hero — its main competing page — so the picker was offering
      // "write this page" for a page this site already has, and offering pages
      // from domains never marked as competitors. Whatever is ticked here
      // becomes a work item somebody acts on, which makes this the one place
      // that miscount actually cost something.
      domains: this.getSupportingPagesColumns().map((col) => ({
        domain: col.domain,
        isYourSite: col.isYourSite,
        rows: col.supportingRows
      })),
      v2Rows: this.topicOwnershipV2Data?.rows,
      extractDomain: (pageUrl) => this.extractDomainFromUrl(pageUrl),
      displaySegmentsForSection: (sc) => this.topicOwnershipV2SectionDisplayHeadingSegments(sc)
    });
    if (!state) return false;
    this.topicOwnershipComposeState = state;
    if (state.activeCompetitorUrl) {
      this.topicOwnershipV2DetailUrl = state.activeCompetitorUrl;
      this.topicOwnershipV2CoveredElsewhereUrl = '';
      this.topicOwnershipV2ListInventoryUrl = '';
      this.topicOwnershipV2RankDrivenUrl = '';
    }
    return true;
  }

  endTopicOwnershipCompose(): void {
    this.topicOwnershipComposeState = null;
    this.topicOwnershipV2DetailUrl = '';
  }

  getTopicOwnershipComposeState(): TopicOwnershipComposeState | null {
    return this.topicOwnershipComposeState;
  }

  topicOwnershipComposeHasSelection(): boolean {
    return topicOwnershipComposeHasSelection(this.topicOwnershipComposeState);
  }

  finalizeTopicOwnershipComposeWorkItem(base: PlanV2IssueCardPayload): PlanV2IssueCardPayload {
    return composeTopicOwnershipWorkItemFromState(base, this.topicOwnershipComposeState);
  }

  onTopicOwnershipComposingChange(composing: boolean): void {
    this.topicOwnershipComposing = composing;
    if (!composing) {
      this.endTopicOwnershipCompose();
    }
  }

  topicOwnershipComposeHasPageGaps(): boolean {
    return (this.topicOwnershipComposeState?.pageGaps.length ?? 0) > 0;
  }

  topicOwnershipComposeHasSectionGaps(): boolean {
    return (this.topicOwnershipComposeState?.sectionCompetitors.length ?? 0) > 0;
  }

  topicOwnershipComposeHasToolGaps(): boolean {
    return (this.topicOwnershipComposeState?.listCompetitors.length ?? 0) > 0;
  }

  topicOwnershipComposeToolsForActiveCompetitor() {
    const state = this.topicOwnershipComposeState;
    const url = state?.activeListCompetitorUrl?.trim();
    if (!state || !url) return [];
    const key = normalizeUrlForCompare(url);
    return state.toolGaps.filter((tool) => normalizeUrlForCompare(tool.competitorUrl) === key);
  }

  onTopicOwnershipComposeListCompetitorChange(competitorUrl: string): void {
    if (!this.topicOwnershipComposeState) return;
    this.topicOwnershipComposeState.activeListCompetitorUrl = competitorUrl;
  }

  topicOwnershipComposeActiveV2Row(): SupportingPagesV2RowResult | null {
    const url = this.topicOwnershipComposeState?.activeCompetitorUrl?.trim();
    if (!url) return null;
    return (
      this.topicOwnershipV2Data?.rows?.find(
        (row) => normalizeUrlForCompare(row.competitorUrl) === normalizeUrlForCompare(url)
      ) ?? null
    );
  }

  onTopicOwnershipComposeCompetitorChange(competitorUrl: string): void {
    if (!this.topicOwnershipComposeState) return;
    this.topicOwnershipComposeState.activeCompetitorUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = competitorUrl;
    this.topicOwnershipV2CoveredElsewhereUrl = '';
    this.topicOwnershipV2ListInventoryUrl = '';
  }

  topicOwnershipComposeSectionRows(row: SupportingPagesV2RowResult): SectionCoverageRow[] {
    return this.topicOwnershipV2SectionCoverageGroups(row);
  }

  isTopicOwnershipComposeHeadingSelected(
    row: SupportingPagesV2RowResult,
    sc: SectionCoverageRow,
    segIndex: number
  ): boolean {
    const id = topicOwnershipHeadingComposeId(row.competitorUrl, sc, segIndex);
    return this.topicOwnershipComposeState?.selectedHeadingIds.has(id) ?? false;
  }

  topicOwnershipHeadingTreeContext(
    v2Row: SupportingPagesV2RowResult,
    sc: SectionCoverageRow,
    composeSelect: boolean
  ) {
    return {
      $implicit: this.topicOwnershipV2SectionDisplayHeadingSegments(sc),
      composeSelect,
      v2Row,
      sc
    };
  }

  /** User view defaults sections to expanded compare; compose matches that. */
  topicOwnershipV2SectionCompareExpanded(
    competitorUrl: string,
    sc: SectionCoverageRow,
    composeSelect = false
  ): boolean {
    if (composeSelect && this.topicOwnershipV2SectionCompareAvailable(sc)) return true;
    return this.isTopicOwnershipV2SectionFullTextExpanded(competitorUrl, sc);
  }

  toggleTopicOwnershipComposeHeading(
    event: Event,
    row: SupportingPagesV2RowResult,
    sc: SectionCoverageRow,
    segIndex: number
  ): void {
    if (!this.topicOwnershipComposing || !this.topicOwnershipComposeState) return;
    if (event instanceof KeyboardEvent && event.key === ' ') {
      event.preventDefault();
    }
    event.stopPropagation();
    const id = topicOwnershipHeadingComposeId(row.competitorUrl, sc, segIndex);
    const next = this.topicOwnershipComposeState.selectedHeadingIds;
    if (next.has(id)) next.delete(id);
    else next.add(id);
  }

  getTechnicalIssueComposeOptions(
    issueType: 'technical_critical' | 'technical_warnings'
  ): TechnicalIssueComposeOption[] {
    const kind = issueType === 'technical_critical' ? 'critical' : 'warning';
    const issues =
      kind === 'critical' ? this.technicalCriticalIssueViews() : this.technicalWarningIssueViews();
    return listTechnicalIssueComposeOptions(issues, kind);
  }

  finalizeTechnicalIssuesWorkItem(
    base: PlanV2IssueCardPayload,
    issueType: 'technical_critical' | 'technical_warnings',
    selected: TechnicalIssueComposeOption[]
  ): PlanV2IssueCardPayload {
    const severity = issueType === 'technical_critical' ? 'critical' : 'warning';
    return composeTechnicalIssuesWorkItem(base, selected, severity);
  }

  getInternalLinkComposeOptions(): InternalLinkComposeOption[] {
    return listInternalLinkComposeOptions(
      this.internalLinkSignalRows,
      this.pageUrlId.trim(),
      internalLinkSuggestionKeysInWorkItems(this.planV2IssueQueue.records)
    );
  }

  finalizeInternalLinksWorkItem(
    base: PlanV2IssueCardPayload,
    selected: InternalLinkComposeOption[]
  ): PlanV2IssueCardPayload[] {
    return composeInternalLinkWorkItemsFromSelection(base, selected);
  }

  private syncPlanV2EmbedHost(): void {
    this.planV2EmbedContext.registerHost(this.isPlanV2Embed() ? this : null);
  }

  buildFreshnessIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const verdict = this.contentFreshnessVerdict();
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'freshness'),
      urlId,
      url,
      issueType: 'freshness',
      cardTitle: 'Content freshness',
      changeSummary: 'Refresh page content to align with SERP freshness expectations.',
      why: verdict || this.freshnessDiagnosisLabel(),
      diagnosisLabel: this.freshnessDiagnosisLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildSerpCompetitionIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'serp_competition'),
      urlId,
      url,
      issueType: 'serp_competition',
      cardTitle: "Who's beating you?",
      changeSummary: 'Improve rankings vs marked competitors on this target query.',
      why: `Your rank: ${this.serpCompetitionYourRankLabel()}. ${this.serpCompetitionChipLabel()}.`,
      diagnosisLabel: this.serpCompetitionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  /**
   * "Missing sections" — the review surface's second content card.
   *
   * Only ever built on the review surface, and only when there is something to
   * decide. Inventory keeps answering both questions in the topic card above,
   * where an operator working out what is going on wants them side by side;
   * splitting them there would cost the comparison and buy nothing, because
   * nothing on Inventory is being sent.
   */
  /**
   * "Answer structure", as the review surface shows it.
   *
   * The deep-dive card answers several questions at once — how the SERP
   * delivers, how this page delivers, which shared vocabulary sits where, what
   * your two strongest passages say, and what nine rivals opened with. That is
   * right on Inventory, where an operator is working out what is going on.
   *
   * Here they are deciding whether to send it, so the card is reduced to the
   * three things that decision needs: what to do, the words the page never
   * uses, and what "doing it" looks like in the rivals' own words beside what
   * yours currently says.
   *
   * Two rules, stated because they are what stops this growing back:
   *
   * **Only the FIRST brief line.** The second is context about vocabulary,
   * which the missing-terms line says in fewer words.
   *
   * **Only excerpts carrying structure evidence, capped at two.** Highlighted
   * means the analyser marked how that winner delivers its answer. An unmarked
   * excerpt is a paragraph, not evidence, and a card that pads itself to look
   * complete is one an operator learns to skim. A page whose rivals carry no
   * marked evidence keeps the instruction and shows no excerpts.
   */
  reviewAnswerStructure(): {
    instruction: string | null;
    vocab: ReviewAnswerVocab | null;
    yours: string | null;
    theirs: Array<{ position: number; domain: string; segments: SerpSnippetSegment[] }>;
  } | null {
    if (!this.embedReviewSurface) return null;
    const d = this.pageIntentDiagnosis;
    if (!d) return null;

    const instruction = this.answerStructureBriefLines()[0] ?? null;

    // The three tones, not the red one alone — see `reviewAnswerVocabGroups`.
    const vocab = reviewAnswerVocabGroups(this.serpVocabChipRows());

    // The structure passages, then the same fallbacks the deep-dive column uses.
    // Those passages are empty exactly when this page matches none of the SERP's
    // shapes — which is the case the comparison exists for. Without the fallback
    // the card silently drops "Yours opens" on every page worth reading it on.
    const passages = this.searchIntentStructurePagePassages();
    const chosen = passages.find((p) => p.matchKind === 'closest') ?? passages[0] ?? null;
    const yoursText =
      chosen?.text ||
      d.yourBestSection?.text ||
      d.yourIntroSection?.text ||
      '';
    // Deliberately unmarked, unlike theirs. The marks available for your own
    // passage are vocabulary matches — words the SERP shares that you happen to
    // use — and this card is about STRUCTURE. Highlighting "Sheets" and "chart"
    // in your own opening says nothing about how that opening is built, and it
    // reads as if those words were the finding. The rivals' marks stay, because
    // there they are the finding: the span that shows how the winner delivers.
    const yours = yoursText.trim() ? this.reviewTrimPlain(yoursText) : null;

    // Segments, not plain text. The mark IS the finding: it is the span the
    // analyser identified as the winner's answer structure, so a quote without
    // it asks the operator to spot the pattern themselves — which is the work
    // this card is supposed to have already done.
    const theirs = this.searchIntentWinnerExcerptsAll()
      .filter((e) => (e.structureEvidenceSpans?.length || 0) > 0)
      .slice(0, 2)
      .map((e) => ({
        position: e.position,
        domain: e.domain,
        segments: this.reviewTrimSegments(
          this.searchIntentExcerptSegments({
            excerpt: e.excerpt,
            structureEvidenceSpans: e.structureEvidenceSpans
          })
        )
      }));

    if (!instruction && !vocab && !theirs.length) return null;
    return { instruction, vocab, yours, theirs };
  }

  /**
   * One line of prose on a belt, with its highlights intact.
   *
   * Trimming segments rather than the raw string, because trimming the string
   * first would throw the marks away and re-finding them is a second answer to
   * "where does the evidence start". A highlighted segment is never cut: the
   * mark is the finding, and half a finding is worse than one line more.
   */
  /** Your own opening, as plain prose. See the note in `reviewAnswerStructure`. */
  private reviewTrimPlain(text: string, max = 190): string {
    const clean = (text || '').replace(/\s+/g, ' ').trim();
    if (clean.length <= max) return clean;
    const cut = clean.slice(0, max);
    const lastSpace = cut.lastIndexOf(' ');
    return `${(lastSpace > 60 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:\s]+$/, '')}…`;
  }

  private reviewTrimSegments(
    segments: SerpSnippetSegment[],
    max = 190
  ): SerpSnippetSegment[] {
    const out: SerpSnippetSegment[] = [];
    let used = 0;
    for (const seg of segments) {
      const text = (seg.text || '').replace(/\s+/g, ' ');
      if (!text) continue;
      if (used + text.length <= max) {
        out.push({ ...seg, text });
        used += text.length;
        continue;
      }
      if (seg.emphasized) {
        out.push({ ...seg, text });
        used += text.length;
        break;
      }
      const room = Math.max(0, max - used);
      const cut = text.slice(0, room);
      const lastSpace = cut.lastIndexOf(' ');
      const kept = (lastSpace > 20 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:\s]+$/, '');
      if (kept) out.push({ ...seg, text: `${kept}…` });
      else if (out.length) out[out.length - 1] = { ...out[out.length - 1], text: `${out[out.length - 1].text}…` };
      break;
    }
    return out;
  }

  /**
   * The count badge Missing sections was missing.
   *
   * Counts whatever the card is SHOWING, which is not always sections: the card
   * also holds rank-driven gaps — queries a rival outranks you on with ground
   * you already cover — and a page with those and no section gaps rendered a
   * full card with no badge at all, which is the thing this badge was added to
   * fix.
   *
   * Three labels rather than one, because "3 SECTIONS" over three queries would
   * be wrong and "3 GAPS" over one section is vaguer than it needs to be. The
   * mixed case is the only one that has to generalise.
   */
  /**
   * What this card should be CALLED, given what is actually in it.
   *
   * "Missing sections" is right for 90% of the cards in the pilot, where every
   * row is a section the page does not have. On the other 10% — 45 pages —
   * every row reads "Shallow coverage — closest: … (78%)", which is the exact
   * opposite claim: you have the section, it is thin. Titling that "Missing
   * sections" tells an operator to write something they already wrote.
   *
   * Decided from the rows rather than from which analyser produced them: the
   * deepen rows all come from rank-driven gaps today, but that is an accident
   * of the section analyser currently clearing `absentTerms` on covered rows,
   * not a rule to encode here.
   */
  reviewGapsCardTitle(): string {
    const sections = this.reviewMissingSections()?.rows ?? [];
    const ranks = this.reviewRankGaps()?.rows ?? [];
    if (!sections.length && !ranks.length) return 'Missing sections';

    const anyToWrite =
      sections.some((row) => row.kind === 'write') || ranks.some((row) => row.isMissing);
    return anyToWrite ? 'Missing sections' : 'Thin sections';
  }

  reviewMissingSectionsChipLabel(): string | null {
    if (!this.embedReviewSurface) return null;
    const sections = this.reviewMissingSections()?.rows.length ?? 0;
    const queries = this.reviewRankGaps()?.rows.length ?? 0;

    // Shared with the Dashboard peek, so the two cannot count it differently.
    return missingSectionsCountLabel(sections, queries);
  }

  buildSectionGapIssuePayload(): PlanV2IssueCardPayload | null {
    if (!this.embedReviewSurface) return null;
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;

    // Every other card gates on this and Missing sections did not, so a page
    // whose sections had already been sent still rendered the card — ticked,
    // with no Send button and nothing to do. That is the "why am I looking at
    // this again" page, and it is this line.
    const key = buildPlanV2IssueKey(urlId, 'section_gap');
    if (
      !this.planV2IssueQueue.isVisibleInIssuesEmbed(key) &&
      !this.planV2IssueQueue.isCardExiting(key, 'work_item') &&
      !this.planV2IssueQueue.isCardExiting(key, 'dismissed')
    ) {
      return null;
    }

    const missing = this.reviewMissingSections();
    const rank = this.reviewRankGaps();
    if (!missing && !rank) return null;

    const allRows = missing?.rows ?? [];
    const cardKey = buildPlanV2IssueKey(urlId, 'section_gap');
    // Before the rows are registered there is nothing to filter by, and the
    // card is showing all of them — so the payload says all of them. Asking
    // whether rows exist rather than whether any are ticked keeps "not yet
    // registered" apart from "operator unticked every one", which are opposite
    // situations that an empty set would make look the same.
    const ticked = new Set(this.reviewSelection.tickedItemIds(urlId, cardKey));
    const rows = this.reviewSelection.hasCardItems(urlId, cardKey)
      ? allRows.filter((r) => ticked.has(r.id))
      : allRows;
    // The work item carries what the card showed: the section, whether it is a
    // new one or a thin one, and the line of theirs that says what goes in it.
    const sections = rows.map((r) => {
      const where = r.closest ? ` (deepen “${r.closest.heading}”)` : '';
      const excerpt = r.excerpt ? ` — they open: “${r.excerpt}”` : '';
      return `${r.label}${where}${excerpt}`;
    });
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'section_gap'),
      urlId,
      url,
      issueType: 'section_gap',
      cardTitle: 'Missing sections',
      changeSummary: 'Add the sections the top-ranking rival covers and this page does not.',
      why: sections.length ? sections.slice(0, 6).join(' · ') : rank?.verdict || 'Rival covers ground this page does not.',
      actionDetail: sections.length ? encodeWorkItemDetail({ bullets: sections.slice(0, 12) }) : null,
      diagnosisLabel: missing?.verdict ?? rank?.verdict ?? null,
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildSerpPhraseIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'serp_phrase'),
      urlId,
      url,
      issueType: 'serp_phrase',
      cardTitle: 'Answer structure & entities',
      changeSummary: 'Match SERP answer structure and entity vocabulary on this page.',
      why: this.serpPhraseMatchActionChipLabel(),
      diagnosisLabel: this.showSerpPhraseMatchActionChip() ? this.serpPhraseMatchActionChipLabel() : null,
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildSerpDifficultyIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const hint = this.serpKeywordDifficultyHint();
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'serp_difficulty'),
      urlId,
      url,
      issueType: 'serp_difficulty',
      cardTitle: 'SERP difficulty',
      changeSummary: 'Account for keyword difficulty when prioritizing effort on this query.',
      why: hint ? `${this.serpKeywordDifficultyShort()} — ${hint}` : this.serpKeywordDifficultyShort(),
      diagnosisLabel: this.serpDifficultyActionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildSerpIntentIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const lines = this.pageTypeBriefLines();
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'serp_intent'),
      urlId,
      url,
      issueType: 'serp_intent',
      cardTitle: 'Page type',
      changeSummary: 'Align page type with dominant SERP page type.',
      why: lines.length ? lines.join(' ') : this.pageIntentMatchActionChipLabel(),
      diagnosisLabel: this.pageIntentMatchActionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  /**
   * The findings a technical card will actually send.
   *
   * On the review surface that is the ticked ones, which is not the same as
   * what the card lists: unticked findings stay on screen — you have to be able
   * to change your mind — but they must not turn up in the work item. Off the
   * review surface there are no ticks and this is everything.
   */
  private technicalPayloadTitles(issues: OnpageIssueView[], cardKey: string): string[] {
    if (!this.embedReviewSurface) return issues.map((i) => i.title).slice(0, 4);
    // Card-scoped unticks are already out of this — see `tickedCodesForCard`.
    // Site-wide checks are out too, for the same reason they are out of the
    // work item itself: they have their own task, and a `why` naming them
    // would describe a page card whose body no longer lists them.
    const ticked = this.reviewSelection.tickedCodesForCard(this.pageUrlId.trim(), cardKey);
    const siteWide = this.siteWideIssueCodes;
    return this.reviewTechnicalIssues(issues)
      .filter((i) => ticked.has(i.code) && !(siteWide?.has(i.code) ?? false))
      .map((i) => i.title)
      .slice(0, 4);
  }

  /**
   * A technical work item that records what the operator actually ticked.
   *
   * Through Inventory's own composer, not a second one written for this surface.
   * The review card was sending the base payload: a fixed sentence, up to four
   * titles, and no `actionDetail` at all. Tick six findings of eight and the
   * work item named four of them with none of their detail — so the care an
   * operator puts into the ticks did not survive into the thing a writer opens.
   *
   * Reusing `composeTechnicalIssuesWorkItem` is the point rather than an
   * economy: two functions building "the technical work item" is how the two
   * surfaces come to disagree about what was decided, which is the bug this
   * codebase has now shipped in four different shapes.
   *
   * Every detail line of a ticked finding is included. The finer per-line
   * picking Inventory offers has no equivalent here, and inventing a default
   * for it would be this surface guessing at a decision nobody made.
   */
  private reviewTechnicalWorkItem(
    base: PlanV2IssueCardPayload,
    issues: OnpageIssueView[],
    kind: 'critical' | 'warning'
  ): PlanV2IssueCardPayload {
    const ticked = this.reviewSelection.tickedCodesForCard(this.pageUrlId.trim(), base.issueKey);
    // Site-wide checks are NOT this page's work item.
    //
    // Each one is already written as its own `site_wide_technical` task —
    // "fixing it clears this finding on every page that has it" — so listing
    // them here too put the same three findings in Work items twice: once
    // under Site-wide, and again under the page that happened to be on screen
    // when they were ticked. On the pilot that turned one page-specific fix
    // into a four-item page card whose other three belonged to the template.
    //
    // The site-wide/page split is how the rest of the app reads; this was the
    // one place that ignored it. Inventory and URL detail are untouched: they
    // compose through their own step, where seeing everything on one page is
    // the point.
    const siteWide = this.siteWideIssueCodes;
    const picked = this.reviewTechnicalIssues(issues).filter(
      (i) => ticked.has(i.code) && !(siteWide?.has(i.code) ?? false)
    );
    // Nothing left that belongs to the page: every ticked check was site-wide
    // and has its own task. The base payload is the honest wording, and the
    // card is not repeating what the Site-wide group already says.
    if (!picked.length) return base;
    return composeTechnicalIssuesWorkItem(base, listTechnicalIssueComposeOptions(picked, kind), kind);
  }

  buildTechnicalCriticalIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const views = this.technicalCriticalIssueViews();
    const titles = this.technicalPayloadTitles(
      views,
      buildPlanV2IssueKey(urlId, 'technical_critical')
    );
    const base: PlanV2IssueCardPayload = {
      issueKey: buildPlanV2IssueKey(urlId, 'technical_critical'),
      urlId,
      url,
      issueType: 'technical_critical',
      cardTitle: 'Technical issues',
      changeSummary: 'Fix critical on-page / technical issues listed for this URL.',
      why: titles.length ? titles.join(' · ') : this.technicalIssuesActionChipLabel(),
      diagnosisLabel: this.technicalIssuesActionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
    // Inventory reaches the detailed version through its compose step. Review
    // has no compose step — the ticks ARE the picking — so it composes here.
    return this.embedReviewSurface ? this.reviewTechnicalWorkItem(base, views, 'critical') : base;
  }

  buildTechnicalWarningsIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const views = this.technicalWarningIssueViews();
    const titles = this.technicalPayloadTitles(
      views,
      buildPlanV2IssueKey(urlId, 'technical_warnings')
    );
    const base: PlanV2IssueCardPayload = {
      issueKey: buildPlanV2IssueKey(urlId, 'technical_warnings'),
      urlId,
      url,
      issueType: 'technical_warnings',
      cardTitle: 'Technical warnings',
      changeSummary: 'Resolve on-page warnings to reduce crawl and ranking friction.',
      why: titles.length ? titles.join(' · ') : this.technicalWarningsActionChipLabel(),
      diagnosisLabel: this.technicalWarningsActionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
    return this.embedReviewSurface ? this.reviewTechnicalWorkItem(base, views, 'warning') : base;
  }

  hasNonInternalLinkSignals(): boolean {
    return this.pageSignals.some((s) => s.kind !== 'internal_link');
  }

  buildSignalsIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url || !this.hasNonInternalLinkSignals()) return null;
    const labels = this.pageSignals.filter((s) => s.kind !== 'internal_link').map((s) => s.label);
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'signals'),
      urlId,
      url,
      issueType: 'signals',
      cardTitle: 'Workspace signals',
      changeSummary: 'Resolve open cannibalization or cluster signals for this URL.',
      why: labels.join(' · '),
      diagnosisLabel: null,
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildInternalLinksSectionPayload(options?: { includeLinksInWorkItems?: boolean }): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    const rows = options?.includeLinksInWorkItems
      ? this.pageSignalInternalLinkDetails()
      : this.pageSignalInternalLinkDetailsPendingTriage();
    if (!urlId || !url || rows.length === 0) return null;
    const count = rows.length;
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'internal_link'),
      urlId,
      url,
      issueType: 'internal_link',
      cardTitle: 'Internal link suggestions',
      changeSummary: `Add ${count} contextual internal link${count === 1 ? '' : 's'}.`,
      why: 'Other pages where you can add a link pointing here.',
      diagnosisLabel: null,
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  /** Shown in Issues embed while in queue or playing send/dismiss exit (same as other focus cards). */
  internalLinksIssuesPayload(): PlanV2IssueCardPayload | null {
    if (!this.isPlanV2Embed()) return null;
    const urlId = (this.pageUrlId || '').trim();
    const key = buildPlanV2IssueKey(urlId, 'internal_link');
    const exiting =
      this.planV2IssueQueue.isCardExiting(key, 'work_item') ||
      this.planV2IssueQueue.isCardExiting(key, 'dismissed');
    const payload = this.buildInternalLinksSectionPayload({ includeLinksInWorkItems: exiting });
    if (!payload) return null;
    if (exiting) return payload;
    return this.planV2IssueQueue.isVisibleInIssuesEmbed(key) ? payload : null;
  }

  internalLinkRowForPayload(payload: PlanV2IssueCardPayload): ActionPlanInternalLinkRow | null {
    const key =
      (payload.suggestionKey || '').trim() ||
      payload.issueKey.split(':').slice(2).join(':').trim();
    if (!key) return null;
    return (
      this.internalLinkSignalRows.find((r) => r.suggestionKey === key && r.status === 'open') || null
    );
  }

  /** Triage buttons only when the card reflects a real problem (not green / all-clear chips). */
  showIssueCardActions(payload: PlanV2IssueCardPayload | null | undefined): boolean {
    if (!payload || !this.isPlanV2Embed() || this.isCompactPeek()) return false;
    if (!this.planV2IssueQueue.isVisibleInIssuesEmbed(payload.issueKey)) return false;
    return this.isIssuePayloadActionable(payload);
  }

  /**
   * The same three conditions for the compact peek, which `showIssueCardActions`
   * excludes outright.
   *
   * That exclusion was correct while the peek was a row of chips with nothing to
   * act on. It is a separate predicate rather than a relaxed flag on the first
   * one because the two surfaces answer differently on the FIRST clause and
   * identically on the other two, and folding them into
   * `isCompactPeek() ? … : …` reads as if the difference were cosmetic.
   */
  showPeekActions(payload: PlanV2IssueCardPayload | null | undefined): boolean {
    if (!this.embedPeekActions) return false;
    if (!payload || !this.isCompactPeek()) return false;
    if (!this.planV2IssueQueue.isVisibleInIssuesEmbed(payload.issueKey)) return false;
    return this.isIssuePayloadActionable(payload);
  }

  isIssuePayloadActionable(payload: PlanV2IssueCardPayload): boolean {
    switch (payload.issueType) {
      case 'topic_ownership':
        // The surface-aware predicate: on review this card is pages only, so a
        // page whose only finding is a missing section must not offer a tick
        // here — the Sections card below is where that decision lives.
        return this.supportingPagesGapForThisSurface();
      case 'section_gap':
        return Boolean(this.reviewMissingSections() || this.reviewRankGaps());
      case 'freshness':
        return !this.embedDiagnosisIsGreen(this.freshnessDiagnosisTone());
      case 'serp_competition':
        return false;
      case 'serp_phrase':
        return !this.embedDiagnosisIsGreen(this.serpPhraseMatchActionChipTone());
      case 'serp_difficulty':
        return false;
      case 'serp_intent':
        return !this.embedDiagnosisIsGreen(this.pageIntentMatchActionChipTone());
      case 'technical_critical':
        return this.hasTechnicalCriticalIssues();
      case 'technical_warnings':
        return this.hasTechnicalWarnings();
      case 'signals':
        return this.hasNonInternalLinkSignals();
      case 'internal_link':
        return this.pageSignalInternalLinkDetailsPendingTriage().length > 0;
      default:
        return false;
    }
  }

  openPlanV2InInventory(): void {
    this.router.navigate(['/inventory'], { queryParams: { section: 'planV2' } });
  }

  hasDemandDiagnostics(): boolean {
    return Boolean(this.pageDemand || this.pageRealization || this.pageHealth || this.pageSecondaryHealth);
  }

  hasRankedKeywords(): boolean {
    return this.rankedKeywordsRows().length > 0;
  }

  rankedKeywordsRows(): PageRankedKeywordRow[] {
    const keywords = this.analysisData?.rankedKeywords?.keywords;
    if (!Array.isArray(keywords)) return [];

    return keywords
      .map((row: any) => ({
        keyword: String(row?.keyword || '').trim(),
        position: Number(row?.position),
        searchVolume: Number.isFinite(row?.searchVolume) ? Number(row.searchVolume) : null
      }))
      .filter((row: PageRankedKeywordRow) => row.keyword && Number.isFinite(row.position))
      .sort((a: PageRankedKeywordRow, b: PageRankedKeywordRow) => {
        if (a.position !== b.position) return a.position - b.position;
        return a.keyword.localeCompare(b.keyword);
      });
  }

  rankedKeywordsFetchedAt(): string | null {
    const fetchedAt = this.analysisData?.rankedKeywords?.fetchedAt;
    if (!fetchedAt) return null;
    return String(fetchedAt);
  }

  formatRankedKeywordVolume(value: number | null): string {
    if (!Number.isFinite(value)) return '—';
    return String(Math.round(value as number));
  }

  trackByRankedKeyword(_index: number, row: PageRankedKeywordRow): string {
    return `${row.keyword}:${row.position}`;
  }

  demandDiagnosticsInfo(key: string): string {
    return DEMAND_DIAGNOSTICS_INFO[key] ?? '';
  }

  pageDemandIntentTokensText(): string {
    const tokens = this.pageDemand?.claimIntentTokens;
    if (!Array.isArray(tokens) || tokens.length === 0) return '—';
    return tokens.join(', ');
  }

  pageDemandSourceConfidence(side: 'claim' | 'realization' | 'ambientTopic'): string {
    if (!this.pageDemand) return '—';
    if (side === 'claim') return formatPageDemandSourceConfidence(this.pageDemand.claim);
    if (side === 'realization') return formatPageDemandSourceConfidence(this.pageDemand.realization);
    return formatPageDemandSourceConfidence(this.pageDemand.ambientTopic);
  }

  pageHealthLabel(): string {
    return this.pageHealthForDisplay().band
      ? pageHealthBandLabel(this.pageHealthForDisplay().band)
      : '—';
  }

  pageHealthClass(): string {
    return this.pageHealthForDisplay().band
      ? pageHealthBandClass(this.pageHealthForDisplay().band)
      : '';
  }

  private pageHealthForDisplay() {
    if (this.pageHealth) return this.pageHealth;
    return classifyPageHealth({
      statusCode: this.analysisData?.statusCode ?? 0,
      estimatedOrganicTraffic: this.analysisData?.estimatedOrganicTraffic ?? null,
      realization: this.pageRealization
    });
  }

  effectiveHealthBand(): EffectiveHealthDisplayBand {
    const primary = this.pageHealthForDisplay().band;
    return resolveEffectiveHealthBand(primary, this.pageSecondaryHealth);
  }

  effectiveHealthLabel(): string {
    return effectiveHealthBandLabel(
      this.pageHealthForDisplay().band,
      this.pageSecondaryHealth
    );
  }

  effectiveHealthClass(): string {
    return effectiveHealthBandClass(
      this.pageHealthForDisplay().band,
      this.pageSecondaryHealth
    );
  }

  improvementsSummary(): string {
    const claim = this.pageDemand?.claim?.label?.trim();
    const band = this.effectiveHealthBand();
    const etv = this.analysisData?.estimatedOrganicTraffic;
    const etvText = Number.isFinite(etv) ? `~${Math.round(etv)} est. traffic/mo` : 'no est. traffic yet';

    if (this.analysisStatus === 'loading') {
      return 'Loading health and demand context for this URL…';
    }
    if (this.analysisStatus === 'error' || !this.analysisData) {
      return 'Run a crawl and analysis for this URL to see improvements.';
    }

    const claimPhrase = claim ? `accountable for “${claim}”` : 'without a confident demand claim yet';

    if (band === 'critical') {
      return `This URL is not eligible for organic performance (${claimPhrase}). Fix availability before SEO improvements.`;
    }
    if (band === 'recently_updated') {
      return `Recently updated — realization gap is on hold. ${claimPhrase}; ${etvText}. Earned visibility may still be building.`;
    }
    if (band === 'unscored') {
      return `Gap not scored yet — ${claimPhrase}; ${etvText}. See drivers below; compare with marked competitors when data allows.`;
    }
    if (band === 'weak') {
      return `Under-realized vs demand — ${claimPhrase}; ${etvText}. There is material upside if rankings move toward the demand ceiling.`;
    }
    if (band === 'best') {
      return `Near ceiling for scored demand — ${claimPhrase}; ${etvText}. Focus on defending position and SERP depth vs competitors.`;
    }
    return `Pulling reasonable weight for demand — ${claimPhrase}; ${etvText}.`;
  }

  improvementsWhyBullets(): string[] {
    const bullets: string[] = [];
    const primary = this.pageHealthForDisplay();

    if (primary.reasons?.length) {
      bullets.push(...primary.reasons);
    }

    if (primary.band === 'unscored' && this.pageSecondaryHealth?.reasons?.length) {
      for (const reason of this.pageSecondaryHealth.reasons) {
        if (!bullets.includes(reason)) bullets.push(reason);
      }
    }

    if (this.pageDemand?.claim?.label) {
      bullets.push(`Demand claim: ${this.pageDemand.claim.label}`);
    }

    const realization = this.pageDemand?.realization;
    if (realization?.label) {
      const pos = realization.position !== null ? ` · pos ${realization.position}` : '';
      bullets.push(`Best aligned rank: ${realization.label}${pos}`);
    }

    const gapGate = this.pageRealization?.gapGate;
    if (gapGate && gapGate !== 'scored') {
      bullets.push(`Realization gap: ${gapGate.replace(/_/g, ' ')}`);
    } else if (this.pageRealization?.gapGate === 'scored') {
      const ratio = this.pageRealization.realizationRatio;
      const gap = this.pageRealization.gap;
      if (ratio !== null && gap !== null) {
        bullets.push(
          `Realized ${this.formatRealizationNumber(this.pageRealization.realizedVisibility)} vs ceiling ${this.formatRealizationNumber(this.pageRealization.ceiling)} (ratio ${this.formatRealizationRatio(ratio)}, gap ${this.formatRealizationNumber(gap)})`
        );
      }
    }

    if (this.pageSecondaryHealth?.model === 'freshness_hold') {
      bullets.push('Secondary scoring on hold — content updated within the last six months');
    }

    if (this.pageSecondaryHealth?.unscoredSegment) {
      bullets.push(this.secondaryUnscoredSegmentLabel());
    }

    return bullets.slice(0, 8);
  }

  /** Strongest topic signal on this page (same order as the topics table). */
  primaryPageTopicSignal(): PageTopicSignal | null {
    const signals = this.getPageTopicSignals();
    return signals.length ? signals[0] : null;
  }

  /**
   * Topic used for Improvements competitor list: primary signal first;
   * if no marked competitors overlap that slug, fall back to the next signal that has matches.
   */
  improvementCompetitorTopicSignal(): PageTopicSignal | null {
    const signals = this.getPageTopicSignals();
    if (!signals.length) return null;

    for (const signal of signals) {
      if (this.getCompetitorTopicMatches(signal).length > 0) {
        return signal;
      }
    }
    return signals[0];
  }

  improvementCompetitorUsesFallbackTopic(): boolean {
    const primary = this.primaryPageTopicSignal();
    const used = this.improvementCompetitorTopicSignal();
    if (!primary || !used) return false;
    return primary.topicSlug !== used.topicSlug;
  }

  improvementCompetitorTopicLabel(): string {
    return this.improvementCompetitorTopicSignal()?.label || 'this page';
  }

  improvementCompetitorLeads(): ImprovementCompetitorLead[] {
    const signal = this.improvementCompetitorTopicSignal();
    if (!signal) return [];

    return this.getCompetitorTopicMatches(signal)
      .map((match) => ({
        domain: match.domain,
        authorityScore: match.authorityScore,
        pageCount: match.pageCount,
        topicLabel: signal.label,
        topicSlug: signal.topicSlug
      }))
      .sort((a, b) => {
        if (b.authorityScore !== a.authorityScore) return b.authorityScore - a.authorityScore;
        if (b.pageCount !== a.pageCount) return b.pageCount - a.pageCount;
        return a.domain.localeCompare(b.domain);
      })
      .slice(0, 6);
  }

  hasImprovementCompetitorLeads(): boolean {
    return this.improvementCompetitorLeads().length > 0;
  }

  trackImprovementCompetitor(_index: number, lead: ImprovementCompetitorLead): string {
    return `${lead.domain}::${lead.topicSlug}`;
  }

  hasPageSerpSnapshot(): boolean {
    return Boolean(this.pageSerp?.results?.length);
  }

  pageSerpPendingReason(): string | null {
    if (this.pageSerp) return null;
    if (!this.pageDemand?.claim?.label) {
      return 'SERP pending — no demand claim yet. Run page demand labeling after crawl.';
    }
    if (this.pageDemand.claim.confidence === 'none') {
      return 'SERP pending — claim confidence too low to pick a target query.';
    }
    return 'SERP pending — no snapshot for this URL. Refresh URL SERP for the domain (dev).';
  }

  pageSerpTargetQueryLabel(): string {
    return this.pageSerp?.queryDisplay?.trim() || '—';
  }

  pageDemandClaimLabel(): string {
    return this.pageDemand?.claim?.label?.trim() || '—';
  }

  pageDemandClaimOverridden(): boolean {
    return Boolean(this.pageDemand?.overrideClaim?.label);
  }

  openClaimOverrideModal(): void {
    this.claimOverrideError = null;
    this.claimOverrideNotice = null;
    this.claimOverrideProgress = null;
    this.claimOverrideLabel = this.pageDemand?.claim?.label?.trim() || '';
    this.claimOverrideOpen = true;
  }

  closeClaimOverrideModal(): void {
    if (this.claimOverrideSaving) return;
    this.claimOverrideOpen = false;
    this.claimOverrideError = null;
  }

  claimOverrideSuggestions(): string[] {
    const suggestions = new Set<string>();
    const current = this.pageDemand?.claim?.label?.trim();
    if (current) suggestions.add(current);
    try {
      const pathname = new URL(this.url || '').pathname || '/';
      const segments = pathname.split('/').filter(Boolean);
      const slug = segments[segments.length - 1]?.replace(/-/g, ' ').trim();
      if (slug) suggestions.add(slug);
    } catch {
      // ignore
    }
    const title =
      this.analysisData?.rawData?.pageData?.meta?.title ||
      this.analysisData?.rawData?.summary?.title ||
      '';
    if (typeof title === 'string' && title.trim()) {
      suggestions.add(title.split('|')[0].trim());
    }
    for (const signal of this.getPageTopicSignals().slice(0, 5)) {
      if (signal.label) suggestions.add(signal.label);
    }
    return Array.from(suggestions).filter((label) => label.length >= 3);
  }

  saveClaimOverride(): void {
    void this.runClaimOverrideCascade();
  }

  private setClaimOverrideProgress(
    phase: ClaimOverrideRefreshPhase,
    percent: number,
    statusLine: string,
    options: { etaLabel?: string | null; indeterminate?: boolean } = {}
  ): void {
    this.claimOverrideProgress = {
      phase,
      percent: Math.min(100, Math.max(0, percent)),
      statusLine,
      etaLabel: options.etaLabel ?? null,
      indeterminate: options.indeterminate === true
    };
  }

  private applyClaimOverrideSerpProgress(progress: UrlSerpRefreshProgress): void {
    const pct = 18 + Math.round(progress.percent * 0.28);
    this.setClaimOverrideProgress('serp', pct, progress.statusLine, {
      etaLabel: progress.etaLabel
    });
  }

  private async runClaimOverrideCascade(): Promise<void> {
    const urlId = (this.pageUrlId || '').trim();
    const scopeDomain = this.planV2IssueDomain();
    const overrideClaimLabel = (this.claimOverrideLabel || '').trim();
    const pageUrl = (this.url || '').trim();

    if (!urlId || !scopeDomain || !overrideClaimLabel) {
      this.claimOverrideError = 'Enter a corrected demand claim.';
      return;
    }
    if (!pageUrl) {
      this.claimOverrideError = 'Page URL is not loaded yet.';
      return;
    }

    this.claimOverrideSaving = true;
    this.claimOverrideError = null;
    this.claimOverrideNotice = null;
    this.setClaimOverrideProgress('claim', 6, 'Saving corrected intent…');

    try {
      const claimRes = await firstValueFrom(
        this.dataForSEOService.overridePageDemandClaim({
          urlId,
          scopeDomain,
          overrideClaimLabel,
          reason: 'wrong_intent',
          skipSerpCascade: true
        })
      );
      if (claimRes.data?.pageDemand) {
        this.pageDemand = claimRes.data.pageDemand;
      }

      this.setClaimOverrideProgress('serp', 18, 'Refreshing SERP for new query…');
      const serpTotals = await this.urlSerpSync.refreshPageUrl(pageUrl, {
        forceRefresh: true,
        onProgress: (p) => this.applyClaimOverrideSerpProgress(p)
      });
      if (serpTotals.failedKeywords > 0) {
        throw new Error('SERP fetch failed for the override query');
      }

      this.setClaimOverrideProgress('rival', 50, 'Crawling top rival page…', { indeterminate: true });
      const rivalRes = await firstValueFrom(
        this.dataForSEOService.refreshSerpRivalCoverage({ url: pageUrl, scopeDomain })
      );
      const rivalCoverage = rivalRes.data?.serpRivalCoverage ?? null;
      if (rivalCoverage) {
        this.serpRivalCoverage = rivalCoverage;
      }
      const rivalDomain = rivalCoverage?.rivalDomain?.trim();
      const gapCount = sectionGapsForV2Row(rivalCoverage?.evalRow).length;
      if (rivalRes.data?.serpRivalRefresh?.success && rivalDomain) {
        const gapNote =
          gapCount > 0
            ? ` · ${gapCount} missing section${gapCount === 1 ? '' : 's'}`
            : ' · no section gaps';
        this.setClaimOverrideProgress(
          'rival',
          78,
          `Checked section gaps vs ${rivalDomain}${gapNote}`
        );
      } else if (rivalRes.data?.serpRivalRefresh?.error) {
        this.setClaimOverrideProgress(
          'rival',
          78,
          rivalRes.data.serpRivalRefresh.error,
          { indeterminate: false }
        );
      } else if (rivalDomain) {
        this.setClaimOverrideProgress('rival', 78, `Finished check vs ${rivalDomain}`);
      }

      this.setClaimOverrideProgress('reload', 90, 'Updating playbook and intent…');
      const reloadError = await this.reloadAnalysisAfterClaimOverrideAsync(
        !rivalRes.data?.serpRivalRefresh?.success,
        rivalRes.data?.serpRivalRefresh?.error
      );

      this.claimOverrideOpen = false;
      this.claimOverrideSaving = false;
      this.claimOverrideProgress = null;
      this.intentOverrideCompleted.emit({ urlId, url: pageUrl });
      if (reloadError) {
        this.claimOverrideNotice = reloadError;
      }
    } catch (err: any) {
      this.claimOverrideSaving = false;
      this.claimOverrideProgress = null;
      const message = err?.error?.message || err?.message;
      if (this.claimOverrideOpen) {
        this.claimOverrideError = message || 'Could not complete intent refresh.';
      } else {
        this.claimOverrideNotice = message || 'Intent refresh did not complete.';
      }
    }
  }

  private reloadAnalysisAfterClaimOverrideAsync(
    partialFailure = false,
    partialError?: string
  ): Promise<string | null> {
    const pageUrl = (this.url || '').trim();
    if (!pageUrl) return Promise.resolve(null);

    // Bust answer-structure LLM cache — Wrong intent? cascade refreshed SERP;
    // reclassify delivery pattern for the new (or same) snippets.
    return firstValueFrom(
      this.dataForSEOService.getUrlAnalysis(pageUrl, { refreshAnswerStructure: true })
    )
      .then((response) => {
        if (response?.data) {
          this.pageDemand = response.data.pageDemand ?? this.pageDemand;
          this.pageSerp = response.data.pageSerp ?? this.pageSerp;
          // The claim override refetches the SERP, so the compare target can
          // move — a stale one would name a page that no longer ranks.
          this.pageCompare = response.data.pageCompare ?? this.pageCompare;
          this.pageIntentDiagnosis = response.data.pageIntentDiagnosis ?? this.pageIntentDiagnosis;
          this.collapseWinnerExcerpts();
          this.pageRealization = response.data.pageRealization ?? this.pageRealization;
          this.pageHealth = response.data.pageHealth ?? this.pageHealth;
          this.pageSecondaryHealth = response.data.pageSecondaryHealth ?? this.pageSecondaryHealth;
          this.loadTopicOwnership();
        }
        if (partialFailure) {
          return (
            partialError ||
            'Claim and SERP updated, but section-gap check did not finish. Try again or run Top SERP rivals in Setup.'
          );
        }
        return null;
      })
      .catch((err) => {
        return (
          err?.error?.message ||
          err?.message ||
          'Claim saved, but could not reload URL analysis.'
        );
      });
  }

  topicSignalMatchesClaim(signal: PageTopicSignal): boolean {
    return topicSignalMatchesDemandClaim(this.pageDemand?.claim?.label, signal);
  }

  getTopPageTopicSignalsForDisplay(): PageTopicSignal[] {
    const signals = this.getTopPageTopicSignals();
    return orderTopicSignalsForClaim(
      signals,
      this.pageDemand?.claim?.label,
      this.isPlanV2Embed()
    ) as PageTopicSignal[];
  }

  pageDemandClaimClass(): string {
    const confidence = this.pageDemand?.claim?.confidence;
    if (confidence === 'low' || confidence === 'none') return 'url-focus-stat-claim--low';
    if (confidence === 'medium') return 'url-focus-stat-claim--medium';
    return 'url-focus-stat-claim--high';
  }

  pageDemandClaimTitle(): string {
    const demand = this.pageDemand;
    if (!demand?.claim) return 'No demand claim stored for this URL.';
    const parts = [
      `Claim: ${demand.claim.label}`,
      `Source: ${demand.claim.source}`,
      `Confidence: ${demand.claim.confidence}`
    ];
    if (demand.overrideClaim?.label) {
      parts.push(`Override (${demand.overrideClaim.reason})`);
    }
    if (demand.claim.detail) parts.push(demand.claim.detail);
    if (demand.realization?.label) {
      parts.push(`Best aligned rank: ${demand.realization.label}`);
      if (demand.realization.position !== null) {
        parts.push(`Position ${demand.realization.position}`);
      }
    }
    return parts.join(' · ');
  }

  rankedKeywordsCountLabel(): string {
    const count = this.rankedKeywordsRows().length;
    return count > 0 ? String(count) : '—';
  }

  rankedKeywordsCountTitle(): string {
    const count = this.rankedKeywordsRows().length;
    const fetched = this.rankedKeywordsFetchedAt();
    if (!count) return 'No ranked keywords on this URL yet.';
    return fetched
      ? `${count} keywords from last ranked-keywords fetch (${fetched})`
      : `${count} keywords from ranked-keywords data`;
  }

  estimatedTrafficLabel(): string {
    const etv = this.analysisData?.estimatedOrganicTraffic;
    if (!Number.isFinite(etv)) return '—';
    return `~${Math.round(Number(etv))}/mo`;
  }

  estimatedTrafficTitle(): string {
    const etv = this.analysisData?.estimatedOrganicTraffic;
    if (!Number.isFinite(etv)) return 'No estimated organic traffic on this analysis.';
    return `DataForSEO estimated organic traffic: ~${Math.round(Number(etv))} visits per month`;
  }

  pageRealizationStatLabel(): string {
    const r = this.pageRealization;
    if (!r) return '—';
    if (r.gapGate !== 'scored') {
      return this.realizationGapGateLabel().replace(/_/g, ' ');
    }
    const gap = formatInventoryRealizationNumber(r.gap);
    const ratio = formatInventoryRealizationRatio(r.realizationRatio);
    return `${gap} · ${ratio}`;
  }

  pageRealizationStatTitle(): string {
    const r = this.pageRealization;
    if (!r) return 'No realization gap data.';
    if (r.gapGate !== 'scored') {
      return `Gap not scored (${r.gapGate}). ${inventoryRealizationGapTitle(r)}`;
    }
    return `${inventoryRealizationGapTitle(r)} · ${inventoryRealizationRatioTitle(r)}`;
  }

  pageContentLastModifiedDate(): Date | null {
    return parsePageContentLastModifiedDate(this.analysisData?.lastContentModified);
  }

  pageContentLastModifiedLabel(): string {
    return pageContentLastModifiedLabel(
      this.pageContentLastModifiedDate(),
      this.analysisData?.lastContentModified
    );
  }

  private serpWinnerPublishedDates(): Date[] {
    return serpWinnerPublishedDatesFromResults(this.pageSerp?.results);
  }

  serpWinnersPublishedDateSummary(): { count: number; typicalLabel: string } | null {
    const dates = this.serpWinnerPublishedDates().sort((a, b) => a.getTime() - b.getTime());
    if (dates.length < 2) return null;
    const median = medianPublishedDate(dates);
    if (!median) return null;
    return {
      count: dates.length,
      typicalLabel: `Typical ${formatFreshnessDateLabel(median)}`
    };
  }

  showContentFreshnessCard(): boolean {
    if (this.analysisStatus !== 'success' || !this.analysisData) return false;
    return Boolean(this.pageContentLastModifiedDate() || this.serpWinnersPublishedDateSummary());
  }

  toggleSerpSectionExpanded(): void {
    this.serpSectionExpanded = !this.serpSectionExpanded;
  }

  serpSectionExpandLabel(): string {
    return this.serpSectionExpanded ? 'Hide top 10' : 'Show top 10';
  }

  freshnessDiagnosisLabel(): string {
    return resolveFreshnessDiagnosisLabel(
      this.pageContentLastModifiedDate(),
      this.serpWinnerPublishedDates()
    );
  }

  freshnessDiagnosisTone(): DiagnosisTone {
    return resolveFreshnessDiagnosisTone(this.freshnessDiagnosisLabel());
  }

  /**
   * Whether a marked competitor has a supporting page this site has no match for.
   *
   * Read from the unified columns, not from the raw domain blocks. The blocks
   * include each domain's HERO — its main competing page, the one the peer
   * comparison already runs against — and counting that as a page you are
   * missing is what made the Dashboard peek say three where the card listed
   * two. Same arithmetic, same answer, wherever it is asked.
   *
   * The consequence worth stating: a domain whose only non-matching row IS its
   * hero now reports no coverage gap. That is correct. The work there is
   * "deepen this page against that rival", which is what Missing sections says,
   * not "go and write a page you already have".
   */
  hasTopicOwnershipCoverageGap(): boolean {
    return this.hasReviewSupportingPagesGaps();
  }

  hasSupportingPagesSectionGaps(): boolean {
    return hasMissingSectionGaps(this.v2RowsForMarkedCompetitors(this.mergedV2Rows()));
  }

  hasSupportingPagesListInventoryGaps(): boolean {
    return hasListInventoryGaps(this.v2RowsForMarkedCompetitors(this.mergedV2Rows()));
  }

  hasSupportingPagesRankDrivenGaps(): boolean {
    return hasRankDrivenGaps(this.v2RowsForMarkedCompetitors(this.mergedV2Rows()));
  }

  private isMarkedCompetitorDomain(domain: string): boolean {
    const key = this.normalizeSerpDomain(domain);
    return this.markedCompetitorDomains.some((m) => this.normalizeSerpDomain(m) === key);
  }

  hasSupportingPagesActionableGap(): boolean {
    return (
      this.hasTopicOwnershipCoverageGap() ||
      this.hasSupportingPagesSectionGaps() ||
      this.hasSupportingPagesListInventoryGaps() ||
      this.hasSupportingPagesRankDrivenGaps() ||
      this.topSerpRivalRankDrivenGaps().length > 0
    );
  }

  showTopicOwnershipGapChip(): boolean {
    return (
      (this.topicOwnershipStatus === 'ready' && this.topicOwnershipDomains.length > 0) ||
      (this.topicOwnershipV2Status === 'ready' && (this.topicOwnershipV2Data?.rows?.length ?? 0) > 0)
    );
  }

  supportingPagesColumnsReady(): boolean {
    return supportingPagesColumnsReady(this.supportingPagesGateState());
  }

  supportingPagesEmptyReady(): boolean {
    return supportingPagesEmptyReady(this.supportingPagesGateState());
  }

  /**
   * The card is on screen but its answer is not in yet.
   *
   * Three renderings were tried and the first two were both wrong. Rendering
   * the card whole — title, GAP FOUND badge, ticked "Sending" box — over an
   * empty body asserts a verdict nobody can see and volunteers it for sending.
   * Hiding the card entirely until it settles is worse in a different way: the
   * section reads as finished, so an operator can look at a page, see no
   * supporting-pages finding, and move on before one arrives.
   *
   * So the card stays, and only the parts that would LIE are withheld: the
   * verdict badge and the tick. What is left says plainly that it is still
   * working. The send is blocked separately — see `reviewCardsPending`, since
   * a page whose findings are still arriving cannot be sent whole.
   */
  reviewSupportingPagesPending(): boolean {
    return this.embedReviewSurface && this.supportingPagesPendingVisible;
  }

  /**
   * The same card, reduced to the one question a review decision needs.
   *
   * Rules and reasoning live in `utils/review-supporting-pages`. This only
   * hands over the two bits of formatting the deep-dive card already owns, so
   * a path and a traffic figure read identically on both surfaces.
   */
  reviewSupportingPagesColumns(): ReviewSupportingPagesColumn[] {
    return buildReviewSupportingPagesColumns(this.getSupportingPagesColumns(), {
      path: (url: string) => this.topicOwnershipPagePath(url),
      traffic: (value?: number) => this.topicOwnershipPageTraffic(value)
    });
  }

  hasReviewSupportingPagesGaps(): boolean {
    return hasReviewSupportingPagesGaps(this.reviewSupportingPagesColumns());
  }

  /**
   * Whether this card has nothing at all to say.
   *
   * "Nothing on this topic that they have and you don't" used to print above
   * three other findings the card was holding, so a page with a rival
   * outranking it on three queries opened with the word "Nothing". Those three
   * have moved to cards of their own, which makes the sentence simple again: it
   * is about pages, and it is true when there are no page gaps.
   */
  reviewSupportingPagesEmpty(): boolean {
    return !this.hasReviewSupportingPagesGaps();
  }

  /**
   * "This page" or "Site-wide" beside a technical finding, on review only.
   *
   * The deep-dive panel states the split in a sentence and lists the page's own
   * findings; that is right where an operator is reading one page closely. On a
   * belt of forty-five it has to be legible per row, because the two are
   * different jobs: one is work on this page, the other is a template fix that
   * clears the same finding on hundreds of pages at once.
   *
   * Null before the domain rollup answers, so a tag never appears and then
   * changes its mind.
   */
  /**
   * "What they cover that you don't", on the review surface.
   *
   * This was already computed and already worded — `evidenceMissingSections`
   * has said it on the Dashboard peek for as long as the peek has existed — and
   * the review surface, which is the screen you reach BY clicking that peek,
   * did not show it. It offered a Compare button instead: a tool where the
   * answer was already sitting in the payload.
   *
   * Sections by name rather than a count, because the count is not the work.
   * "3 sections" is a number to interpret; "PMP® Exam Formula Cheat Sheet" is a
   * thing to write.
   */
  reviewMissingSections(): ReviewMissingSectionsView | null {
    if (!this.embedReviewSurface) return null;
    if (this.supportingPagesSettling()) return null;
    return reviewMissingSections(
      sectionGapsForV2Row(this.serpRivalEvalRow()),
      this.pageCompare?.target?.domain || '',
      // The rival PAGE. `activeSerpRivalCoverage` is the same guarded read the
      // rows come from, so the link cannot point at a different rival than the
      // sections were computed against — the argano/adobe misattribution again.
      this.activeSerpRivalCoverage()?.rivalUrl || null
    );
  }

  /**
   * Links worth adding, on the review surface.
   *
   * The suggestions are already loaded here — `internalLinkSignalRows` feeds
   * the peek's Internal links row — and the review surface simply never showed
   * them. Rules in `utils/review-internal-links`.
   */
  /**
   * Queries a rival outranks you on, on the review surface.
   *
   * The fourth kind of finding this card can hold, and the one the review was
   * dropping — which is how a card ended up asserting GAP FOUND over "Nothing
   * on this topic that they have and you don't". `hasSupportingPagesActionableGap`
   * counts these, so the badge was right and the body was missing them.
   */
  reviewRankGaps(): ReviewRankGapsView | null {
    if (!this.embedReviewSurface) return null;
    if (this.supportingPagesSettling()) return null;
    // Name AND page from the same guarded read the rows come from. The domain
    // used to be picked live off the SERP while the rows came from the stored
    // evaluation — the argano/adobe misattribution — and a link built from the
    // live pick could point at a page that produced none of these queries.
    const coverage = this.activeSerpRivalCoverage();
    return reviewRankGaps(
      this.topSerpRivalRankDrivenGaps(),
      this.topEditorialSerpRival()?.domain ?? null,
      coverage?.rivalUrl || null
    );
  }

  // `reviewInternalLinks()` used to render a fourth block inside the supporting
  // pages card. It was showing the same suggestions the dedicated Internal
  // links card below already lists, under a tick that also covered pages and
  // sections — so unticking links meant unticking everything. The dedicated
  // card has its own tick and its own compose; this was the duplicate.

  /**
   * Findings on this page that still need an answer.
   *
   * Site-wide defects already decided this period drop out — see
   * `utils/site-wide-suppression` for why, and for the deliberate consequence
   * that a later page shows fewer findings than it has. Only the review surface
   * suppresses; URL Detail lists everything, always.
   */
  reviewTechnicalIssues(issues: OnpageIssueView[]): OnpageIssueView[] {
    if (!this.embedReviewSurface) return issues;
    const tagged = technicalReviewIssues({ issues, siteWideCodes: this.siteWideIssueCodes });
    const decided = decidedSiteWideCodes(this.planV2IssueQueue.records.values());
    const open = splitSettledSiteWide(tagged, decided).open;
    const keys = new Set(open.map((i) => i.code));
    return issues.filter((i) => keys.has(i.code));
  }

  /** "6 site-wide issues — already decided this period." Null when none are. */
  reviewSettledSiteWideLabel(issues: OnpageIssueView[]): string | null {
    if (!this.embedReviewSurface) return null;
    const tagged = technicalReviewIssues({ issues, siteWideCodes: this.siteWideIssueCodes });
    const decided = decidedSiteWideCodes(this.planV2IssueQueue.records.values());
    return settledSiteWideLabel(splitSettledSiteWide(tagged, decided).settled.length);
  }

  /**
   * Hands the page's technical checks to the send.
   *
   * This used to announce only the site-wide codes, because they were the only
   * ones the send did anything with. Every check is announced now: each one is
   * its own tick, and an untick has to be remembered whether the crawler calls
   * it a template defect or not.
   *
   * Only the checks still being ASKED about are announced. A code already
   * settled is filtered out by `reviewTechnicalIssues` upstream, and announcing
   * it here would re-send an answer the operator did not give on this page.
   */
  private announceSiteWideCodes(): void {
    if (!this.embedReviewSurface) return;
    // Runs every settled change-detection pass. `registerChecks` compares what
    // it holds and returns without touching anything when the list matches, so
    // the steady state costs one array walk and changes no state.

    const urlId = this.pageUrlId.trim();
    if (!urlId) return;

    const register = (cardKey: string, issues: OnpageIssueView[]) => {
      if (!cardKey) return;
      this.reviewSelection.registerChecks(
        urlId,
        cardKey,
        this.reviewTechnicalIssues(issues).map((issue) => ({
          code: issue.code,
          cardIssueKey: cardKey,
          siteWide: Boolean(this.siteWideIssueCodes?.has(issue.code))
        }))
      );
    };

    register(
      this.buildTechnicalCriticalIssuePayload()?.issueKey || '',
      this.technicalCriticalIssueViews()
    );
    register(
      this.buildTechnicalWarningsIssuePayload()?.issueKey || '',
      this.technicalWarningIssueViews()
    );
  }

  /**
   * Whether one technical finding is ticked, on the review surface.
   *
   * The card tick says "fix this card"; these say which of its findings that
   * covers. Both are needed: a page with eight findings where six are wanted is
   * the common case, and before this the operator's only move was to take all
   * eight or none.
   */
  /**
   * The row-level ticks on the two content cards.
   *
   * Supporting pages lists pages to write and Missing sections lists sections to
   * add, and both used to be all-or-nothing: an operator who wanted one of two
   * pages had to take both or neither. Technical findings got their own ticks
   * first; there was never a reason for the content cards to be different.
   *
   * Keyed by card and row rather than by the kind of thing the row is, because
   * unlike a technical check these are not remembered — see the note on
   * `cardItemsByUrlId`.
   */
  private reviewCardKey(type: 'topic_ownership' | 'section_gap'): string {
    const urlId = (this.pageUrlId || '').trim();
    return urlId ? buildPlanV2IssueKey(urlId, type) : '';
  }

  /**
   * Whether a card is going, as its rows need to know.
   *
   * The card tick is a master switch. With it off, nothing on the card is being
   * sent, so rows that still show ticks are describing a decision that is not
   * happening — which is what made "Not this one" sit under two ticked pages.
   *
   * Rows read this to render unticked AND disabled while the card is off.
   * Disabled rather than merely unticked, for the same reason the emptied-card
   * click had to be handled: a checkbox the user can click but whose bound
   * value cannot change leaves the browser showing a tick the model denies.
   * There is also nothing to choose — no row is going either way.
   *
   * Their own state is untouched underneath, so turning the card back on
   * restores what was picked rather than flattening it.
   */
  reviewCardSending(type: 'topic_ownership' | 'section_gap'): boolean {
    const key = this.reviewCardKey(type);
    return !key || this.reviewSelection.isSelected(key);
  }

  /** Same question for the two technical cards, which key off their own payloads. */
  reviewTechnicalCardSending(payload: PlanV2IssueCardPayload | null | undefined): boolean {
    const key = payload?.issueKey || '';
    return !key || this.reviewSelection.isSelected(key);
  }

  isSupportingPageTicked(gapUrl: string): boolean {
    return this.reviewSelection.isItemTicked(
      this.reviewSelection.itemKey(this.reviewCardKey('topic_ownership'), gapUrl)
    );
  }

  toggleSupportingPage(gapUrl: string, event: Event): void {
    event.stopPropagation();
    const card = this.reviewCardKey('topic_ownership');
    // Clicking a row on a card that is off turns the card back on rather than
    // toggling into a state nothing can see.
    if (!this.reviewCardSending('topic_ownership')) {
      this.reviewSelection.reactivateCardWithItem(this.pageUrlId.trim(), card, gapUrl);
      return;
    }
    this.reviewSelection.toggleItem(this.reviewSelection.itemKey(card, gapUrl));
  }

  isMissingSectionTicked(rowId: string): boolean {
    return this.reviewSelection.isItemTicked(
      this.reviewSelection.itemKey(this.reviewCardKey('section_gap'), rowId)
    );
  }

  toggleMissingSection(rowId: string, event: Event): void {
    event.stopPropagation();
    const card = this.reviewCardKey('section_gap');
    if (!this.reviewCardSending('section_gap')) {
      this.reviewSelection.reactivateCardWithItem(this.pageUrlId.trim(), card, rowId);
      return;
    }
    this.reviewSelection.toggleItem(this.reviewSelection.itemKey(card, rowId));
  }

  /**
   * The internal-link suggestions on this page, as individual decisions.
   *
   * One registered item per link rather than one for the card, which is the
   * opposite of every other card here and is the honest shape for this one: a
   * link task belongs on the SOURCE page, where somebody edits the copy, not on
   * the page being reviewed. Inventory has always filed them that way, and one
   * bundled item filed here would put the work under the wrong page.
   *
   * `register` is bucketed by the page being reviewed — which is how the send
   * finds them — while each payload carries its own source `urlId`. Same keys
   * Inventory writes, so a link sent from either surface is the same record.
   */
  private announceReviewInternalLinks(urlId: string): void {
    for (const row of this.pageSignalInternalLinkDetailsPendingTriage()) {
      this.reviewSelection.register(
        urlId,
        composeInternalLinkWorkItemOnSourcePage(row, this.embedPlanV2Row?.priorityScore ?? null)
      );
    }
  }

  /** Every link on this card, as issue keys, for the master tick to act on. */
  private reviewInternalLinkKeys(): string[] {
    return this.pageSignalInternalLinkDetailsPendingTriage().map((row) =>
      internalLinkWorkItemIssueKey(row)
    );
  }

  isInternalLinkTicked(row: ActionPlanInternalLinkRow): boolean {
    return this.reviewSelection.isSelected(internalLinkWorkItemIssueKey(row));
  }

  toggleInternalLink(row: ActionPlanInternalLinkRow, event: Event): void {
    event.stopPropagation();
    const key = internalLinkWorkItemIssueKey(row);
    // Card off: the click means "this one", the same as every other card here.
    if (!this.reviewInternalLinksSending()) {
      for (const other of this.reviewInternalLinkKeys()) {
        const wanted = other === key;
        if (this.reviewSelection.isSelected(other) !== wanted) this.reviewSelection.toggle(other);
      }
      return;
    }
    this.reviewSelection.toggle(key);
  }

  /** The card's master tick: on when anything under it is going. */
  reviewInternalLinksSending(): boolean {
    return this.reviewInternalLinkKeys().some((key) => this.reviewSelection.isSelected(key));
  }

  /** Whether this card has links the operator can still decide on. */
  reviewInternalLinkTickable(): boolean {
    return this.embedReviewSurface && this.reviewInternalLinkKeys().length > 0;
  }

  reviewInternalLinksCountLabel(): string | null {
    if (!this.embedReviewSurface) return null;
    const n = this.reviewInternalLinkKeys().length;
    return n > 0 ? this.reviewCountBadge(n, 'LINK', 'LINKS') : null;
  }

  /**
   * The card tick, over rows that are each their own decision.
   *
   * Turning it off unticks every link; turning it on ticks them all. There is
   * no separate card record to write — unlike the other cards, this one is a
   * heading over N decisions rather than a decision of its own.
   */
  toggleReviewInternalLinksCard(event: Event): void {
    event.stopPropagation();
    const on = this.reviewInternalLinksSending();
    for (const key of this.reviewInternalLinkKeys()) {
      if (this.reviewSelection.isSelected(key) === on) this.reviewSelection.toggle(key);
    }
  }

  /** Hands both content cards' rows to the send, the way the checks are handed over. */
  private announceReviewCardItems(): void {
    if (!this.embedReviewSurface) return;
    const urlId = (this.pageUrlId || '').trim();
    if (!urlId) return;

    this.announceReviewInternalLinks(urlId);

    const pagesKey = this.reviewCardKey('topic_ownership');
    if (pagesKey) {
      this.reviewSelection.registerCardItems(
        urlId,
        pagesKey,
        this.reviewSupportingPagesColumns().flatMap((col) => col.gaps.map((g) => g.url))
      );
    }

    const sectionsKey = this.reviewCardKey('section_gap');
    if (sectionsKey) {
      this.reviewSelection.registerCardItems(
        urlId,
        sectionsKey,
        (this.reviewMissingSections()?.rows ?? []).map((r) => r.id)
      );
    }
  }

  isTechnicalCheckTicked(issue: OnpageIssueView, cardKey = ''): boolean {
    return cardKey
      ? this.reviewSelection.isCheckTickedOnCard(cardKey, issue.code)
      : this.reviewSelection.isCheckTicked(issue.code);
  }

  toggleTechnicalCheck(issue: OnpageIssueView, cardKey: string, event: Event): void {
    event.stopPropagation();
    if (cardKey && !this.reviewSelection.isSelected(cardKey)) {
      this.reviewSelection.reactivateCardWithCheck(this.pageUrlId.trim(), cardKey, issue.code);
      return;
    }
    this.reviewSelection.toggleCheck(issue.code, this.pageUrlId.trim(), cardKey);
  }

  /**
   * "Not fixing this — you unticked it on an earlier page."
   *
   * Said out loud rather than left as a quietly empty checkbox, because a
   * default the operator did not set on THIS page is a default they have to be
   * told about. Null on the page where the untick happened: they just did it.
   */
  technicalCheckMutedNote(issue: OnpageIssueView, cardKey = ''): string | null {
    if (!this.embedReviewSurface) return null;
    if (this.reviewSelection.checkMutedEarlier(issue.code, this.pageUrlId.trim())) {
      return 'muted earlier';
    }
    // Said on the page where the decision is made, not two pages later.
    //
    // Some unticks are sticky and some are not, and they look almost identical.
    // The operator finding that out on page 3 is finding out too late — this is
    // the moment they make the choice and the only moment changing it is cheap.
    //
    // It marks exactly what the send will remember, so its absence is as
    // informative as its presence: no label means the next page asks again.
    if (!cardKey) return null;
    const siteWide = Boolean(this.siteWideIssueCodes?.has(issue.code));

    // Card turned off: it decides its site-wide checks and nothing else. The
    // rows all LOOK unticked here, so without the label a page-specific one
    // would be indistinguishable from a site-wide one that is about to be
    // muted for the rest of the queue.
    if (!this.reviewSelection.isSelected(cardKey)) {
      return siteWide ? 'muted for this period' : null;
    }

    // Unticked on its own: remembered whatever its scope, which is what the
    // operator asked for — a check they are not fixing is not fixed anywhere.
    return this.reviewSelection.isCheckTickedOnCard(cardKey, issue.code)
      ? null
      : 'muted for this period';
  }

  technicalIssueScopeLabel(issue: OnpageIssueView): string | null {
    if (!this.embedReviewSurface) return null;
    const [tagged] = technicalReviewIssues({
      issues: [issue],
      siteWideCodes: this.siteWideIssueCodes
    });
    return tagged ? technicalScopeLabel(tagged.scope) : null;
  }

  /**
   * Whether this card, as the surface renders it, has a gap.
   *
   * The badge has to describe the body underneath it, and the two surfaces show
   * different bodies. Inventory's card holds all four findings, so the broad
   * predicate is right there. Review's holds pages only — sections and rank
   * gaps have their own card now — so the broad predicate would put GAP FOUND
   * over "Nothing on this topic that they have and you don't", which is the
   * card asserting the opposite of what it shows.
   */
  supportingPagesGapForThisSurface(): boolean {
    return this.embedReviewSurface
      ? this.hasReviewSupportingPagesGaps()
      : this.hasSupportingPagesActionableGap();
  }

  topicOwnershipGapChipLabel(): string {
    if (this.embedReviewSurface) {
      const n = this.reviewSupportingPagesColumns().reduce((sum, c) => sum + c.gaps.length, 0);
      return this.reviewCountBadge(n, 'PAGE', 'PAGES');
    }
    return this.supportingPagesGapForThisSurface() ? 'GAP FOUND' : 'NO GAP';
  }

  topicOwnershipGapChipTone(): DiagnosisTone {
    return this.supportingPagesGapForThisSurface() ? 'warn' : 'ok';
  }

  supportingPagesUnifiedLoading(): boolean {
    return this.topicOwnershipStatus === 'loading' || this.topicOwnershipV2Status === 'loading';
  }

  /** The rules live in `utils/supporting-pages-render-gate` — see the note there. */
  private supportingPagesGateState(): SupportingPagesGateState {
    return {
      topicOwnershipStatus: this.topicOwnershipStatus as SupportingPagesLoadStatus,
      topicOwnershipV2Status: this.topicOwnershipV2Status as SupportingPagesLoadStatus,
      markedSerpEvalPending: this.markedSerpEval.pending,
      columnCount: this.getSupportingPagesColumns().length,
      settledOnly: this.embedReviewSurface
    };
  }

  supportingPagesSettling(): boolean {
    return supportingPagesSettling(this.supportingPagesGateState());
  }

  getSupportingPagesColumns(): SupportingPagesUnifiedColumn[] {
    const extractDomain = (pageUrl: string) => this.extractDomainFromUrl(pageUrl);
    const markedDomains = this.markedCompetitorDomains.map((d) => this.normalizeSerpDomain(d));
    const markedV2Rows = this.allMarkedV2Rows();
    const preferredByDomain = this.markedCompetitorUrlsOnSerp();
    let columns: SupportingPagesUnifiedColumn[] = [];

    const snapshotDomains = this.topicOwnershipDomains.filter(
      (block) =>
        block.isYourSite ||
        markedDomains.includes(this.normalizeSerpDomain(block.domain))
    );

    if (this.topicOwnershipStatus === 'ready' && snapshotDomains.length) {
      columns = buildSupportingPagesUnifiedColumns({
        domains: snapshotDomains,
        focalUrl: this.url || '',
        v2Rows: markedV2Rows,
        preferredCompetitorUrlByDomain: preferredByDomain,
        extractDomain,
        filterRows: (rows) =>
          this.getTopicOwnershipRowsForDisplay({ domain: '', isYourSite: false, rows })
      });
    } else if (this.topicOwnershipStatus === 'ready' && markedV2Rows.length) {
      columns = buildSupportingPagesV2OnlyColumns({
        focalUrl: this.url || '',
        yourSiteDomain: extractDomain(this.url || '') || 'Your site',
        v2Rows: markedV2Rows,
        extractDomain
      });
    } else if (this.topicOwnershipStatus === 'ready') {
      const yourSiteDomain = extractDomain(this.url || '');
      if (yourSiteDomain) {
        columns = [
          {
            domain: yourSiteDomain,
            isYourSite: true,
            heroUrl: '',
            heroRow: null,
            supportingRows: [],
            v2Row: null
          }
        ];
      }
    }

    return attachV2RowsToSupportingPageColumns(
      augmentSupportingPagesColumns({
        columns,
        v2Rows: markedV2Rows,
        serpSeeds: this.supportingPagesSerpSeeds(),
        extractDomain,
        markedDomains
      }),
      markedV2Rows,
      extractDomain
    );
  }

  private allMarkedV2Rows(): SupportingPagesV2RowResult[] {
    const fromMerged = this.v2RowsForMarkedCompetitors(this.mergedV2Rows());
    const keys = new Set(fromMerged.map((r) => normalizeUrlForCompare(r.competitorUrl)));
    const extra = this.markedSerpCompetitorV2Rows.filter(
      (r) => !keys.has(normalizeUrlForCompare(r.competitorUrl))
    );
    return [...fromMerged, ...extra];
  }

  private ensureMarkedSerpCompetitorEvals(): void {
    const focalUrl = (this.url || '').trim();
    if (!focalUrl || !this.pageSerp?.results?.length || !this.markedCompetitorDomains.length) {
      return;
    }
    if (this.topicOwnershipV2Status === 'loading' || this.topicOwnershipStatus === 'loading') {
      return;
    }

    const seeds = this.supportingPagesSerpSeeds();
    if (!seeds.length) return;

    const existing = new Set(
      this.allMarkedV2Rows().map((r) => normalizeUrlForCompare(r.competitorUrl))
    );
    const missing = seeds.filter((s) => !existing.has(normalizeUrlForCompare(s.url)));
    if (!missing.length) return;

    // The third source. It adds rows to a card the other two have already
    // reported ready, which on a review surface means the operator watches a
    // verdict change under them -- see `supportingPagesSettling`.
    const focalQuery = this.focalQueryForRankDrivenGaps();
    const token = this.markedSerpEval.begin();
    const subscription = this.dataForSEOService
      .postSupportingPagesOpportunityEval({
        focalUrl,
        competitorUrls: missing.map((s) => s.url),
        yourCandidateUrls: [],
        focalQuery,
        v1ByCompetitor: missing.map((s) => ({
          competitorUrl: s.url,
          similarUrl: null,
          score: null
        }))
      })
      .subscribe({
        next: (res) => {
          // False means this answers a question the operator has moved on from.
          if (!this.markedSerpEval.settle(token)) return;
          const rows = res.data?.rows || [];
          if (!rows.length) return;
          const keys = new Set(
            this.markedSerpCompetitorV2Rows.map((r) => normalizeUrlForCompare(r.competitorUrl))
          );
          const added = rows.filter((r) => !keys.has(normalizeUrlForCompare(r.competitorUrl)));
          if (added.length) {
            this.markedSerpCompetitorV2Rows = [...this.markedSerpCompetitorV2Rows, ...added];
          }
        },
        error: () => {
          this.markedSerpEval.settle(token);
        }
      });
    this.markedSerpEval.track(subscription);
  }

  supportingPagesShowHeroChips(col: SupportingPagesUnifiedColumn): boolean {
    return !col.isYourSite && !!(col.heroUrl || '').trim();
  }

  private v2RowsForMarkedCompetitors(
    rows: SupportingPagesV2RowResult[]
  ): SupportingPagesV2RowResult[] {
    return rows.filter((row) => {
      const domain = this.normalizeSerpDomain(this.extractDomainFromUrl(row.competitorUrl));
      return (
        !!domain &&
        this.markedCompetitorDomains.some((m) => this.normalizeSerpDomain(m) === domain)
      );
    });
  }

  /** Marked competitor URL on this page's SERP, keyed by normalized domain. */
  private markedCompetitorUrlsOnSerp(): Record<string, string> {
    const out: Record<string, string> = {};
    const serpResults = this.pageSerp?.results || [];
    for (const marked of this.markedCompetitorDomains) {
      const key = this.normalizeSerpDomain(marked);
      const hit = serpResults.find((row) => this.normalizeSerpDomain(row.domain) === key);
      if (hit?.url?.trim()) out[key] = hit.url.trim();
    }
    return out;
  }

  /** Marked competitors on this page's SERP — inject when topic snapshot has no rows. */
  private supportingPagesSerpSeeds(): SerpCompetitorColumnSeed[] {
    const seeds: SerpCompetitorColumnSeed[] = [];
    const seen = new Set<string>();

    for (const marked of this.markedCompetitorDomains) {
      const key = this.normalizeSerpDomain(marked);
      if (!key || seen.has(key)) continue;
      const hit = (this.pageSerp?.results || []).find(
        (row) => this.normalizeSerpDomain(row.domain) === key
      );
      if (!hit?.url?.trim()) continue;
      seen.add(key);
      seeds.push({ domain: key, url: hit.url.trim(), source: 'marked' });
    }

    return seeds;
  }

  private normalizeSerpDomain(domain: string): string {
    return (domain || '').toLowerCase().trim().replace(/^www\./, '');
  }

  topicOwnershipHeroLinkTitle(col: SupportingPagesUnifiedColumn): string {
    if (col.isYourSite) return col.heroUrl;
    const row = col.heroRow || { url: col.heroUrl, seoIntentScore: 0, topics: [] };
    return this.topicOwnershipLinkTitle(
      { domain: col.domain, isYourSite: false, rows: col.supportingRows },
      row
    );
  }

  topicOwnershipSupportingLinkTitle(col: SupportingPagesUnifiedColumn, row: DomainTopicUrlRow): string {
    return this.topicOwnershipLinkTitle(
      { domain: col.domain, isYourSite: col.isYourSite, rows: col.supportingRows },
      row
    );
  }

  getVisibleSupportingRows(col: SupportingPagesUnifiedColumn): DomainTopicUrlRow[] {
    if (this.isTopicOwnershipPagesExpanded(col.domain)) return col.supportingRows;
    return col.supportingRows.slice(0, SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT);
  }

  hasMoreSupportingRowsInColumn(col: SupportingPagesUnifiedColumn): boolean {
    return col.supportingRows.length > SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT;
  }

  supportingRowsExpandLabel(col: SupportingPagesUnifiedColumn): string {
    if (this.isTopicOwnershipPagesExpanded(col.domain)) return 'Show fewer pages';
    const hidden = Math.max(0, col.supportingRows.length - SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT);
    return hidden > 0 ? `Show more pages (${hidden} more)` : 'Show more pages';
  }

  topicOwnershipV2RowForColumn(col: SupportingPagesUnifiedColumn): SupportingPagesV2RowResult | null {
    return col.v2Row;
  }

  /**
   * Whether to show the missing-sections chip at all.
   *
   * Must use the same count as the label beside it. Gating on the stored
   * `missingSectionCount` while labelling from the filtered set is how a chip
   * ends up reading "0 missing sections".
   */
  topicOwnershipV2HasMissingSections(row: SupportingPagesV2RowResult): boolean {
    return hasSectionGapsForV2Row(row);
  }

  topicOwnershipV2MissingSectionsChipLabel(row: SupportingPagesV2RowResult): string {
    // The filtered count, so the chip and the list it opens agree.
    const count = sectionGapsForV2Row(row).length;
    if (count === 1) return '1 missing section';
    return `${count} missing sections`;
  }

  topicOwnershipV2CoveredElsewhereChipLabel(row: SupportingPagesV2RowResult): string {
    return row.coveredElsewhereUrl ? 'Covered elsewhere' : 'Not covered elsewhere';
  }

  topicOwnershipV2CoveredElsewhereChipTitle(row: SupportingPagesV2RowResult): string {
    if (row.coveredElsewhereUrl) {
      return `${row.coveredElsewhereLabel || 'Covered elsewhere'}: ${row.coveredElsewhereUrl}`;
    }
    return 'No similar supporting page found elsewhere on your site for this competitor URL.';
  }

  topicOwnershipV2HasListInventory(row: SupportingPagesV2RowResult): boolean {
    return !!(
      row.listInventoryDiff?.detected && (row.listInventoryDiff.competitorOnly?.length ?? 0) > 0
    );
  }

  topicOwnershipV2ListInventoryChipLabel(row: SupportingPagesV2RowResult): string {
    const count = row.listInventoryDiff?.competitorOnly?.length ?? 0;
    if (count === 1) return '1 tool on their list, not on yours';
    return `${count} tools on their list, not on yours`;
  }

  topicOwnershipV2ListInventoryChipTitle(row: SupportingPagesV2RowResult): string {
    const diff = row.listInventoryDiff;
    if (!diff?.detected) return '';
    const missing = (diff.competitorOnly || []).map((item) => item.label).join(', ');
    return `Roundup list diff — on their list but not mentioned on your page: ${missing}`;
  }

  focalQueryForRankDrivenGaps(): string | undefined {
    const serpQuery = this.pageSerpTargetQueryLabel();
    if (serpQuery && serpQuery !== '—') return serpQuery;
    const claim = this.pageDemandClaimLabel();
    if (claim && claim !== '—') return claim;
    return undefined;
  }

  /**
   * Queries the TOP SERP RIVAL outranks you on.
   *
   * `activeSerpRivalCoverage()`, not the raw stored coverage. The stored row is
   * whichever rival the evaluation last ran against; the guard checks it is
   * still the rival the current SERP names, and everything else on this card
   * already goes through it.
   *
   * Without the guard the rows and the sentence above them came from different
   * places: the rows from the stored evaluation, the domain in "X outranks you
   * on 3 queries" from a live pick off the SERP. Two loads of the same page
   * showed the same three queries attributed first to argano.com and then to
   * business.adobe.com. A wrong number is bad; naming the wrong competitor
   * sends someone to read the wrong company's page.
   *
   * When the stored evaluation is stale the rows now disappear instead of
   * appearing under a name that did not produce them. Showing nothing is the
   * right failure here — the card is evidence, and evidence attributed to the
   * wrong source is worse than absent.
   */
  topSerpRivalRankDrivenGaps(): RankDrivenSectionGap[] {
    // One gate for every reader of these rows — the belt's card, the Dashboard
    // peek's count and title, the "does this page have an actionable gap"
    // predicate and the work-item composer. Switching the card off while the
    // peek still counted the rows would put a badge over a card that never
    // renders, which is the shape of half the bugs found in this queue.
    if (!RANK_DRIVEN_GAPS_ENABLED) return [];
    const row = this.activeSerpRivalCoverage()?.evalRow;
    return rankDrivenGapsForV2Row(row, {
      focalQuery: this.focalQueryForRankDrivenGaps(),
      focalAnalysis: this.analysisData,
      rivalAnalysis: undefined
    });
  }

  topicOwnershipV2RankDrivenGaps(row: SupportingPagesV2RowResult): RankDrivenSectionGap[] {
    return rankDrivenGapsForV2Row(row, {
      focalQuery: this.focalQueryForRankDrivenGaps(),
      focalAnalysis: this.analysisData,
      rivalAnalysis: undefined
    });
  }

  rankDrivenPassageSegments(keyword: string, passage: string | null | undefined) {
    return splitPassageByKeywordTokens(keyword, passage || '');
  }

  readonly rankDrivenHeadingTag = rankDrivenHeadingTag;

  topicOwnershipV2HasRankDrivenGaps(row: SupportingPagesV2RowResult): boolean {
    return this.topicOwnershipV2RankDrivenGaps(row).length > 0;
  }

  topicOwnershipV2RankDrivenChipLabel(row: SupportingPagesV2RowResult): string {
    const count = this.topicOwnershipV2RankDrivenGaps(row).length;
    if (count === 1) return '1 rank-driven gap';
    return `${count} rank-driven gaps`;
  }

  topicOwnershipV2RankDrivenChipTitle(row: SupportingPagesV2RowResult): string {
    const lines = this.topicOwnershipV2RankDrivenGaps(row).map((g) => g.evidenceLine);
    return lines.join(' ');
  }

  isTopicOwnershipV2RankDrivenOpen(col: SupportingPagesUnifiedColumn): boolean {
    const v2 = col.v2Row;
    return !!v2 && this.topicOwnershipV2RankDrivenUrl === v2.competitorUrl;
  }

  toggleTopicOwnershipV2RankDriven(competitorUrl: string): void {
    if (this.topicOwnershipV2RankDrivenUrl === competitorUrl) {
      this.topicOwnershipV2RankDrivenUrl = '';
      return;
    }
    this.topicOwnershipV2RankDrivenUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2ListInventoryUrl = '';
    this.topicOwnershipV2CoveredElsewhereUrl = '';
  }

  isTopicOwnershipV2ListInventoryOpen(col: SupportingPagesUnifiedColumn): boolean {
    const v2 = col.v2Row;
    return !!v2 && this.topicOwnershipV2ListInventoryUrl === v2.competitorUrl;
  }

  toggleTopicOwnershipV2ListInventory(competitorUrl: string): void {
    if (this.topicOwnershipV2ListInventoryUrl === competitorUrl) {
      this.topicOwnershipV2ListInventoryUrl = '';
      this.topicOwnershipV2RankDrivenUrl = '';
      return;
    }
    this.topicOwnershipV2ListInventoryUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2RankDrivenUrl = '';
    this.topicOwnershipV2CoveredElsewhereUrl = '';
  }

  isTopicOwnershipV2CompareOpen(col: SupportingPagesUnifiedColumn): boolean {
    const v2 = col.v2Row;
    return !!v2 && this.topicOwnershipV2DetailUrl === v2.competitorUrl;
  }

  /** Missing-sections compare panel — rendered full-width below domain columns. */
  getOpenSectionCompareV2Row(): SupportingPagesV2RowResult | null {
    const detailUrl = this.topicOwnershipV2DetailUrl?.trim();
    if (!detailUrl) return null;
    const row = this.mergedV2Rows().find(
      (r) => normalizeUrlForCompare(r.competitorUrl) === normalizeUrlForCompare(detailUrl)
    );
    if (!row || !this.topicOwnershipV2HasSectionCoverage(row)) return null;
    return row;
  }

  isTopicOwnershipV2CoveredElsewhereOpen(col: SupportingPagesUnifiedColumn): boolean {
    const v2 = col.v2Row;
    return !!v2 && this.topicOwnershipV2CoveredElsewhereUrl === v2.competitorUrl;
  }

  toggleTopicOwnershipV2CoveredElsewhere(competitorUrl: string): void {
    if (this.topicOwnershipV2CoveredElsewhereUrl === competitorUrl) {
      this.topicOwnershipV2CoveredElsewhereUrl = '';
      return;
    }
    this.topicOwnershipV2CoveredElsewhereUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2ListInventoryUrl = '';
    this.topicOwnershipV2RankDrivenUrl = '';
  }

  topicOwnershipCompareOptions(): TopicOwnershipCompareOption[] {
    const seen = new Set<string>();
    const options: TopicOwnershipCompareOption[] = [];
    for (const block of this.topicOwnershipDomains) {
      if (block.isYourSite) continue;
      for (const row of block.rows) {
        const normalized = normalizeUrlForCompare(row.url);
        if (!normalized || seen.has(normalized)) continue;
        seen.add(normalized);
        const path = this.topicOwnershipPagePath(row.url);
        const traffic = this.topicOwnershipPageTraffic(row.estimatedOrganicTraffic);
        const label = traffic ? `${block.domain} · ${path} (${traffic})` : `${block.domain} · ${path}`;
        options.push({
          url: row.url,
          label,
          seoIntentScore: row.seoIntentScore,
          focalMatchScore: row.focalMatchScore
        });
      }
    }
    const sorted = options.sort((a, b) =>
      compareSupportingPagesByRelevance(
        { focalMatchScore: a.focalMatchScore, seoIntentScore: a.seoIntentScore ?? 0 },
        { focalMatchScore: b.focalMatchScore, seoIntentScore: b.seoIntentScore ?? 0 }
      )
    );

    // The SERP winner is prepended rather than sorted in, and unsorted is the
    // point: this list is ordered by topical relevance, and the page that took
    // the position is not competing on that axis. It usually is not in the
    // topic snapshot at all — nobody marked `projectmanagementacademy.net`.
    const target = this.compareDefaultTarget();
    if (!target) return sorted;
    const key = normalizeUrlForCompare(target.url);
    if (!key || seen.has(key)) return sorted;

    return [
      {
        url: target.url,
        label: `${target.domain} · ${this.topicOwnershipPagePath(target.url)}${
          target.position ? ` (#${target.position} on this SERP)` : ''
        }`
      },
      ...sorted
    ];
  }

  /**
   * The auto-selected compare target (§3.1) — what the Compare button opens on.
   *
   * Null in every state where there is nothing to compare, so the button and
   * the "why this page" line disappear together rather than the button
   * offering a comparison the row has just said cannot be made.
   */
  compareDefaultTarget(): CompareSummaryTarget | null {
    if (!this.pageCompare) return null;
    return compareDrawerTarget(this.pageCompare);
  }

  /** The sentence under the button: why THIS page is the default. */
  compareDefaultReason(): string | null {
    return this.compareDefaultTarget()?.reason ?? null;
  }

  openCompareDefault(): void {
    const target = this.compareDefaultTarget();
    if (target) this.openQuickCompareWithUrl(target.url);
  }

  openQuickCompareWithUrl(compareUrl: string): void {
    const primaryUrl = this.normalizeNavigableUrl(this.url || '');
    const normalizedCompare = this.normalizeNavigableUrl(compareUrl);
    if (!primaryUrl || !normalizedCompare) return;
    this.quickComparePrimaryUrl = primaryUrl;
    this.quickCompareCompareUrl = normalizedCompare;
    this.quickCompareOpen = true;
  }

  onTopicOwnershipCompareSelected(competitorUrl: string): void {
    if (!competitorUrl?.trim()) return;
    this.openQuickCompareWithUrl(competitorUrl);
    this.topicOwnershipCompareUrl = '';
  }

  /**
   * The badges on the review surface count; everywhere else they judge.
   *
   * On review these cards only render when there IS something, so a verdict
   * badge reads GAP FOUND / ISSUES FOUND / WARNINGS FOUND on every card, on
   * every page, forever — the most valuable corner of each card spent saying
   * nothing. A count lets an operator size up a page without reading a body,
   * which is the whole job of the surface.
   *
   * It also matches the Dashboard peek, which has always said "1 SECTION" and
   * "2 PAGE-SPECIFIC" rather than a verdict — so the review was the odd one out.
   *
   * Inventory keeps the verdicts. Its cards render whether or not there is a
   * gap, so "NO GAP" is real information there and a count of zero is not.
   */
  private reviewCountBadge(n: number, one: string, many: string): string {
    return n === 1 ? `1 ${one}` : `${n} ${many}`;
  }

  technicalIssuesActionChipLabel(): string {
    if (this.embedReviewSurface) {
      return this.reviewCountBadge(
        this.reviewTechnicalIssues(this.technicalCriticalIssueViews()).length,
        'ISSUE',
        'ISSUES'
      );
    }
    return this.hasTechnicalCriticalIssues() ? 'ISSUES FOUND' : 'NO ISSUES';
  }

  technicalIssuesActionChipTone(): DiagnosisTone {
    return this.hasTechnicalCriticalIssues()
      ? 'warn'
      : 'ok';
  }

  technicalWarningsActionChipLabel(): string {
    if (this.embedReviewSurface) {
      return this.reviewCountBadge(
        this.reviewTechnicalIssues(this.technicalWarningIssueViews()).length,
        'WARNING',
        'WARNINGS'
      );
    }
    return this.hasTechnicalWarnings() ? 'WARNINGS FOUND' : 'NO WARNINGS';
  }

  technicalWarningsActionChipTone(): DiagnosisTone {
    return this.hasTechnicalWarnings()
      ? 'warn'
      : 'ok';
  }

  pageIntentMatchActionChipLabel(): string {
    const d = this.pageIntentDiagnosis;
    if (!d || d.serpFamily === 'unknown') return '—';
    if (d.serpFamily === 'mixed') return 'MIXED SERP';
    return d.intentAlignedWithSerp ? 'MATCH' : 'NO MATCH';
  }

  pageIntentMatchActionChipTone(): DiagnosisTone {
    const d = this.pageIntentDiagnosis;
    if (!d || d.serpFamily === 'unknown') return 'muted';
    // A mixed SERP your page's own type already ranks on is not a decision —
    // see `mixedSerpAlignment`. Green, so it leaves the review queue.
    if (this.pageTypeMixedAlignment()) return 'ok';
    if (d.serpFamily === 'mixed') return 'neutral';
    return d.intentAlignedWithSerp
      ? 'ok'
      : 'warn';
  }

  showSerpPhraseMatchActionChip(): boolean {
    if (!this.pageIntentDiagnosis) return false;
    return (
      this.hasLexicalVocabChips() ||
      this.showFragmentedSerpVocabularyNote() ||
      (this.pageIntentDiagnosis.missingTerms?.length ?? 0) > 0 ||
      this.pageIntentDiagnosis.answerStructureAligned === false
    );
  }

  serpPhraseMatchActionChipLabel(): string {
    const d = this.pageIntentDiagnosis;
    if (!d) return '—';
    const missing = d.missingTerms?.length ?? 0;
    const structureMismatch =
      Boolean(d.answerStructureFamily) &&
      d.answerStructureFamily !== 'unknown' &&
      d.answerStructureFamily !== 'fragmented' &&
      d.answerStructureAligned === false;
    if (missing === 0 && !structureMismatch) {
      return this.showFragmentedSerpVocabularyNote() ? 'REVIEW STRUCTURE' : 'ALIGNED';
    }
    if (structureMismatch && missing === 0) return 'STRUCTURE GAP';
    return missing > 0 ? 'MISSING SIGNALS' : 'ALIGNED';
  }

  serpPhraseMatchActionChipTone(): DiagnosisTone {
    const d = this.pageIntentDiagnosis;
    if (!d) return 'muted';
    const missing = d.missingTerms?.length ?? 0;
    const structureMismatch =
      Boolean(d.answerStructureFamily) &&
      d.answerStructureFamily !== 'unknown' &&
      d.answerStructureFamily !== 'fragmented' &&
      d.answerStructureAligned === false;
    if (missing === 0 && !structureMismatch) {
      return this.showFragmentedSerpVocabularyNote()
        ? 'neutral'
        : 'ok';
    }
    return 'warn';
  }

  serpDifficultyBand(): 'easy' | 'medium' | 'hard' | 'unknown' {
    const kd = this.pageSerp?.keywordDifficulty;
    if (!Number.isFinite(kd)) return 'unknown';
    const n = Number(kd);
    if (n < 15) return 'easy';
    if (n <= 25) return 'medium';
    return 'hard';
  }

  serpDifficultyActionChipLabel(): string {
    const band = this.serpDifficultyBand();
    if (band === 'unknown') return '—';
    return band.toUpperCase();
  }

  serpDifficultyActionChipTone(): DiagnosisTone {
    const band = this.serpDifficultyBand();
    if (band === 'easy') return 'ok';
    if (band === 'medium') return 'neutral';
    if (band === 'hard') return 'warn';
    return 'muted';
  }

  /**
   * Your page's type is one of the shapes this mixed SERP ranks, or null.
   *
   * Memoised per diagnosis rather than recomputed: the tone, the brief line and
   * the card gate all ask, and three answers to one question is the failure
   * this codebase keeps finding.
   */
  pageTypeMixedAlignment(): MixedSerpAlignment | null {
    const d = this.pageIntentDiagnosis;
    if (!d) return null;
    if (this.pageTypeMixedAlignmentFor !== d) {
      this.pageTypeMixedAlignmentFor = d;
      this.pageTypeMixedAlignmentCache = mixedSerpAlignment(
        d.serpFamily,
        d.pageTypeFamily || d.bestSegmentFamily,
        d.serpRowIntents,
        d.serpMixedIntentSamples
      );
    }
    return this.pageTypeMixedAlignmentCache;
  }

  private pageTypeMixedAlignmentFor: PageIntentDiagnosis | null = null;
  private pageTypeMixedAlignmentCache: MixedSerpAlignment | null = null;

  /**
   * Two SERP results that ARE the page type this card says Google expects.
   *
   * The verdict ("SERP expects definition") was unfalsifiable on the belt: the
   * only way to check it was to open Google, which is the click the belt
   * exists to remove. The titles also answer the question the verdict raises —
   * what would a definition page look like for this query.
   *
   * Review surface only. Inventory and URL detail already show the whole SERP
   * table with an intent column a row away, so there the examples would be a
   * second copy of what is on screen; on the belt that table is not there.
   *
   * The families live on the diagnosis and the titles on the SERP snapshot, so
   * they are joined by position here. Memoised per (diagnosis, snapshot) pair:
   * the template calls this on every change-detection pass.
   */
  pageTypeExamples(): PageTypeExample[] {
    if (!this.embedReviewSurface) return [];
    const d = this.pageIntentDiagnosis;
    const serp = this.pageSerp;
    if (!d || !serp) return [];
    if (this.pageTypeExamplesForDiagnosis !== d || this.pageTypeExamplesForSerp !== serp) {
      this.pageTypeExamplesForDiagnosis = d;
      this.pageTypeExamplesForSerp = serp;
      const titleByPosition = new Map<number, { domain: string; title: string }>();
      for (const row of serp.results || []) {
        titleByPosition.set(row.position, { domain: row.domain || '', title: row.title || '' });
      }
      this.pageTypeExamplesCache = serpPageTypeExamples(
        d.serpFamily,
        (d.serpRowIntents || []).map((row) => ({
          position: row.position,
          family: row.family,
          domain: titleByPosition.get(row.position)?.domain,
          title: titleByPosition.get(row.position)?.title
        })),
        serp.yourUrl || this.url || ''
      );
    }
    return this.pageTypeExamplesCache;
  }

  private pageTypeExamplesForDiagnosis: PageIntentDiagnosis | null = null;
  private pageTypeExamplesForSerp: PageSerpSnapshot | null = null;
  private pageTypeExamplesCache: PageTypeExample[] = [];

  /** "Ranking as definition" — names what the two examples below are examples OF. */
  pageTypeExamplesLabel(): string {
    const label = this.pageIntentFamilyLabel(this.pageIntentDiagnosis?.serpFamily);
    return label ? `Ranking as ${label.toLowerCase()}` : 'Ranking now';
  }

  /** Page-type card brief (serpFamily vs yours) — no answer-structure lines. */
  pageTypeBriefLines(): string[] {
    const d = this.pageIntentDiagnosis;
    if (!d) return [];
    const lines: string[] = [];
    const serpLabel = this.pageIntentFamilyLabel(d.serpFamily);
    const yoursFamily = d.pageTypeFamily || d.bestSegmentFamily;
    const yoursLabel = this.pageIntentFamilyLabel(yoursFamily);

    // Prefer the placement/why bullet (what's missing) over a vague “SERP mostly X”.
    const placement = d.bullets.find((b) => b.kind === 'placement');
    if (placement) {
      const text = placement.text.length > 220 ? `${placement.text.slice(0, 217)}…` : placement.text;
      lines.push(text);
    } else if (d.serpFamily === 'mixed') {
      const aligned = this.pageTypeMixedAlignment();
      if (aligned) {
        // Nothing to pick. Naming the rival that shares the type is what makes
        // this a finding rather than a reassurance.
        const who = aligned.domain
          ? `${aligned.domain}${aligned.position ? ` (#${aligned.position})` : ''}`
          : aligned.position
            ? `#${aligned.position}`
            : 'a top result';
        lines.push(
          `Top results disagree on page type, but ${this.pageIntentFamilyLabel(
            aligned.family
          )} is one of the shapes ranking — ${who} is the same. Nothing to change here.`
        );
      } else if (!(d.serpMixedIntentSamples?.length >= 2)) {
        lines.push('Top results disagree on page type — pick one angle.');
      } else {
        lines.push('Pick one page type to align with.');
      }
    } else if (d.intentAlignedWithSerp) {
      const where = d.pageTypeLabel ? ` (${d.pageTypeLabel})` : '';
      lines.push(`SERP and your page both read as ${serpLabel}${where}.`);
    } else if (yoursFamily === 'unknown' && d.serpFamily !== 'unknown') {
      lines.push(this.pageTypeMissingCuesLine(d.serpFamily));
    } else if (yoursFamily && yoursFamily !== 'unknown' && d.serpFamily !== 'unknown') {
      lines.push(`SERP expects ${serpLabel}; your page reads ${yoursLabel}.`);
    } else if (d.serpFamily !== 'unknown') {
      lines.push(`SERP mostly ${serpLabel}.`);
    }

    if (lines.length < 2 && !placement) {
      const extra = d.bullets.find((b) => b.kind === 'claim_mismatch');
      if (extra) {
        const text = extra.text.length > 140 ? `${extra.text.slice(0, 137)}…` : extra.text;
        lines.push(text);
      }
    }
    return lines.slice(0, 2);
  }

  private static readonly PAGE_TYPE_SIGNAL_CUES: Record<string, string> = {
    definition: '“is a/an”, “refers to”, “defined as”, or “means”',
    how_to: '“how to”, “step-by-step”, “steps to”, or “guide to”',
    listicle: '“best”, “top N”, “alternatives”, or “competitors”',
    versus: '“vs”, “versus”, “difference between”, or “compared to”',
    template: '“template”, “download”, “PDF”, or “worksheet”',
    pricing: '“pricing”, “plans”, “per month”, or price language'
  };

  private pageTypeMissingCuesLine(serpFamily: string): string {
    const label = this.pageIntentFamilyLabel(serpFamily);
    const cues = UrlDetailComponent.PAGE_TYPE_SIGNAL_CUES[serpFamily];
    if (!cues) {
      return `No section reads as ${label} — strengthen that page type in the intro or primary heading.`;
    }
    return `No section reads as ${label} — crawled copy lacks ${label} cues (${cues}). Add clear ${label} framing in the intro or primary heading.`;
  }

  /** Answer structure & entities card brief. */
  /**
   * The top results agree on no answer shape, so there is nothing to compare to.
   *
   * `fragmented` is the analyser's own word for it and `unknown` is its absence.
   * Either way the card has no rival to hold this page against: on
   * /templates/project-post-mortem-template every winner excerpt carries zero
   * structure-evidence spans, which is why "How they answer" rendered nothing
   * while "How you answer" quoted the page back at the operator.
   */
  serpAnswerStructureIsFragmented(): boolean {
    const family = this.pageIntentDiagnosis?.answerStructureFamily;
    return !family || family === 'fragmented' || family === 'unknown';
  }

  answerStructureBriefLines(): string[] {
    const d = this.pageIntentDiagnosis;
    if (!d) return [];
    const lines: string[] = [];

    const structureBullet = d.bullets.find((b) => b.kind === 'answer_structure');
    if (structureBullet) {
      const text =
        structureBullet.text.length > 180
          ? `${structureBullet.text.slice(0, 177)}…`
          : structureBullet.text;
      lines.push(text);
    } else if (d.answerStructureCue && !this.serpAnswerStructureIsFragmented()) {
      // The cue is the model's read of a consensus. When the consensus did not
      // hold — `fragmented`, ratio 0 — the cue survives in the payload and used
      // to be printed as the finding: "SERP answers via item lists" over a
      // diagnosis that says the top results share no shape at all. The Dashboard
      // read the honest line and called the row Clear; the belt read this one
      // and asked for a decision. Same page, opposite verdicts.
      const cue =
        d.answerStructureCue.length > 180
          ? `${d.answerStructureCue.slice(0, 177)}…`
          : d.answerStructureCue;
      lines.push(cue);
    }

    if (lines.length < 2) {
      const extra = d.bullets.find(
        (b) => b.kind === 'lexical_gap' || b.kind === 'vocabulary_fragmented'
      );
      if (extra) {
        const text = extra.text.length > 140 ? `${extra.text.slice(0, 137)}…` : extra.text;
        lines.push(text);
      }
    }
    return lines.slice(0, 2);
  }

  /** @deprecated Prefer pageTypeBriefLines / answerStructureBriefLines. */
  pageIntentBriefLines(): string[] {
    return this.pageTypeBriefLines();
  }

  showSerpPhraseMatchCard(): boolean {
    if (!this.pageIntentDiagnosis) return false;
    return (
      Boolean(this.pageIntentDiagnosis.answerStructureFamily) ||
      this.hasPageIntentCompare() ||
      this.hasLexicalVocabChips() ||
      this.showFragmentedSerpVocabularyNote()
    );
  }

  serpKeywordDifficultyShort(): string {
    const kd = this.pageSerp?.keywordDifficulty;
    if (!Number.isFinite(kd)) return '—';
    return `${Math.round(Number(kd))}`;
  }

  /** Shown only when KD is missing — not the scale/source line. */
  serpKeywordDifficultyHint(): string | null {
    if (!Number.isFinite(this.pageSerp?.keywordDifficulty)) {
      return 'Refresh SERP to load keyword difficulty.';
    }
    return null;
  }

  contentFreshnessVerdict(): string | null {
    return comparePageFreshnessToSerp(
      this.pageContentLastModifiedDate(),
      this.serpWinnerPublishedDates()
    ).verdict;
  }

  pageSerpQuerySourceLabel(): string {
    const source = this.pageSerp?.querySource;
    if (source === 'strategist_override') return 'Strategist override';
    if (source === 'realization') return 'Realization keyword';
    if (source === 'ceiling_driver') return 'Ceiling driver';
    if (source === 'claim_fallback') return 'Claim fallback';
    return '—';
  }

  pageSerpFetchedLabel(): string {
    if (!this.pageSerp?.fetchedAt) return '—';
    try {
      return new Date(this.pageSerp.fetchedAt).toLocaleString();
    } catch {
      return this.pageSerp.fetchedAt;
    }
  }

  pageSerpKeywordDifficultyLabel(): string | null {
    const kd = this.pageSerp?.keywordDifficulty;
    if (!Number.isFinite(kd)) return null;
    const source =
      this.pageSerp?.keywordDifficultyDataSource === 'api' ? 'Labs API' : 'cached';
    return `Keyword difficulty ${Math.round(Number(kd))}/100 (${source})`;
  }

  pageSerpKeywordDifficultyTitle(): string {
    return (
      'DataForSEO Labs keyword difficulty: how hard it is to rank in the top 10 for this target query ' +
      '(0 = easy, 100 = very hard). Updated on URL SERP refresh. Not per-domain authority.'
    );
  }

  pageSerpYourPresenceLabel(): string {
    if (!this.pageSerp) return '—';
    if (this.pageSerp.yourPosition === null) return 'Not in top 10';
    const match =
      this.pageSerp.matchType === 'exact_url'
        ? 'exact URL'
        : this.pageSerp.matchType === 'domain'
          ? 'domain match'
          : 'match';
    return `#${this.pageSerp.yourPosition} (${match})`;
  }

  pageSerpResultCompetitorKind(row: PageSerpResultRow): 'you' | 'marked' | 'unknown' {
    const activeDomain = this.normalizeSerpDomain(this.extractDomainFromUrl(this.url || ''));
    const rowDomain = this.normalizeSerpDomain(row.domain);
    if (activeDomain && rowDomain === activeDomain) return 'you';
    if (rowDomain && this.markedCompetitorDomains.some((d) => this.normalizeSerpDomain(d) === rowDomain)) {
      return 'marked';
    }
    return 'unknown';
  }

  pageSerpResultCompetitorLabel(row: PageSerpResultRow): string {
    const kind = this.pageSerpResultCompetitorKind(row);
    if (kind === 'you') return 'You';
    if (kind === 'marked') return 'Marked competitor';
    return 'Unknown';
  }

  pageSerpRowIntentFamily(row: PageSerpResultRow): string {
    const hit = this.pageIntentDiagnosis?.serpRowIntents?.find((i) => i.position === row.position);
    return hit?.family || 'unknown';
  }

  pageSerpRowIntentLabel(row: PageSerpResultRow): string {
    return this.pageIntentFamilyLabel(this.pageSerpRowIntentFamily(row));
  }

  trackPageSerpRow(_index: number, row: PageSerpResultRow): string {
    return `${row.position}::${row.domain}::${row.url}`;
  }

  pageSerpSnippetSegments(row: PageSerpResultRow): SerpSnippetSegment[] {
    return buildSerpSnippetSegments(row.description || '', row.highlighted);
  }

  /** Top-10 rows whose snippet contains the hovered chip (winners compare pane). */
  pageSerpCompareWinnerRows(): PageSerpResultRow[] {
    if (!this.hoveredVocabChip || !this.pageSerp?.results?.length) return [];
    return this.pageSerp.results.filter((row) => this.pageSerpRowHasVocabChipMatch(row));
  }

  pageSerpCompareWinnerRowSegments(row: PageSerpResultRow): SerpSnippetSegment[] {
    if (!this.hoveredVocabChip) return [];
    const snippet = [row.title, row.description].filter(Boolean).join(' — ');
    return buildSerpFoldedTermSegments(snippet, this.hoveredVocabChip, foldLexicalToken);
  }

  pageSerpRowHasVocabChipMatch(row: PageSerpResultRow): boolean {
    if (!this.hoveredVocabChip) return false;
    const snippet = [row.title, row.description].filter(Boolean).join(' — ');
    if (!snippet.trim()) return false;
    return buildSerpFoldedTermSegments(snippet, this.hoveredVocabChip, foldLexicalToken).some(
      (s) => s.emphasized
    );
  }

  hasPageIntentDiagnosis(): boolean {
    return (this.pageIntentDiagnosis?.bullets?.length || 0) > 0;
  }

  hasPageIntentCompare(): boolean {
    const d = this.pageIntentDiagnosis;
    if (!d) return false;
    return (
      Boolean(d.answerStructurePageText?.trim()) ||
      Boolean(d.yourBestSection?.text) ||
      (d.serpWinnerExcerpts?.length || 0) > 0
    );
  }

  /** Full page passage(s) for answer-structure match / closest (Our page column). */
  searchIntentStructurePagePassages(): Array<{
    label: string;
    text: string;
    matchKind: 'match' | 'closest';
    family?: string;
    evidenceSpans: string[];
    questionSpans: string[];
    headingIsQuestion: boolean;
    fitPct: number | null;
  }> {
    const d = this.pageIntentDiagnosis;
    if (!d) return [];
    const fromApi = (d.answerStructurePagePassages || [])
      .filter((p) => (p.text || '').trim() && (p.label || '').trim())
      .map((p) => ({
        label: p.label.trim(),
        text: p.text.trim(),
        matchKind: (p.matchKind === 'closest' ? 'closest' : 'match') as 'match' | 'closest',
        family: p.family,
        evidenceSpans: (p.evidenceSpans || d.answerStructurePageEvidenceSpans || []).filter(Boolean),
        questionSpans: (p.questionSpans || []).filter(Boolean),
        headingIsQuestion: Boolean(p.headingIsQuestion),
        fitPct: Number.isFinite(p.fitRatio) ? Math.round(Number(p.fitRatio) * 100) : null
      }));
    if (fromApi.length) {
      return dedupeOverlappingStructurePassages(fromApi, 3);
    }

    const text = (d.answerStructurePageText || '').trim();
    const label = (d.answerStructurePageLabel || '').trim();
    if (!text || !label) return [];
    return [
      {
        label,
        text,
        matchKind: d.answerStructureAligned ? 'match' : 'closest',
        family: d.answerStructurePageFamily || undefined,
        evidenceSpans: (d.answerStructurePageEvidenceSpans || []).filter(Boolean),
        questionSpans: [],
        headingIsQuestion: false,
        fitPct: null
      }
    ];
  }

  /** @deprecated Prefer searchIntentStructurePagePassages(). */
  searchIntentStructurePagePassage(): { label: string; text: string; matchKind: 'match' | 'closest' } | null {
    return this.searchIntentStructurePagePassages()[0] || null;
  }

  structurePassageHeading(label: string): { tag: string | null; title: string } {
    return parseStructurePassageHeading(label);
  }

  /** @deprecated Prefer per-passage fitPct on searchIntentStructurePagePassages(). */
  answerStructureMatchPct(): number | null {
    return null;
  }

  answerStructurePassageFitTitle(passage: {
    matchKind: 'match' | 'closest';
    fitPct: number | null;
    family?: string;
    headingIsQuestion?: boolean;
    questionSpans?: string[];
  }): string {
    const family = this.answerStructureFamilyLabel(
      passage.family || this.pageIntentDiagnosis?.answerStructureFamily
    );
    const pct = passage.fitPct;
    const questionNote =
      passage.headingIsQuestion || (passage.questionSpans?.length || 0) > 0
        ? ' Includes a bonus for restating the target query as a question.'
        : '';
    if (pct == null) return `How strongly this passage fits ${family} delivery.${questionNote}`;
    if (passage.matchKind === 'closest') {
      return `${pct}% fit — nearest delivery pattern on the page, not an exact ${family} match.${questionNote}`;
    }
    return `${pct}% fit — how strongly this passage exhibits ${family} delivery (cues + highlighted evidence).${questionNote}`;
  }

  searchIntentStructurePassageSegments(
    text: string,
    evidenceSpans?: string[] | null,
    questionSpans?: string[] | null,
    matchKind?: 'match' | 'closest'
  ): SerpSnippetSegment[] {
    // Keep Our-page structure view stable on chip hover — hover only filters the winners column.
    const spans =
      evidenceSpans ||
      this.pageIntentDiagnosis?.answerStructurePageEvidenceSpans ||
      [];
    return buildCollapsedStructurePassageSegments(text || '', spans, {
      questionSpans,
      matchKind
    });
  }

  structurePassageSegmentClass(
    seg: SerpSnippetSegment,
    passage: { matchKind: 'match' | 'closest' }
  ): string {
    if (!seg.emphasized) return '';
    if (seg.highlightKind === 'question') return 'url-serp-question-term';
    if (passage.matchKind === 'closest' || seg.highlightKind === 'closest') {
      return 'url-serp-closest-term';
    }
    return 'url-serp-contract-term';
  }

  hasSearchIntentQuestionEvidence(): boolean {
    return this.searchIntentStructurePagePassages().some(
      (p) => p.headingIsQuestion || (p.questionSpans?.length || 0) > 0
    );
  }

  onVocabChipHover(term: string | null): void {
    this.hoveredVocabChip = term;
  }

  clearVocabChipHover(): void {
    this.hoveredVocabChip = null;
  }

  answerStructureFamilyLabel(family: string | null | undefined): string {
    if (!family || family === 'unknown') return 'unknown';
    if (family === 'fragmented') return 'fragmented';
    return (
      UrlDetailComponent.ANSWER_STRUCTURE_LABELS[family] ||
      family.replace(/_/g, ' ')
    );
  }

  private static readonly ANSWER_STRUCTURE_LABELS: Record<string, string> = {
    item_list: 'item list',
    comparative_contrast: 'comparative contrast',
    numbered_howto: 'numbered how-to',
    tool_lander: 'tool lander',
    definitional_lede: 'definitional lede',
    feature_checklist: 'feature checklist',
    vs_framing: 'vs framing',
    fragmented: 'fragmented',
    unknown: 'unknown'
  };

  answerStructureSerpChipClass(): string {
    const d = this.pageIntentDiagnosis;
    const f = d?.answerStructureFamily;
    if (!f || f === 'unknown' || f === 'fragmented') {
      return 'badge badge--muted';
    }
    if (d?.answerStructureAligned) return 'badge badge--success';
    return 'badge';
  }

  answerStructureYourChipClass(): string {
    const d = this.pageIntentDiagnosis;
    if (!d?.answerStructurePageFamily || d.answerStructurePageFamily === 'unknown') {
      return 'badge badge--muted';
    }
    if (d.answerStructureAligned) return 'badge badge--success';
    return 'badge badge--warning';
  }

  answerStructureYourChipLabel(): string {
    const d = this.pageIntentDiagnosis;
    if (!d?.answerStructurePageFamily) return 'none';
    return this.answerStructureFamilyLabel(d.answerStructurePageFamily);
  }

  pageIntentContractTerms(): string[] {
    return this.pageIntentDiagnosis?.serpLexicalTerms || [];
  }

  isVocabChipHovered(term: string): boolean {
    return this.hoveredVocabChip === term;
  }

  pageIntentVocabChipTitle(term: string): string {
    if (this.hoveredVocabChip === term) {
      return `Showing “${term}” in matching top-10 snippets (right pane)`;
    }
    return `Hover to filter top-10 snippets for “${term}” (plural-aware)`;
  }

  pageIntentYourSectionSegments(text: string): SerpSnippetSegment[] {
    if (this.hoveredVocabChip) {
      return buildSerpFoldedTermSegments(text, this.hoveredVocabChip, foldLexicalToken);
    }
    return buildYourPageVocabularySegments(
      text,
      this.pageIntentContractTerms(),
      this.pageIntentDiagnosis?.serpCollocationHeads
    ).map((seg) =>
      seg.misaligned
        ? { text: seg.text, emphasized: false, misaligned: false }
        : seg
    );
  }

  pageIntentMisalignedPhraseLinks(text: string | null | undefined): MisalignedPhraseLink[] {
    if (!text?.trim()) return [];
    return collectMisalignedPhraseLinks(
      text,
      this.pageIntentContractTerms(),
      this.pageIntentDiagnosis?.serpCollocationHeads
    );
  }

  pageIntentMisalignedAriaLabel(seg: SerpSnippetSegment): string {
    const link = seg.misalignedLink;
    if (!link) return `${seg.text}: SERP vocabulary used in the wrong phrase context`;
    return `${link.head} connects to ${link.actual} on your page; winners use ${link.head} near ${link.expected}`;
  }

  pageIntentMisalignedChipTitle(term: string): string {
    const link = this.pageIntentMisalignedLinkForTerm(term);
    if (!link) return 'Used in the wrong phrase context';
    return `Your page: ${link.head} → ${link.actual}. Winners: ${link.head} → ${link.expected}.`;
  }

  private pageIntentMisalignedLinkForTerm(term: string) {
    const d = this.pageIntentDiagnosis;
    const texts = [d?.yourBestSection?.text, d?.yourIntroSection?.text].filter(Boolean) as string[];
    const termFold = term.toLowerCase().replace(/[^a-z0-9]+/g, '');
    for (const text of texts) {
      const link = this.pageIntentMisalignedPhraseLinks(text).find((l) => {
        const headFold = l.head.toLowerCase().replace(/[^a-z0-9]+/g, '');
        return headFold === termFold || headFold.startsWith(termFold) || termFold.startsWith(headFold);
      });
      if (link) return link;
    }
    return undefined;
  }

  hasLexicalVocabChips(): boolean {
    return this.serpVocabChipRows().length > 0;
  }

  /**
   * Structurally aligned AND no vocabulary to place or add.
   *
   * "Aligned" is read from the diagnosis rather than from the badge, because
   * the badge already collapses three states into one label and this needs the
   * structural half alone. Only the two groups the review card actually renders
   * count as something to send: `onPage` is a placement call and `missing` is a
   * gap. A term already in the right place asks for nothing.
   */
  reviewAnswerStructureHasNothingToSend(): boolean {
    const d = this.pageIntentDiagnosis;
    if (!d) return false;
    const family = d.answerStructureFamily;
    const decisiveSerp = Boolean(family) && family !== 'unknown' && family !== 'fragmented';
    if (!decisiveSerp || d.answerStructureAligned !== true) return false;
    const vocab = reviewAnswerVocabGroups(this.serpVocabChipRows());
    // Null means the composer found nothing worth grouping — nothing to send.
    if (!vocab) return true;
    return vocab.onPage.length === 0 && vocab.missing.length === 0;
  }

  /** Green = in chosen answer-structure passage; yellow = elsewhere on page; red = absent. */
  serpVocabChipRows(): Array<{ term: string; tone: 'in_passage' | 'on_page' | 'absent' }> {
    const d = this.pageIntentDiagnosis;
    if (!d) return [];

    // The server's answer when it sent one. It reads every crawled section;
    // this component only ever had three of them, so a word written in the
    // page's fifth section came back red here — the "boundaries" bug, where a
    // card said "never used" about a page's own opening sentence.
    const placed = d.serpTermPlacement;
    if (placed?.length) {
      return placed.slice(0, 16).map((row) => ({
        term: row.term,
        tone:
          row.where === 'in_answer'
            ? ('in_passage' as const)
            : row.where === 'on_page'
              ? ('on_page' as const)
              : ('absent' as const)
      }));
    }

    // Payloads written before placement existed. The fallback keeps the two
    // tones it can prove — a term in the passage, and a term in a section this
    // component holds — and calls everything else `on_page` rather than
    // `absent`: unmeasured must never render as a claim that the page never
    // uses the word.
    const terms = this.serpVocabChipTerms();
    if (!terms.length) return [];
    const passage = (d.answerStructurePageText || '').trim();
    const pageTexts = [
      passage,
      d.yourBestSection?.text || '',
      d.yourIntroSection?.text || ''
    ].filter((t) => t.trim().length > 0);

    return terms.map((term) => {
      if (passage && this.serpTextHasVocabTerm(passage, term)) {
        return { term, tone: 'in_passage' as const };
      }
      return { term, tone: 'on_page' as const };
    });
  }

  serpVocabChipClass(tone: 'in_passage' | 'on_page' | 'absent'): string {
    if (tone === 'in_passage') return 'badge badge--success';
    if (tone === 'on_page') return 'badge badge--warning';
    return 'badge badge--danger';
  }

  private serpVocabChipTerms(): string[] {
    const d = this.pageIntentDiagnosis;
    if (!d) return [];
    const fromContract = (d.serpLexicalTerms || []).filter(Boolean);
    if (fromContract.length) return fromContract.slice(0, 16);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const term of [...(d.presentLexicalTerms || []), ...(d.missingTerms || [])]) {
      const key = foldLexicalToken(term);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(term);
      if (out.length >= 16) break;
    }
    return out;
  }

  private serpTextHasVocabTerm(text: string, term: string): boolean {
    return buildSerpFoldedTermSegments(text, term, foldLexicalToken).some((s) => s.emphasized);
  }

  private static readonly WINNER_EXCERPTS_DEFAULT = 5;

  searchIntentWinnerExcerptsAll(): NonNullable<PageIntentDiagnosis['serpWinnerExcerpts']> {
    return this.pageIntentDiagnosis?.serpWinnerExcerpts || [];
  }

  searchIntentWinnerExcerpts(): NonNullable<PageIntentDiagnosis['serpWinnerExcerpts']> {
    const all = this.searchIntentWinnerExcerptsAll();
    if (this.winnerExcerptsExpanded) return all;
    return all.slice(0, UrlDetailComponent.WINNER_EXCERPTS_DEFAULT);
  }

  searchIntentWinnerExcerptsHiddenCount(): number {
    return Math.max(
      0,
      this.searchIntentWinnerExcerptsAll().length - UrlDetailComponent.WINNER_EXCERPTS_DEFAULT
    );
  }

  canExpandWinnerExcerpts(): boolean {
    return !this.winnerExcerptsExpanded && this.searchIntentWinnerExcerptsHiddenCount() > 0;
  }

  expandWinnerExcerpts(): void {
    // Capture Our-page height before expanding so the winners box fills that space
    // (scrolls inside) instead of growing the whole compare row.
    const yours = this.serpCompareYoursRef?.nativeElement;
    const winners = this.serpCompareWinnersRef?.nativeElement;
    const h = yours ? Math.round(yours.getBoundingClientRect().height) : 0;
    if (winners && h > 0) winners.style.maxHeight = `${h}px`;
    this.winnerExcerptsExpanded = true;
  }

  collapseWinnerExcerpts(): void {
    this.winnerExcerptsExpanded = false;
    const winners = this.serpCompareWinnersRef?.nativeElement;
    if (winners) winners.style.maxHeight = '';
  }

  hasSearchIntentStructureEvidence(): boolean {
    const excerpts = this.pageIntentDiagnosis?.serpWinnerExcerpts || [];
    if (excerpts.some((e) => (e.structureEvidenceSpans?.length || 0) > 0)) return true;
    if ((this.pageIntentDiagnosis?.answerStructurePageEvidenceSpans || []).length > 0) return true;
    return (this.pageIntentDiagnosis?.answerStructureEvidence || []).some(
      (e) => (e.spans?.length || 0) > 0
    );
  }

  searchIntentExcerptSegments(excerpt: {
    excerpt: string;
    highlightTerms?: string[];
    structureEvidenceSpans?: string[];
  }): SerpSnippetSegment[] {
    // Top winner excerpts: structure evidence only (vocab/DFS stays on chip hover below).
    const structure = (excerpt.structureEvidenceSpans || []).filter(Boolean);
    return buildSerpSnippetSegments(excerpt.excerpt || '', structure.length ? structure : []);
  }

  searchIntentSectionGapsRivalUrl(): string | null {
    const cov = this.activeSerpRivalCoverage();
    // Same filtered count as everywhere else, or this names a rival for a
    // section-gap link that opens on an empty list.
    if (!cov || !hasSectionGapsForV2Row(cov.evalRow)) return null;
    return cov.rivalUrl;
  }

  searchIntentSectionGapsRivalDomain(): string {
    const url = this.searchIntentSectionGapsRivalUrl();
    if (!url) return 'top rival';
    return this.extractDomainFromUrl(url) || 'top rival';
  }

  openSearchIntentSectionGaps(): void {
    const rivalUrl = this.searchIntentSectionGapsRivalUrl();
    if (!rivalUrl) return;
    this.openSerpRivalSectionCompare(rivalUrl);
  }

  /** Top snippets lacked a shared repeated-vocabulary contract (chips intentionally omitted). */
  showFragmentedSerpVocabularyNote(): boolean {
    const d = this.pageIntentDiagnosis;
    if (!d) return false;
    const shared =
      d.serpVocabularyShared ?? (d.serpLexicalTerms?.length ?? 0) > 0;
    if (shared) return false;
    return this.hasPageIntentCompare() || this.hasPageIntentDiagnosis();
  }

  pageIntentDiagnosisBulletClass(bullet: PageIntentDiagnosisBullet): string {
    if (bullet.severity === 'success') return 'url-serp-diagnosis-success';
    if (bullet.severity === 'warn') return 'url-serp-diagnosis-warn';
    return 'url-serp-diagnosis-info';
  }

  private static readonly INTENT_FAMILY_LABELS: Record<string, string> = {
    definition: 'definition',
    how_to: 'how-to',
    listicle: 'list / roundup',
    versus: 'versus',
    template: 'template',
    pricing: 'pricing',
    mixed: 'mixed',
    unknown: 'unknown'
  };

  pageIntentFamilyLabel(family: string | null | undefined): string {
    if (!family || family === 'unknown') return 'unknown';
    return UrlDetailComponent.INTENT_FAMILY_LABELS[family] || family;
  }

  pageIntentYourPageFamilyLabel(): string {
    const d = this.pageIntentDiagnosis;
    return this.pageIntentFamilyLabel(d?.pageTypeFamily || d?.bestSegmentFamily);
  }

  pageIntentFamilyChipClassFor(family: string | null | undefined): string {
    const f = family || 'unknown';
    if (f === 'mixed' || f === 'unknown') return 'badge badge--muted';
    return 'badge';
  }

  pageIntentSerpFamilyChipClass(): string {
    const d = this.pageIntentDiagnosis;
    if (!d) return 'badge';
    if (d.serpFamily === 'mixed') return 'badge';
    if (d.serpFamily === 'unknown') return 'badge badge--muted';
    if (d.intentAlignedWithSerp) return 'badge badge--success';
    return 'badge';
  }

  pageIntentYourFamilyChipClass(): string {
    const d = this.pageIntentDiagnosis;
    if (!d) return 'badge badge--muted';
    if (d.intentAlignedWithSerp) return 'badge badge--success';
    const yours = d.pageTypeFamily || d.bestSegmentFamily;
    if (!yours || yours === 'unknown') return 'badge badge--muted';
    if (d.serpFamily === 'mixed') return 'badge badge--warning';
    return 'badge badge--warning';
  }

  showPageIntentAlignmentRow(): boolean {
    return Boolean(this.pageIntentDiagnosis);
  }

  pageSerpCompactSummary(): string | null {
    if (!this.pageSerp?.results?.length) return null;
    const presence = this.pageSerpYourPresenceLabel();
    let you = 0;
    let marked = 0;
    let unknown = 0;
    for (const row of this.pageSerp.results) {
      const kind = this.pageSerpResultCompetitorKind(row);
      if (kind === 'you') you += 1;
      else if (kind === 'marked') marked += 1;
      else unknown += 1;
    }
    const parts = [presence];
    if (you) parts.push(`${you} you`);
    if (marked) parts.push(`${marked} marked`);
    if (unknown) parts.push(`${unknown} unknown`);
    return parts.join(' · ');
  }

  pageSerpPublishedDate(row: PageSerpResultRow): Date | null {
    if (!row.publishedAt) return null;
    try {
      const d = new Date(row.publishedAt);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }

  pageSerpPublishedAgeClass(row: PageSerpResultRow): string {
    const d = this.pageSerpPublishedDate(row);
    if (!d) return '';
    const ageMs = Date.now() - d.getTime();
    const sixMonths = 183 * 24 * 60 * 60 * 1000;
    const twoYears = 730 * 24 * 60 * 60 * 1000;
    if (ageMs < sixMonths) return 'url-serp-date-fresh';
    if (ageMs < twoYears) return 'url-serp-date-aging';
    return 'url-serp-date-stale';
  }

  serpCompetitionInsight(): SerpCompetitionInsight | null {
    if (!this.pageSerp?.results?.length) return null;
    const yourPosition = this.pageSerp.yourPosition;
    const results = [...this.pageSerp.results].sort((a, b) => a.position - b.position);

    const markedCompetitorsAbove: SerpCompetitionMarkedAbove[] = [];
    for (const row of results) {
      if (this.pageSerpResultCompetitorKind(row) !== 'marked') continue;
      if (row.position > 5) continue;
      const beatsYou = yourPosition === null || row.position < yourPosition;
      if (!beatsYou) continue;
      markedCompetitorsAbove.push({
        position: row.position,
        domain: row.domain,
        url: row.url,
        title: row.title
      });
    }

    const markedAboveYou = markedCompetitorsAbove.length > 0;
    const topLeaders: SerpCompetitionTopLeader[] = results
      .filter((row) => row.position >= 1 && row.position <= 3)
      .filter((row) => this.pageSerpResultCompetitorKind(row) !== 'you')
      .filter((row) => {
        const domain = (row.domain || '').trim().toLowerCase().replace(/^www\./, '');
        return domain && !isNonCompetitorDomain(domain);
      })
      .map((row) => ({
        position: row.position,
        domain: row.domain,
        url: row.url,
        title: row.title,
        descriptionPreview: this.serpCompetitionDescriptionPreview(row.description),
        publishedLabel: this.serpCompetitionPublishedLabel(row)
      }));

    return {
      yourPosition,
      markedAboveYou,
      markedCompetitorsAbove,
      topLeaders
    };
  }

  /**
   * Top 3 SERP slots in rank order (#1 → #3), including social/UGC.
   * Social rows are listed for visibility but should not offer Quick Compare.
   */
  serpCompetitionTopThreeRows(): SerpCompetitionDisplayRow[] {
    if (!this.pageSerp?.results?.length) return [];
    return [...this.pageSerp.results]
      .filter((row) => row.position >= 1 && row.position <= 3)
      .filter((row) => this.pageSerpResultCompetitorKind(row) !== 'you')
      .sort((a, b) => a.position - b.position)
      .map((row) => {
        const weakReason = weakSerpCompareReason(row.url, {
          title: row.title,
          description: row.description
        });
        return {
          position: row.position,
          domain: row.domain,
          url: row.url,
          title: row.title,
          descriptionPreview: this.serpCompetitionDescriptionPreview(row.description),
          publishedLabel: this.serpCompetitionPublishedLabel(row),
          isSocial: isNonCompetitorDomain(row.domain),
          isWeakCompareTarget: weakReason !== null,
          weakCompareBadge: weakReason ? weakSerpCompareBadgeLabel(weakReason) : null,
          isMarked: this.pageSerpResultCompetitorKind(row) === 'marked',
          intentLabel: this.pageSerpRowIntentLabel(row)
        };
      });
  }

  /** Marked rivals in top 5 not already shown in {@link serpCompetitionTopThreeRows}. */
  serpCompetitionMarkedRivalsExtra(insight: SerpCompetitionInsight): SerpCompetitionMarkedAbove[] {
    const topKeys = new Set(
      this.serpCompetitionTopThreeRows().map((row) => normalizeUrlForCompare(row.url))
    );
    return insight.markedCompetitorsAbove.filter(
      (row) => !topKeys.has(normalizeUrlForCompare(row.url))
    );
  }

  /**
   * Whether a SERP row offers Quick Compare.
   *
   * The two guards that remain are the ones that describe the TARGET: a social
   * result and a `isWeakCompareTarget` are pages a content comparison cannot
   * say anything useful about, so comparing them produces a confident-looking
   * answer about nothing.
   *
   * What was here and is gone is `insight.markedAboveYou || this.isDevView()`.
   * It came in with the marked-competitor supporting-pages pass, and the effect
   * was that the page ranking #1 for your own query offered no compare button
   * unless a *marked* rival also happened to be above you — so on the exact
   * page where you are not in the top 10 and want to know why, the winner was
   * the one row you could not open. Marking a competitor is a statement about
   * who you track over time; it is not a precondition for looking at whoever is
   * beating you today.
   */
  serpCompetitionShowQuickCompare(row: SerpCompetitionDisplayRow): boolean {
    if (row.isSocial || row.isWeakCompareTarget) return false;
    return Boolean(this.serpCompetitionInsight());
  }

  serpCompetitionYourRankLabel(): string {
    const pos = this.pageSerp?.yourPosition;
    if (pos === null || pos === undefined) return 'Not in top 10';
    return `#${pos}`;
  }

  serpCompetitionMarkedAboveLabel(): string {
    if (!this.markedCompetitorDomains.length) return '—';
    const insight = this.serpCompetitionInsight();
    if (!insight) return '—';
    return insight.markedAboveYou ? 'Yes' : 'No';
  }

  serpCompetitionChipLabel(): string {
    if (!this.markedCompetitorDomains.length) return 'NO MARKED RIVALS';
    const insight = this.serpCompetitionInsight();
    if (!insight) return '—';
    if (insight.markedAboveYou) return 'COMPETITORS AHEAD';
    if (insight.yourPosition === 1) return 'YOU LEAD';
    return 'NO RIVALS AHEAD';
  }

  serpCompetitionChipTone(): DiagnosisTone {
    if (!this.markedCompetitorDomains.length) return 'muted';
    const insight = this.serpCompetitionInsight();
    if (!insight) return 'muted';
    // A marked rival ranking above you is a loss that has already happened,
    // not a thing to look at: --danger, not --warning.
    if (insight.markedAboveYou) return 'bad';
    if (insight.yourPosition === 1) return 'ok';
    return 'neutral';
  }

  private serpCompetitionDescriptionPreview(description: string | undefined): string {
    const text = (description || '').replace(/\s+/g, ' ').trim();
    if (!text) return '';
    return text.length > 160 ? `${text.slice(0, 157)}…` : text;
  }

  private serpCompetitionPublishedLabel(row: PageSerpResultRow): string | null {
    const d = this.pageSerpPublishedDate(row);
    if (!d) return null;
    try {
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return null;
    }
  }

  refreshUrlSerpForDomain(): void {
    const pageUrl = this.url || '';
    if (!pageUrl || this.urlSerpRefreshRunning) return;

    this.urlSerpRefreshRunning = true;
    this.urlSerpRefreshProgress = {
      percent: 0,
      keywordsDone: 0,
      totalKeywords: 0,
      statusLine: this.urlSerpForceRefresh
        ? 'Starting URL SERP refresh (force API)…'
        : 'Starting URL SERP refresh (cache-first)…',
      etaLabel: null,
      chunksCompleted: 0,
      runningCostUsd: 0
    };

    void this.urlSerpSync
      .refreshPageUrl(pageUrl, {
        forceRefresh: this.urlSerpForceRefresh,
        onProgress: (p) => {
          this.urlSerpRefreshProgress = p;
        }
      })
      .then(() => {
        this.loadAnalysisData();
        if (this.urlSerpRefreshProgress) {
          this.urlSerpRefreshProgress = {
            ...this.urlSerpRefreshProgress,
            percent: 100,
            etaLabel: 'Done',
            statusLine: 'SERP refresh complete'
          };
        }
      })
      .catch((err) => {
        console.error('Failed to refresh URL SERP snapshots:', err);
      })
      .finally(() => {
        this.urlSerpRefreshRunning = false;
        setTimeout(() => {
          if (!this.urlSerpRefreshRunning) {
            this.urlSerpRefreshProgress = null;
          }
        }, 6000);
      });
  }

  pageHealthModelLabel(): string {
    if (!this.pageHealth) return '—';
    if (this.pageHealth.model === 'realization_gap') return 'Realization gap';
    if (this.pageHealth.model === 'unscored') return 'Unscored';
    if (this.pageHealth.model === 'eligibility') return 'Eligibility';
    return 'Unscored';
  }

  pageSecondaryHealthLabel(): string {
    if (!this.pageSecondaryHealth?.band) {
      if (this.pageSecondaryHealth?.model === 'freshness_hold') return 'Recently updated';
      return '—';
    }
    return pageHealthBandLabel(this.pageSecondaryHealth.band);
  }

  pageSecondaryHealthClass(): string {
    if (!this.pageSecondaryHealth?.band) {
      if (this.pageSecondaryHealth?.model === 'freshness_hold') return 'score-neutral';
      return '';
    }
    return pageHealthBandClass(this.pageSecondaryHealth.band);
  }

  pageSecondaryHealthModelLabel(): string {
    if (!this.pageSecondaryHealth) return '—';
    if (this.pageSecondaryHealth.model === 'earned_visibility') return 'Earned visibility';
    if (this.pageSecondaryHealth.model === 'freshness_hold') return 'Freshness hold';
    return 'None';
  }

  secondaryUnscoredSegmentLabel(): string {
    const segment = this.pageSecondaryHealth?.unscoredSegment;
    if (!segment) return '—';
    if (segment === 'no_claim') return 'No demand claim';
    if (segment === 'low_confidence_claim') return 'Low-confidence demand claim';
    if (segment === 'no_market_data') return 'Awaiting earned market data';
    if (segment === 'no_aligned_ceiling') return 'No claim-aligned demand ceiling';
    return 'Demand ceiling below material upside floor';
  }

  hasSecondaryHealthDiagnostics(): boolean {
    return !!this.pageSecondaryHealth;
  }

  realizationGapGateLabel(): string {
    return this.pageRealization?.gapGate || '—';
  }

  formatRealizationNumber(value: number | null | undefined): string {
    if (!Number.isFinite(value)) return '—';
    return Number(value).toFixed(1);
  }

  formatRealizationRatio(value: number | null | undefined): string {
    if (!Number.isFinite(value)) return '—';
    return Number(value).toFixed(2);
  }

  /**
   * The domain's site-wide issue codes, or null until they land.
   *
   * Null is not "none" — it is "we do not know yet", and the Technical evidence
   * row renders its pre-split wording while it holds. Collapsing the two would
   * make a page briefly claim every one of its issues is its own before the
   * answer arrives, which is the flicker equivalent of a wrong number.
   */
  private siteWideIssueCodes: Set<string> | null = null;

  /**
   * Domain-level explanations for issues that stay page-specific, by code.
   *
   * Empty until the rollup lands, which is the right default: an issue with no
   * shared cause reads exactly as it did before, and one whose cause we have
   * not fetched yet must not imply we looked and found none.
   */
  private sharedIssueCauses: Record<string, string> = {};

  /**
   * Load the domain's technical rollup, so this page can tell its own issues
   * from the template's.
   *
   * The service caches per domain, so the five package rows an operator opens
   * in a row share one request. A failure leaves `siteWideIssueCodes` null and
   * the panel degrades to the undivided list it showed before this existed.
   */
  private loadSiteWideIssueCodes(): void {
    const domain = this.planV2IssueDomain();
    if (!domain) return;
    this.technicalRollup.getRollup(domain).subscribe((rollup) => {
      this.siteWideIssueCodes = rollup ? new Set(rollup.siteWideCodes) : null;
      // Checks are announced from `ngDoCheck` once the page has settled, not
      // from here. The rollup is cached per domain, so from page two onward it
      // resolved before that page's analysis existed and registered an EMPTY
      // list — after which nothing re-registered, the card could not tell it
      // had been emptied, and unticking every finding left "Sending" ticked
      // over a work item naming no work.
      this.sharedIssueCauses = Object.fromEntries(
        (rollup?.sharedCauses ?? []).map((c) => [c.code, c.note])
      );
    });
  }

  ngOnInit(): void {
    this.syncPlanV2EmbedHost();
    if (this.isEmbedMode()) {
      this.url = (this.embedUrl || '').trim() || null;
      // After `url`, always: embedded, the domain arrives as an @Input, but on
      // the standalone route `planV2IssueDomain()` derives it from `this.url`,
      // and asking before that is assigned resolves to no domain at all — which
      // is how the page-specific split silently worked in the Dashboard peek
      // and silently did not on the URL detail page.
      this.loadSiteWideIssueCodes();
      if (this.url) this.loadAnalysisData();
      return;
    }
    // Decode inventory route URL param (can be encoded multiple times).
    this.url = this.decodeUrlParam(this.route.snapshot.paramMap.get('url') || '');
    this.loadSiteWideIssueCodes();
    if (this.url) this.loadAnalysisData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['embedPreset'] || changes['embedUrl']) {
      this.syncPlanV2EmbedHost();
    }
    if (!this.isEmbedMode() || !changes['embedUrl'] || changes['embedUrl'].firstChange) return;
    const next = (changes['embedUrl'].currentValue || '').trim();
    if (next && next !== this.url) {
      // The settled flag is remembered per URL, so the memory has to be dropped
      // with the URL. Angular REUSES this component when the belt advances — it
      // is one `*ngIf` whose input changes, not a new instance — so without this
      // reset `lastEmittedSettled` stays `true` from the previous page. A page
      // that then loads without ever looking unsettled emits nothing at all, the
      // host never learns it is ready, and the spinner stays up forever.
      //
      // That is the "page 10 never loads" report: not slow, stuck.
      this.lastEmittedSettled = null;
      // Per-URL state, dropped with the URL — the component is REUSED as the
      // belt advances, so anything left set here describes the wrong page.
      this.markedSerpEval.cancel();
      this.url = next;
      this.loadAnalysisData();
    }
  }

  /**
   * Emits `embedSettledChange` on transitions only.
   *
   * `DoCheck` runs on every change-detection pass, so this compares against the
   * last value and emits nothing when nothing moved. The comparison is two
   * boolean reads; the alternative — a timer, or the host polling — is worse on
   * both accuracy and cost.
   */
  private lastEmittedSettled: boolean | null = null;

  ngDoCheck(): void {
    if (!this.embedReviewSurface) return;
    this.trackSupportingPagesPending();
    // Page signals feed the Internal links block, and they arrive on their own
    // request — left out of this, that block lands after the page has been
    // declared ready and shifts everything under it.
    const settled =
      this.analysisStatus === 'success' &&
      !this.supportingPagesSettling() &&
      !this.pageSignalsLoading;
    // Only once the data is final. Registering rows mid-load would hand the
    // send a list the operator never saw, and the columns genuinely do change
    // while the marked-competitor evaluation is still running.
    if (settled) {
      this.announceReviewCardItems();
      this.announceSiteWideCodes();
    }
    if (settled === this.lastEmittedSettled) return;
    this.lastEmittedSettled = settled;
    this.embedSettledChange.emit(settled);
  }

  /**
   * Only mention the wait if there is going to be one.
   *
   * With the evaluation cached server-side, a warm page settles in about
   * 200ms — so the placeholder appeared and vanished before it could be read,
   * on pages that were never going to show a card at all. A message that says
   * "checking" and then resolves to nothing is worse than silence: it draws
   * the eye to an absence.
   *
   * The threshold matches `--duration-slow` (324ms), which is the point this
   * design system already treats as "long enough that a person notices". Below
   * it, nothing renders and the card simply appears; above it, the wait is real
   * and worth explaining.
   */
  private static readonly PENDING_VISIBLE_AFTER_MS = 324;
  private supportingPagesPendingVisible = false;
  private supportingPagesPendingTimer: ReturnType<typeof setTimeout> | null = null;

  private trackSupportingPagesPending(): void {
    if (!this.supportingPagesSettling()) {
      this.clearSupportingPagesPendingTimer();
      this.supportingPagesPendingVisible = false;
      return;
    }
    if (this.supportingPagesPendingVisible || this.supportingPagesPendingTimer) return;
    this.supportingPagesPendingTimer = setTimeout(() => {
      this.supportingPagesPendingTimer = null;
      this.supportingPagesPendingVisible = true;
    }, UrlDetailComponent.PENDING_VISIBLE_AFTER_MS);
  }

  private clearSupportingPagesPendingTimer(): void {
    if (!this.supportingPagesPendingTimer) return;
    clearTimeout(this.supportingPagesPendingTimer);
    this.supportingPagesPendingTimer = null;
  }

  ngOnDestroy(): void {
    this.clearSupportingPagesPendingTimer();
    this.markedSerpEval.cancel();
    this.planV2EmbedContext.registerHost(null);
    this.signalsSubscription?.unsubscribe();
    this.analysisSubscription?.unsubscribe();
    this.clearPageRefetchPollTimer();
  }

  refetchSinglePageAnalysis(): void {
    const pageUrl = (this.url || '').trim();
    if (!this.isDevView() || !pageUrl || this.pageRefetchRunning) return;

    this.pageRefetchRunning = true;
    this.pageRefetchStatus = 'Starting single-page re-fetch…';
    this.clearPageRefetchPollTimer();

    this.dataForSEOService.refetchSinglePageAnalysis(pageUrl).subscribe({
      next: (res) => {
        const taskId = String(res?.data?.taskId || '').trim();
        const status = String(res?.data?.status || '').trim();
        if (status === 'completed') {
          this.pageRefetchStatus = 'Single-page re-fetch completed. Reloading URL detail…';
          this.pageRefetchRunning = false;
          this.loadAnalysisData();
          return;
        }
        if (status === 'pending' && taskId) {
          this.pageRefetchStatus = 'Re-fetch task queued. Waiting for completion…';
          this.pollSinglePageRefetch(taskId, 1);
          return;
        }
        this.pageRefetchStatus = 'Re-fetch returned no task. Please retry.';
        this.pageRefetchRunning = false;
      },
      error: (error) => {
        this.pageRefetchStatus =
          error?.error?.message || error?.message || 'Failed to start single-page re-fetch.';
        this.pageRefetchRunning = false;
      }
    });
  }

  private pollSinglePageRefetch(taskId: string, attempt: number): void {
    const maxAttempts = 60; // 5 minutes @ 5s intervals
    this.dataForSEOService.getUrlAnalysisStatus(taskId).subscribe({
      next: (res) => {
        const status = String(res?.data?.status || '').trim();
        if (status === 'completed') {
          this.pageRefetchStatus = 'Single-page re-fetch completed. Reloading URL detail…';
          this.pageRefetchRunning = false;
          this.clearPageRefetchPollTimer();
          this.loadAnalysisData();
          return;
        }
        if (status === 'failed') {
          this.pageRefetchStatus = res?.data?.error || 'Single-page re-fetch failed.';
          this.pageRefetchRunning = false;
          this.clearPageRefetchPollTimer();
          return;
        }
        if (attempt >= maxAttempts) {
          this.pageRefetchStatus = 'Still running in background. You can refresh URL detail in a moment.';
          this.pageRefetchRunning = false;
          this.clearPageRefetchPollTimer();
          return;
        }
        this.pageRefetchStatus = `Re-fetch running… (${attempt}/${maxAttempts})`;
        this.pageRefetchPollTimer = setTimeout(() => this.pollSinglePageRefetch(taskId, attempt + 1), 5000);
      },
      error: (error) => {
        this.pageRefetchStatus =
          error?.error?.message || error?.message || 'Failed to poll single-page re-fetch status.';
        this.pageRefetchRunning = false;
        this.clearPageRefetchPollTimer();
      }
    });
  }

  private clearPageRefetchPollTimer(): void {
    if (!this.pageRefetchPollTimer) return;
    clearTimeout(this.pageRefetchPollTimer);
    this.pageRefetchPollTimer = null;
  }

  loadAnalysisData(): void {
    if (!this.url) return;
    
    this.clearPageRefetchPollTimer();
    this.analysisStatus = 'loading';
    this.pageUrlId = '';
    // Cleared with the rest: a compare target left over from the previous URL
    // would name someone else's rival under this page's heading.
    this.pageCompare = null;
    this.planClusters = [];
    this.pageSignals = [];
    this.pageSignalsLoading = false;
    this.cannibalSignalRows = [];
    this.internalLinkSignalRows = [];
    this.unclusteredSignalRows = [];
    this.signalsSubscription?.unsubscribe();
    this.pageTopicsExpanded = false;
    this.topicOwnershipStatus = 'idle';
    this.topicOwnershipDomains = [];
    this.topicOwnershipPagesExpandedByDomain.clear();
    this.topicOwnershipUserExpanded = false;
    this.technicalIssueDetailsExpanded.clear();
    const gen = ++this.analysisLoadGen;
    this.analysisSubscription?.unsubscribe();
    this.analysisSubscription = this.dataForSEOService.getUrlAnalysis(this.url).subscribe({
      next: (response) => {
        if (gen !== this.analysisLoadGen) return;
        if (response.success) {
          this.pageUrlId = (response.data.urlId || '').trim();
          this.planClusters = Array.isArray(response.data.planClusters)
            ? response.data.planClusters.filter((c) => typeof c === 'string' && c.trim())
            : [];
          this.analysisData = response.data.analysis;
          this.pageDemand = response.data.pageDemand || null;
          this.pageRealization = response.data.pageRealization || null;
          this.pageHealth = response.data.pageHealth || null;
          this.pageSecondaryHealth = response.data.pageSecondaryHealth || null;
          this.pageSerp = response.data.pageSerp || null;
          this.pageCompare = response.data.pageCompare || null;
          this.ensureMarkedSerpCompetitorEvals();
          this.pageIntentDiagnosis = response.data.pageIntentDiagnosis || null;
          this.collapseWinnerExcerpts();
          this.analysisStatus = 'success';
          this.loadPageSignals();
          this.loadPlanV2NextAction();
          if (this.analysisData) {
            this.extractContentElements();
            this.extractLinkData();
            this.loadCompetitorTopicCoverage();
            this.loadTopicOwnership();
          }
        } else {
          this.analysisStatus = 'error';
        }
      },
      error: (error) => {
        if (gen !== this.analysisLoadGen) return;
        console.error('Failed to load analysis:', error);
        this.analysisStatus = 'error';
      }
    });
  }

  /** Two states that look alike in one line, so they get separate sentences. */
  planV2PeriodHint(): string {
    return this.planV2Period?.hasPair
      ? 'The crawl pair this action is read against — the same period the dashboard and Issues show.'
      : 'Only one crawl has been captured for this domain, so there is no previous crawl to compare against yet.';
  }

  private loadPlanV2NextAction(): void {
    if (this.isPlanV2Embed()) {
      this.planV2NextAction = null;
      this.planV2NextActionError = null;
      this.planV2Period = null;
      return;
    }
    if (!this.isActionPlanV2Enabled()) {
      this.planV2NextAction = null;
      this.planV2NextActionError = null;
      this.planV2Period = null;
      return;
    }
    const domain = this.extractDomainFromUrl(this.url || '');
    const urlId = this.pageUrlId.trim();
    if (!domain || !urlId) {
      this.planV2NextAction = null;
      this.planV2Period = null;
      return;
    }
    this.crawlRunPeriod.viewFor(domain, { refresh: true }).subscribe((view) => {
      // Ignore a late answer for a URL the operator has already navigated away from.
      if (this.extractDomainFromUrl(this.url || '') !== domain) return;
      this.planV2Period = view;
    });
    this.planV2NextActionLoading = true;
    this.planV2NextActionError = null;
    this.inventoryPlanClusters.getActionPlanV2(domain, { urlId, pageSize: 1 }).subscribe({
      next: (res) => {
        this.planV2NextAction = res?.data?.rows?.[0] ?? null;
        this.planV2NextActionLoading = false;
      },
      error: (err) => {
        this.planV2NextAction = null;
        this.planV2NextActionLoading = false;
        this.planV2NextActionError = err?.error?.message || 'Could not load next action.';
      }
    });
  }

  private loadPageSignals(): void {
    const domain = this.extractDomainFromUrl(this.url || '');
    const urlId = this.pageUrlId.trim();
    if (!domain || !urlId) {
      this.pageSignals = buildPageSignalsForUrl(urlId, this.planClusters, [], [], []);
      this.pageSignalsLoading = false;
      return;
    }

    this.pageSignalsLoading = true;
    this.signalsSubscription?.unsubscribe();
    this.signalsSubscription = forkJoin({
      cannibal: this.inventoryPlanClusters
        .getCannibalizationActionPlan(domain, {
          limit: 200,
          includeCompleted: false,
          includeDismissed: false,
          includePageIntersection: false
        })
        .pipe(catchError(() => of(null))),
      internalLinks: this.inventoryPlanClusters
        .getInternalLinkActionPlan(domain, {
          urlId,
          limit: 500,
          includeCompleted: false,
          includeDismissed: false
        })
        .pipe(catchError(() => of(null))),
      unclustered: this.inventoryPlanClusters
        .getUnclusteredPageActionPlan(domain, {
          limit: 200,
          includeCompleted: false,
          includeDismissed: false
        })
        .pipe(catchError(() => of(null)))
    }).subscribe({
      next: (res) => {
        this.cannibalSignalRows = Array.isArray(res.cannibal?.data?.rows) ? res.cannibal.data.rows : [];
        this.internalLinkSignalRows = Array.isArray(res.internalLinks?.data?.rows)
          ? res.internalLinks.data.rows
          : [];
        this.unclusteredSignalRows = Array.isArray(res.unclustered?.data?.rows) ? res.unclustered.data.rows : [];
        this.pageSignals = buildPageSignalsForUrl(
          urlId,
          this.planClusters,
          this.cannibalSignalRows.map((row) => ({
            status: row.status,
            loserUrlId: row.loserUrlId,
            urlAId: row.urlAId,
            urlBId: row.urlBId
          })),
          this.internalLinkSignalRows.map((row) => ({
            status: row.status,
            sourceUrlId: row.sourceUrlId,
            targetUrlId: row.targetUrlId
          })),
          this.unclusteredSignalRows.map((row) => ({
            status: row.status,
            urlId: row.urlId
          }))
        );
        this.pageSignalsLoading = false;
      },
      error: () => {
        this.pageSignals = buildPageSignalsForUrl(urlId, this.planClusters, [], [], []);
        this.pageSignalsLoading = false;
      }
    });
  }

  pageSignalChipClass(kind: InventoryPageSignalKind): string {
    return `page-signal-chip--${kind}`;
  }

  hasPageSignalKind(kind: InventoryPageSignalKind): boolean {
    return this.pageSignals.some((s) => s.kind === kind);
  }

  pageSignalCannibalDetails(): ActionPlanCannibalizationRow[] {
    const urlId = this.pageUrlId.trim();
    if (!urlId) return [];
    return this.cannibalSignalRows.filter(
      (row) =>
        row.status === 'open' &&
        (row.loserUrlId === urlId || row.urlAId === urlId || row.urlBId === urlId)
    );
  }

  pageSignalInternalLinkDetails(): ActionPlanInternalLinkRow[] {
    return filterInternalLinksIncomingToPage(this.internalLinkSignalRows, this.pageUrlId.trim());
  }

  /** Incoming links not already sent to Work items (Issues triage on the beneficiary URL). */
  pageSignalInternalLinkDetailsPendingTriage(): ActionPlanInternalLinkRow[] {
    return filterInternalLinksPendingIssuesTriage(
      this.internalLinkSignalRows,
      this.pageUrlId.trim(),
      internalLinkSuggestionKeysInWorkItems(this.planV2IssueQueue.records)
    );
  }

  pageSignalInternalLinkDetailsForDisplay(): ActionPlanInternalLinkRow[] {
    if (this.isPlanV2Embed()) {
      const exiting =
        this.planV2IssueQueue.isCardExiting(this.planV2IssueKey('internal_link'), 'work_item') ||
        this.planV2IssueQueue.isCardExiting(this.planV2IssueKey('internal_link'), 'dismissed');
      return exiting
        ? this.pageSignalInternalLinkDetails()
        : this.pageSignalInternalLinkDetailsPendingTriage();
    }
    return this.pageSignalInternalLinkDetails();
  }

  internalLinkRowDisplay(row: ActionPlanInternalLinkRow) {
    return buildInternalLinkRowDisplay(row, this.pageUrlId.trim());
  }

  internalLinkDirectionChip(row: ActionPlanInternalLinkRow): string {
    return internalLinkDirectionLabel(this.internalLinkRowDisplay(row).direction);
  }

  internalLinkDirectionChipClasses(row: ActionPlanInternalLinkRow): string {
    return internalLinkDirectionChipClass(this.internalLinkRowDisplay(row).direction);
  }

  internalLinkPlacementParts(row: ActionPlanInternalLinkRow) {
    const sentence = (row.where?.sentence || '').trim();
    const anchor = (row.suggestedAnchor || '').trim();
    return splitPlacementByAnchor(sentence, anchor);
  }

  formatInternalLinkScore(score: number): string {
    return formatInternalLinkRelevanceScore(score);
  }

  onInternalLinksComposingChange(composing: boolean): void {
    this.internalLinksComposing = composing;
    if (!composing) return;
    // List collapses when compose opens; keep the section anchored in the drawer viewport.
    setTimeout(() => this.scrollInternalLinksSectionIntoView(), 0);
  }

  private scrollInternalLinksSectionIntoView(): void {
    const el = this.internalLinksSectionRef?.nativeElement;
    if (!el) return;
    el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  pageSignalUnclusteredDetail(): ActionPlanUnclusteredPageRow | null {
    const urlId = this.pageUrlId.trim();
    if (!urlId) return null;
    return this.unclusteredSignalRows.find((row) => row.status === 'open' && row.urlId === urlId) || null;
  }

  pageSignalCannibalCounterpart(row: ActionPlanCannibalizationRow): string {
    const urlId = this.pageUrlId.trim();
    if (row.urlAId === urlId) return row.urlB;
    if (row.urlBId === urlId) return row.urlA;
    if (row.loserUrlId === urlId) {
      return row.winnerUrlId === row.urlAId ? row.urlB : row.urlA;
    }
    return row.urlB;
  }

  unclusteredRecommendationLabel(rec: ActionPlanUnclusteredPageRow['recommendation']): string {
    if (rec === 'delete') return 'Delete';
    if (rec === 'cluster_or_delete') return 'Cluster or delete';
    if (rec === 'rewrite_redirect_or_delete') return 'Rewrite, redirect, or delete';
    return rec;
  }

  inventoryDetailLink(targetUrl: string): string[] {
    const trimmed = (targetUrl || '').trim();
    return trimmed ? ['/inventory', encodeURIComponent(trimmed)] : ['/inventory'];
  }

  openInventoryForSignals(): void {
    const domain = this.extractDomainFromUrl(this.url || '');
    if (domain) this.domainContext.setSelectedDomain(domain);
  }

  /**
   * Extract key content elements from structured content
   */
  extractContentElements(): void {
    const structuredContent = this.getStructuredContent();
    if (!structuredContent) return;

    this.contentElements = [];
    
    // Process main_topic content
    if (structuredContent.main_topic && Array.isArray(structuredContent.main_topic)) {
      structuredContent.main_topic.forEach((topic: any) => {
        // Add the main heading
        if (topic.h_title) {
          this.contentElements.push({
            type: 'heading',
            level: topic.level || 1,
            content: topic.h_title
          });
        }
        
        // Add primary content
        if (topic.primary_content && Array.isArray(topic.primary_content)) {
          topic.primary_content.forEach((content: any) => {
            if (content.text && content.text.trim()) {
              this.contentElements.push({
                type: 'text',
                content: content.text.trim()
              });
            }
            
            // Add links from the content
            if (content.urls && Array.isArray(content.urls)) {
              content.urls.forEach((url: any) => {
                if (url.anchor_text && url.url) {
                  this.contentElements.push({
                    type: 'link',
                    content: url.anchor_text,
                    href: url.url
                  });
                }
              });
            }
          });
        }
        

      });
    }
    
    // Process header content
    if (structuredContent.header) {
      // Add primary header content
      if (structuredContent.header.primary_content && Array.isArray(structuredContent.header.primary_content)) {
        structuredContent.header.primary_content.forEach((content: any) => {
          if (content.text && content.text.trim()) {
            this.contentElements.push({
              type: 'heading',
              level: 1,
              content: content.text.trim()
            });
          }
          
          // Add links from header
          if (content.urls && Array.isArray(content.urls)) {
            content.urls.forEach((url: any) => {
              if (url.anchor_text && url.url) {
                this.contentElements.push({
                  type: 'link',
                  content: url.anchor_text,
                  href: url.url
                });
              }
            });
          }
        });
      }
      

    }
    
    // Process footer content
    if (structuredContent.footer) {
      // Add primary footer content only
      if (structuredContent.footer.primary_content && Array.isArray(structuredContent.footer.primary_content)) {
        structuredContent.footer.primary_content.forEach((content: any) => {
          if (content.text && content.text.trim()) {
            this.contentElements.push({
              type: 'text',
              content: content.text.trim()
            });
          }
          
          // Add links from footer
          if (content.urls && Array.isArray(content.urls)) {
            content.urls.forEach((url: any) => {
              if (url.anchor_text && url.url) {
                this.contentElements.push({
                  type: 'link',
                  content: url.anchor_text,
                  href: url.url
                });
              }
            });
          }
        });
      }
    }
    
    // Process table content
    if (structuredContent.table_content && Array.isArray(structuredContent.table_content)) {
      structuredContent.table_content.forEach((table: any) => {
        if (table.body && Array.isArray(table.body)) {
          table.body.forEach((row: any, rowIndex: number) => {
            if (row.row_cells && Array.isArray(row.row_cells)) {
              // Create a table element with the row data
              this.contentElements.push({
                type: 'table',
                content: `Table Row ${rowIndex + 1}`,
                tableData: row.row_cells.map((cell: any) => ({
                  text: cell.text || '',
                  isHeader: cell.is_header || false,
                  urls: cell.urls || null
                }))
              });
            }
          });
        }
      });
    }
  }

  getScoreClass(score: number): string {
    if (score >= 90) return 'excellent';
    if (score >= 70) return 'good';
    if (score >= 50) return 'fair';
    return 'poor';
  }

  /** The same four bands as `getScoreClass()`, as a `.badge` tone. Kept
   *  separate rather than folded in: `getScoreClass()` also colours plain
   *  text (the OnPage score, the chip percentage), and a text colour and a
   *  badge tone are not the same decision even when they agree. */
  scoreBadgeClass(score: number): string {
    if (score >= 70) return 'badge--success';
    if (score >= 50) return 'badge--warning';
    return 'badge--danger';
  }

  technicalOnPageScoreLabel(): string {
    const score = this.analysisData?.onpageScore;
    if (score == null || !Number.isFinite(Number(score))) return '—';
    const n = Number(score);
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  }

  technicalOnPageIssuesLabel(): string | null {
    const { critical, warnings } = buildOnpageTechnicalIssueViews(this.analysisData);
    if (critical.length === 0 && warnings.length === 0) return null;
    const parts: string[] = [];
    if (critical.length > 0) parts.push(`${critical.length} critical`);
    if (warnings.length > 0) parts.push(`${warnings.length} warning${warnings.length === 1 ? '' : 's'}`);
    return parts.join(' · ');
  }

  technicalCriticalIssueViews(): OnpageIssueView[] {
    return buildOnpageTechnicalIssueViews(this.analysisData, this.sharedIssueCauses).critical;
  }

  technicalWarningIssueViews(): OnpageIssueView[] {
    return buildOnpageTechnicalIssueViews(this.analysisData, this.sharedIssueCauses).warnings;
  }

  hasTechnicalCriticalIssues(): boolean {
    return this.technicalCriticalIssueViews().length > 0;
  }

  hasTechnicalWarnings(): boolean {
    return this.technicalWarningIssueViews().length > 0;
  }

  isTechnicalIssueDetailsExpanded(kind: 'critical' | 'warning', index: number): boolean {
    return this.technicalIssueDetailsExpanded.has(`${kind}:${index}`);
  }

  toggleTechnicalIssueDetails(kind: 'critical' | 'warning', index: number): void {
    const key = `${kind}:${index}`;
    if (this.technicalIssueDetailsExpanded.has(key)) {
      this.technicalIssueDetailsExpanded.delete(key);
    } else {
      this.technicalIssueDetailsExpanded.add(key);
    }
  }

  formatTechnicalPerfMs(value: unknown): string {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    return `${Math.round(Number(value))} ms`;
  }

  toggleContentSection(): void {
    this.contentSectionCollapsed = !this.contentSectionCollapsed;
  }

  toggleLinksSection(): void {
    this.linksSectionCollapsed = !this.linksSectionCollapsed;
  }

  /**
   * Extract internal links data from analysis
   */
  extractLinkData(): void {
    if (!this.analysisData?.links?.internalLinks) {
      this.internalLinks = [];
      return;
    }

    this.internalLinks = this.analysisData.links.internalLinks || [];
  }

  /**
   * Get filtered and sorted links
   */
  getFilteredLinks(): any[] {
    let filtered = this.internalLinks;

    // Apply filter
    if (this.linkFilter !== 'all') {
      filtered = filtered.filter(link => link.linkType === this.linkFilter);
    }

    // Apply sort
    filtered = [...filtered].sort((a, b) => {
      switch (this.linkSort) {
        case 'type':
          const typeOrder = { 'structural': 1, 'contextual': 2, 'same_page': 3, 'unknown': 4 };
          return (typeOrder[a.linkType as keyof typeof typeOrder] || 99) - 
                 (typeOrder[b.linkType as keyof typeof typeOrder] || 99);
        case 'confidence':
          return (b.confidence || 0) - (a.confidence || 0);
        case 'anchor':
          return (a.anchorText || '').localeCompare(b.anchorText || '');
        case 'url':
          return (a.targetRelativeUrl || '').localeCompare(b.targetRelativeUrl || '');
        default:
          return 0;
      }
    });

    return filtered;
  }

  /**
   * Get link statistics
   */
  getLinkStats(): { total: number; structural: number; contextual: number; samePage: number; unknown: number } {
    return {
      total: this.internalLinks.length,
      structural: this.internalLinks.filter(l => l.linkType === 'structural').length,
      contextual: this.internalLinks.filter(l => l.linkType === 'contextual').length,
      samePage: this.internalLinks.filter(l => l.linkType === 'same_page').length,
      unknown: this.internalLinks.filter(l => l.linkType === 'unknown' || !l.linkType).length
    };
  }

  /**
   * Get link type badge class
   */
  getLinkTypeClass(linkType: string): string {
    switch (linkType) {
      case 'structural': return 'badge--accent';
      case 'contextual': return 'badge--success';
      case 'same_page': return '';
      default: return 'badge--muted';
    }
  }

  /**
   * Get link type label
   */
  getLinkTypeLabel(linkType: string): string {
    switch (linkType) {
      case 'structural': return 'Structural';
      case 'contextual': return 'Contextual';
      case 'same_page': return 'Same Page';
      default: return 'Unknown';
    }
  }

  /**
   * Get confidence color class
   */
  getConfidenceClass(confidence: number): string {
    if (confidence >= 0.8) return 'confidence-high';
    if (confidence >= 0.6) return 'confidence-medium';
    return 'confidence-low';
  }

  /**
   * Check if links are available
   */
  hasLinks(): boolean {
    return this.internalLinks.length > 0;
  }

  /**
   * Toggle link details expansion
   */
  toggleLinkDetails(link: any): void {
    link._expanded = !link._expanded;
  }

  /**
   * Get the structured content from the analysis data
   */
  getStructuredContent(): any {
    if (!this.analysisData?.rawData?.structuredContent) {
      return null;
    }
    return this.analysisData.rawData.structuredContent;
  }

  // TODO: DataForSEO markdown_view → page_as_markdown (see CONTENT_INVENTORY_TOPIC_COMPARE_UI.md)
  // This would provide clean markdown content instead of structured content
  // Consider implementing this in the future for better content display

  /**
   * Get heading level class for styling
   */
  getHeadingClass(level: number): string {
    return `heading-level-${level}`;
  }

  /**
   * Check if content has any elements
   */
  hasContent(): boolean {
    return this.contentElements.length > 0;
  }

  /**
   * TrackBy function for ngFor performance
   */
  trackByElement(index: number, element: ContentElement): string {
    return `${element.type}-${index}-${element.content.substring(0, 20)}`;
  }

  /**
   * TrackBy function for links
   */
  trackByLink(index: number, link: any): string {
    return `${link.targetRelativeUrl}-${link.anchorText}-${index}`;
  }

  hasTopicSignals(): boolean {
    const raw = this.analysisData?.topic?.topicSignals;
    return Array.isArray(raw) && raw.length > 0;
  }

  /**
   * Topic signals for this page, strongest first (same ordering as useful for scanning).
   */
  private static readonly TOP_PAGE_TOPIC_SIGNALS = 5;

  getPageTopicSignals(): PageTopicSignal[] {
    const raw = this.analysisData?.topic?.topicSignals;
    if (!Array.isArray(raw)) return [];
    const mapped = raw
      .filter((s: any) => s && typeof s.topicSlug === 'string')
      .map((s: any) => ({
        topicSlug: s.topicSlug,
        label: typeof s.label === 'string' && s.label.trim() ? s.label : s.topicSlug,
        weight: Number.isFinite(s.weight) ? s.weight : 0,
        salience: Number.isFinite(s.salience) ? s.salience : 0,
        relativeDensity: Number.isFinite(s.relativeDensity) ? s.relativeDensity : 0
      }));
    return mapped.sort((a, b) => b.weight - a.weight);
  }

  getTopPageTopicSignals(): PageTopicSignal[] {
    return this.getPageTopicSignals().slice(0, UrlDetailComponent.TOP_PAGE_TOPIC_SIGNALS);
  }

  hasMorePageTopicsThanTop(): boolean {
    return this.getPageTopicSignals().length > UrlDetailComponent.TOP_PAGE_TOPIC_SIGNALS;
  }

  togglePageTopicsExpanded(): void {
    this.pageTopicsExpanded = !this.pageTopicsExpanded;
  }

  pageTopicsExpandControlLabel(): string {
    if (this.pageTopicsExpanded) return 'Show top topics only';
    const total = this.getPageTopicSignals().length;
    const rest = total - UrlDetailComponent.TOP_PAGE_TOPIC_SIGNALS;
    return rest > 0 ? `Show more topics (${rest} more)` : 'Show all topics';
  }

  /**
   * Same blend as content inventory / domain topic intent: 0.7·weight + 0.2·salience + 0.1·relativeDensity (clamped 0..1).
   */
  pageTopicRelevance01(signal: PageTopicSignal): number {
    const v =
      0.7 * (signal.weight ?? 0) +
      0.2 * (signal.salience ?? 0) +
      0.1 * (signal.relativeDensity ?? 0);
    return Math.min(1, Math.max(0, v));
  }

  formatTopicMetric(n: number): string {
    return Number.isFinite(n) ? n.toFixed(3) : '—';
  }

  formatPageTopicRelevancePct(signal: PageTopicSignal): string {
    return `${Math.round(this.pageTopicRelevance01(signal) * 100)}%`;
  }

  pageTopicRelevanceScore0to100(signal: PageTopicSignal): number {
    return this.pageTopicRelevance01(signal) * 100;
  }

  trackByTopicSignal(index: number, signal: PageTopicSignal): string {
    return signal.topicSlug + '-' + index;
  }

  /** Placeholder columns/lines for supporting-pages skeleton */
  readonly topicOwnershipSkeletonCols = [0, 1, 2];
  readonly topicOwnershipSkeletonLines = [0, 1, 2, 3, 4];

  loadTopicOwnership(): void {
    const gen = ++this.topicOwnershipLoadGen;
    this.topicOwnershipCompareUrl = '';
    this.resetTopicOwnershipV2Test();
    this.markedSerpCompetitorV2Rows = [];
    this.markedSerpEval.cancel();
    this.supportingPageLexicalByUrl = new Map();
    this.topicOwnershipCacheMiss = false;
    this.topicOwnershipSnapshotGeneratedAt = null;
    const pageUrl = (this.url || '').trim();
    const yourDomain = this.extractDomainFromUrl(pageUrl);
    const yourDomainKey = yourDomain ? yourDomain.toLowerCase().replace(/^www\./, '') : '';
    if (!yourDomainKey || !this.analysisData) {
      this.topicOwnershipStatus = 'ready';
      this.topicOwnershipDomains = [];
      return;
    }

    const pageTopics = getTopTopicsForSupportingPages(this.analysisData, { limit: 3 });
    if (pageTopics.length === 0) {
      this.topicOwnershipStatus = 'ready';
      this.topicOwnershipDomains = [];
      this.focalAnchorTopicSlugs = new Set();
      this.focalAnchorTopicLabels = [];
      return;
    }

    const anchor = resolveFocalAnchorTopics(
      this.analysisData,
      pageUrl
    );
    this.focalAnchorTopicSlugs = anchor.slugs;
    this.focalAnchorTopicLabels = anchor.labels;

    this.topicOwnershipStatus = 'loading';
    this.topicOwnershipDomains = [];
    this.topicOwnershipPagesExpandedByDomain.clear();
    this.topicOwnershipUserExpanded = false;

    this.loadSerpRivalCoverage(pageUrl);

    this.dataForSEOService.getSupportingPagesByTopicSnapshot(pageUrl).subscribe({
      next: (res) => {
        if (gen !== this.topicOwnershipLoadGen) return;
        if (res.cached && res.data) {
          this.applySupportingPagesSnapshot(res.data);
          this.topicOwnershipStatus = 'ready';
          this.loadTopicOwnershipV2FromCache();
          return;
        }
        this.topicOwnershipDomains = [];
        this.topicOwnershipCacheMiss = true;
        this.topicOwnershipStatus = 'ready';
        this.topicOwnershipV2Status = 'ready';
        this.ensureMarkedSerpCompetitorEvals();
      },
      error: () => {
        if (gen !== this.topicOwnershipLoadGen) return;
        this.topicOwnershipStatus = 'error';
        this.topicOwnershipDomains = [];
        this.topicOwnershipCacheMiss = true;
      }
    });
  }

  private applySupportingPagesSnapshot(data: SupportingPagesByTopicSnapshotData): void {
    this.focalAnchorTopicSlugs = new Set(data.focalAnchorTopicSlugs || []);
    this.focalAnchorTopicLabels = data.focalAnchorTopicLabels || [];
    this.topicOwnershipSnapshotGeneratedAt = data.generatedAt || null;
    // FW-ENTITY-01: the API applies this on read too; repeated here so a
    // snapshot served by an older backend is cleaned the same way.
    this.topicOwnershipDomains = filterCompetingEntityRowsFromRivalColumns(
      data.domains || [],
      data.focalUrl || (this.url || '').trim()
    )
      .filter((block) => block.isYourSite || (block.rows?.length ?? 0) > 0)
      .map((block) => ({
      domain: block.domain,
      isYourSite: block.isYourSite,
      rows: block.rows as DomainTopicUrlRow[]
    }));
    this.topicOwnershipCacheMiss = false;
  }

  private loadTopicOwnershipV2FromCache(): void {
    const focalUrl = (this.url || '').trim();
    if (!focalUrl || this.topicOwnershipStatus !== 'ready') return;

    const gen = ++this.topicOwnershipV2LoadGen;
    this.topicOwnershipV2Status = 'loading';
    this.topicOwnershipV2Error = null;
    this.topicOwnershipV2Data = null;

    this.dataForSEOService.getSupportingPagesOpportunitySnapshot(focalUrl).subscribe({
      next: (res) => {
        if (gen !== this.topicOwnershipV2LoadGen) return;
        if (res.cached && res.data) {
          this.topicOwnershipV2Data = res.data;
          this.topicOwnershipV2Status = 'ready';
          this.ensureMarkedSerpCompetitorEvals();
          return;
        }
        this.topicOwnershipV2Status = 'ready';
        this.ensureMarkedSerpCompetitorEvals();
      },
      error: (err) => {
        if (gen !== this.topicOwnershipV2LoadGen) return;
        this.topicOwnershipV2Status = 'error';
        this.topicOwnershipV2Error =
          err?.error?.message || err?.message || 'Supporting pages opportunity snapshot failed';
      }
    });
  }

  /**
   * Load the precomputed "Top SERP rival" section coverage for this focal URL.
   * Independent of the marked-competitor supporting-pages snapshot, so it's
   * available even when no v1 topic snapshot exists (built via the Setup step).
   */
  private loadSerpRivalCoverage(pageUrl: string): void {
    this.serpRivalCoverage = null;
    this.dataForSEOService.getSerpRivalCoverage(pageUrl).subscribe({
      next: (res) => {
        this.serpRivalCoverage = res.cached && res.data ? res.data : null;
      },
      error: () => {
        this.serpRivalCoverage = null;
      }
    });
  }

  /** Top editorial SERP rival for the current snapshot (lowest position that beats you). */
  topEditorialSerpRival(): { url: string; domain: string } | null {
    return findTopSerpRival(this.pageSerp, this.extractDomainFromUrl(this.url || ''));
  }

  /** Precomputed rival coverage when it matches the current SERP query + top rival. */
  activeSerpRivalCoverage(): SerpRivalCoverageData | null {
    const cov = this.serpRivalCoverage;
    if (!cov || cov.status !== 'ready') return null;
    const rival = this.topEditorialSerpRival();
    if (!rival || normalizeUrlForCompare(cov.rivalUrl) !== normalizeUrlForCompare(rival.url)) {
      return null;
    }
    const query = this.pageSerp?.queryDisplay?.trim();
    if (query && cov.query?.trim() && query !== cov.query.trim()) return null;
    return cov;
  }

  /**
   * Whether the Top SERP rival has sections this page is missing.
   *
   * Counted from the eval row, not from the stored `missingSectionCount`. This
   * gates a step in the expand playbook — "go and look at the section gaps" —
   * and the stored number counts groups before the filters, so on a listicle it
   * would send an operator to a panel that lists nothing.
   */
  hasActiveSerpRivalSectionGaps(): boolean {
    return hasSectionGapsForV2Row(this.serpRivalEvalRow());
  }

  /** The Top SERP rival's eval row, when its section coverage is ready. */
  serpRivalEvalRow(): SupportingPagesV2RowResult | null {
    const cov = this.activeSerpRivalCoverage();
    if (!cov?.evalRow) return null;
    return cov.evalRow as SupportingPagesV2RowResult;
  }

  /**
   * V2 rows for the section-compare UI, augmented with the precomputed Top SERP
   * rival row (so "what they cover that your page doesn't" renders it even when
   * the rival's domain is not a marked competitor / has no v1 snapshot).
   */
  private mergedV2Rows(): SupportingPagesV2RowResult[] {
    const base = this.topicOwnershipV2Data?.rows ? [...this.topicOwnershipV2Data.rows] : [];
    const rival = this.serpRivalEvalRow();
    if (rival) {
      const key = normalizeUrlForCompare(rival.competitorUrl);
      if (!base.some((r) => normalizeUrlForCompare(r.competitorUrl) === key)) {
        base.push(rival);
      }
    }
    return base;
  }

  /** The Top SERP rival coverage for a given SERP result URL, if it matches. */
  /**
   * Why the section comparison is blank, when it is blank for a reason (§0at).
   *
   * Review surface only. Inventory and URL detail show the whole compare
   * apparatus, including a Fetch control for exactly this state — there, a
   * sentence saying we could not read the rival sits above the thing that
   * fixes it. On the belt there is no such control and no such context, so a
   * card that simply does not appear reads as "we found nothing wrong", which
   * on the largest-gap page of a domain is the opposite of the truth.
   */
  reviewSectionCompareNotice(): SectionCompareNotice | null {
    if (!this.embedReviewSurface) return null;
    const cov = this.serpRivalCoverage;
    if (!cov) return null;
    // A comparison we DID manage to show is never explained away.
    if (this.buildSectionGapIssuePayload()) return null;
    return sectionCompareNotice(cov.status, cov.rivalDomain);
  }

  serpRivalCoverageForUrl(url: string): SerpRivalCoverageData | null {
    const cov = this.activeSerpRivalCoverage();
    if (!cov) return null;
    return normalizeUrlForCompare(cov.rivalUrl) === normalizeUrlForCompare(url) ? cov : null;
  }

  isTopSerpRival(url: string): boolean {
    const rival = this.topEditorialSerpRival();
    if (!rival) return false;
    return normalizeUrlForCompare(rival.url) === normalizeUrlForCompare(url);
  }

  /** Chip label for a rival row with section gaps (Top SERP snapshot or marked-competitor v2 eval). */
  serpRivalMissingSectionsLabel(url: string): string | null {
    const n = this.serpRivalMissingSectionCount(url);
    if (n == null || n <= 0) return null;
    return n === 1 ? '1 missing section' : `${n} missing sections`;
  }

  serpRivalRankDrivenGapsLabel(url: string): string | null {
    if (!this.isTopSerpRival(url)) return null;
    const count = this.topSerpRivalRankDrivenGaps().length;
    if (count <= 0) return null;
    return count === 1 ? '1 rank-driven gap' : `${count} rank-driven gaps`;
  }

  toggleTopSerpRivalRankDrivenPanel(): void {
    this.topSerpRivalRankDrivenOpen = !this.topSerpRivalRankDrivenOpen;
  }

  /**
   * Sections this page is missing versus one rival URL.
   *
   * Both branches count the eval row rather than reading the stored
   * `missingSectionCount`. This number is the amber "N missing sections — see
   * what they cover" button, and it sat on the same screen as the Missing
   * sections card, which has always counted the filtered set — so on the ~7% of
   * pages where the two disagree the page showed two different numbers for one
   * finding, and the button promised a list longer than the one behind it.
   */
  private serpRivalMissingSectionCount(url: string): number | null {
    const cov = this.serpRivalCoverageForUrl(url);
    if (cov?.status === 'ready') return sectionGapsForV2Row(cov.evalRow).length;

    const v2 = this.v2RowForCompetitorUrl(url);
    if (v2 && this.topicOwnershipV2HasSectionCoverage(v2)) {
      return sectionGapsForV2Row(v2).length;
    }
    return null;
  }

  private v2RowForCompetitorUrl(url: string): SupportingPagesV2RowResult | null {
    const key = normalizeUrlForCompare(url);
    if (!key) return null;
    return (
      this.allMarkedV2Rows().find((r) => normalizeUrlForCompare(r.competitorUrl) === key) ?? null
    );
  }

  /** Open section-compare for Top SERP rival snapshot or marked-competitor v2 eval. */
  openSerpRivalSectionCompare(url: string): void {
    const trimmed = (url || '').trim();
    if (!trimmed) return;

    if (this.serpRivalCoverageForUrl(trimmed)?.status === 'ready') {
      this.toggleTopicOwnershipV2Detail(trimmed);
    } else if (this.v2RowForCompetitorUrl(trimmed)) {
      this.toggleTopicOwnershipV2Detail(trimmed);
    } else {
      return;
    }

    setTimeout(() => {
      document
        .querySelector('.url-topic-ownership')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  }

  devRebuildSupportingPagesByTopic(): void {
    const pageUrl = (this.url || '').trim();
    if (!this.isDevView() || !pageUrl || this.supportingPagesV1RebuildRunning) return;

    this.supportingPagesV1RebuildRunning = true;
    this.supportingPagesRebuildStatus = 'Building Supporting pages by topic…';
    this.resetTopicOwnershipV2Test();

    this.dataForSEOService.rebuildSupportingPagesByTopicSnapshot(pageUrl).subscribe({
      next: (res) => {
        this.supportingPagesV1RebuildRunning = false;
        this.supportingPagesRebuildStatus = res.message || 'Supporting pages snapshot rebuilt';
        if (res.data) {
          this.applySupportingPagesSnapshot(res.data);
          this.topicOwnershipStatus = 'ready';
          this.loadTopicOwnershipV2FromCache();
        } else {
          this.topicOwnershipDomains = [];
          this.topicOwnershipCacheMiss = true;
          this.topicOwnershipStatus = 'ready';
          this.topicOwnershipV2Status = 'ready';
        }
      },
      error: (err) => {
        this.supportingPagesV1RebuildRunning = false;
        this.supportingPagesRebuildStatus =
          err?.error?.message || err?.message || 'Supporting pages rebuild failed';
      }
    });
  }

  devRebuildSupportingPagesOpportunities(): void {
    const pageUrl = (this.url || '').trim();
    if (!this.isDevView() || !pageUrl || this.supportingPagesOppRebuildRunning) return;

    this.supportingPagesOppRebuildRunning = true;
    this.supportingPagesRebuildStatus = 'Building Supporting page opportunities…';

    this.dataForSEOService
      .rebuildSupportingPagesOpportunitySnapshot(pageUrl, { skipV1: true })
      .subscribe({
        next: (res) => {
          this.supportingPagesOppRebuildRunning = false;
          this.supportingPagesRebuildStatus = res.message || 'Opportunity snapshot rebuilt';
          if (res.data?.opportunities) {
            this.topicOwnershipV2Data = res.data.opportunities;
            this.topicOwnershipV2Status = 'ready';
            this.topicOwnershipV2Error = null;
          } else {
            this.topicOwnershipV2Data = null;
            this.topicOwnershipV2Status = 'ready';
          }
        },
        error: (err) => {
          this.supportingPagesOppRebuildRunning = false;
          this.supportingPagesRebuildStatus =
            err?.error?.message || err?.message || 'Opportunity rebuild failed';
          this.topicOwnershipV2Status = 'error';
          this.topicOwnershipV2Error =
            err?.error?.message || err?.message || 'Opportunity rebuild failed';
        }
      });
  }

  private resetTopicOwnershipV2Test(): void {
    this.topicOwnershipV2LoadGen++;
    this.topicOwnershipV2Status = 'idle';
    this.topicOwnershipV2Data = null;
    this.topicOwnershipV2Error = null;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2CoveredElsewhereUrl = '';
    this.topicOwnershipV2ListInventoryUrl = '';
    this.topicOwnershipV2RankDrivenUrl = '';
    this.topicOwnershipV2TextUrl = '';
    this.topicOwnershipV2CalUrl = '';
    this.topicOwnershipV2SectionCompareCollapsedKeys.clear();
  }

  private findTopicOwnershipRow(pageUrl: string): DomainTopicUrlRow | null {
    const key = normalizeUrlForCompare(pageUrl);
    for (const block of this.topicOwnershipDomains) {
      for (const row of block.rows) {
        if (normalizeUrlForCompare(row.url) === key) return row;
      }
    }
    return null;
  }

  private loadTopicOwnershipV2Test(): void {
    this.loadTopicOwnershipV2FromCache();
  }

  retryTopicOwnershipV2Test(): void {
    if (this.isDevView()) {
      this.devRebuildSupportingPagesOpportunities();
      return;
    }
    this.loadTopicOwnershipV2FromCache();
  }

  toggleTopicOwnershipV2Detail(competitorUrl: string): void {
    if (this.topicOwnershipV2DetailUrl === competitorUrl) {
      this.topicOwnershipV2DetailUrl = '';
      return;
    }
    this.topicOwnershipV2DetailUrl = competitorUrl;
    this.topicOwnershipV2CoveredElsewhereUrl = '';
    this.topicOwnershipV2ListInventoryUrl = '';
    this.topicOwnershipV2RankDrivenUrl = '';
    this.topicOwnershipV2TextUrl = '';
    this.topicOwnershipV2CalUrl = '';
  }

  toggleTopicOwnershipV2Text(competitorUrl: string): void {
    if (this.topicOwnershipV2TextUrl === competitorUrl) {
      this.topicOwnershipV2TextUrl = '';
      return;
    }
    this.topicOwnershipV2TextUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2CalUrl = '';
  }

  toggleTopicOwnershipV2Cal(competitorUrl: string): void {
    if (this.topicOwnershipV2CalUrl === competitorUrl) {
      this.topicOwnershipV2CalUrl = '';
      return;
    }
    this.topicOwnershipV2CalUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2TextUrl = '';
  }

  topicOwnershipV2NovelClass(level: string): string {
    if (level === 'strong') return 'url-topic-ownership-v2-pill--strong';
    if (level === 'depth') return 'url-topic-ownership-v2-pill--depth';
    if (level === 'aligned') return 'url-topic-ownership-v2-pill--aligned';
    return 'url-topic-ownership-v2-pill--unknown';
  }

  topicOwnershipV2ActionClass(action: string): string {
    if (action === 'expand_focal') return 'url-topic-ownership-v2-action--expand';
    if (action === 'new_supporting_page') return 'url-topic-ownership-v2-action--new';
    if (action === 'covered_elsewhere') return 'url-topic-ownership-v2-action--covered';
    return 'url-topic-ownership-v2-action--muted';
  }

  topicOwnershipV2RowHighlight(row: { opportunityPriority: number; novelVsFocal: string }): boolean {
    return row.opportunityPriority >= 50 || row.novelVsFocal === 'strong';
  }

  topicOwnershipV2RowPath(pageUrl: string): string {
    return this.topicOwnershipPagePath(pageUrl);
  }

  topicOwnershipV2RowDomain(pageUrl: string): string {
    try {
      return new URL(pageUrl).hostname.replace(/^www\./i, '');
    } catch {
      return pageUrl;
    }
  }

  topicOwnershipV2RowDomainPath(pageUrl: string): string {
    return `${this.topicOwnershipV2RowDomain(pageUrl)}${this.topicOwnershipV2RowPath(pageUrl)}`;
  }

  /** Position in the focal page's target-query SERP snapshot (1–10), or null if URL absent from top 10. */
  topicOwnershipV2SerpPositionForUrl(pageUrl: string): number | null {
    if (!this.pageSerp?.results?.length) return null;
    const targetKey = normalizeUrlForCompare(pageUrl);
    if (!targetKey) return null;
    for (const row of this.pageSerp.results) {
      if (normalizeUrlForCompare(row.url) === targetKey) {
        return row.position;
      }
    }
    return null;
  }

  topicOwnershipV2ShowSerpRankChip(pageUrl: string): boolean {
    return this.hasPageSerpSnapshot();
  }

  topicOwnershipV2SerpRankChipLabel(pageUrl: string): string {
    const pos = this.topicOwnershipV2SerpPositionForUrl(pageUrl);
    if (pos != null && pos >= 1) return `Ranks #${pos}`;
    return 'Not in top 10';
  }

  topicOwnershipV2SerpRankChipTitle(pageUrl: string): string {
    const query = this.pageSerpTargetQueryLabel();
    const pos = this.topicOwnershipV2SerpPositionForUrl(pageUrl);
    const page = this.topicOwnershipV2RowDomainPath(pageUrl);
    if (pos != null) {
      return `${page} ranks #${pos} for «${query}» in this URL's top-10 SERP snapshot.`;
    }
    return `${page} is not in the top 10 for «${query}» in this URL's SERP snapshot.`;
  }

  topicOwnershipV2SerpRankChipClass(pageUrl: string): string {
    const pos = this.topicOwnershipV2SerpPositionForUrl(pageUrl);
    if (pos == null) return 'url-topic-ownership-v2-serp-chip--absent';
    if (pos <= 3) return 'url-topic-ownership-v2-serp-chip--strong';
    return 'url-topic-ownership-v2-serp-chip--ranked';
  }

  topicOwnershipV2HasSectionCoverage(row: SupportingPagesV2RowResult): boolean {
    return this.topicOwnershipV2SectionCoverageRows(row).length > 0;
  }

  topicOwnershipV2SectionCoverageRows(row: SupportingPagesV2RowResult): SectionCoverageRow[] {
    const rows = row.sectionCoverage?.length ? [...row.sectionCoverage] : [];
    return rows.sort((a, b) => (a.sectionNumber ?? 0) - (b.sectionNumber ?? 0));
  }

  topicOwnershipV2SectionCoverageGroups(row: SupportingPagesV2RowResult): SectionCoverageRow[] {
    let groups = row.sectionCoverageGroups?.length ? [...row.sectionCoverageGroups] : [];
    if (groups.length) {
      if (!this.isDevView()) {
        groups = groups.filter((sc) => !sc.listicleSuppressed);
      }
      return groups.sort((a, b) => (a.sectionNumber ?? 0) - (b.sectionNumber ?? 0));
    }
    return this.topicOwnershipV2SectionCoverageRows(row).filter(
      (sc) => this.topicOwnershipV2SectionNotOnPage(sc)
    );
  }

  topicOwnershipV2CompareFilterMode(): 'not_on_page' | 'gaps' | 'all' {
    return this.topicOwnershipV2CompareFilter;
  }

  setTopicOwnershipV2CompareFilter(mode: 'not_on_page' | 'gaps' | 'all'): void {
    this.topicOwnershipV2CompareFilter = mode;
  }

  /** Section missing entirely on focal page. */
  topicOwnershipV2SectionNotOnPage(sc: SectionCoverageRow): boolean {
    return !sc.sectionCovered;
  }

  /** A section is a gap when its topic is uncovered or it has absent phrases. */
  topicOwnershipV2SectionIsGap(sc: SectionCoverageRow): boolean {
    return !sc.sectionCovered || (sc.absentTerms?.length ?? 0) > 0;
  }

  topicOwnershipV2FilteredSectionCoverageRows(
    row: SupportingPagesV2RowResult
  ): SectionCoverageRow[] {
    const mode = this.topicOwnershipV2CompareFilterMode();
    if (!this.isDevView() && mode === 'not_on_page') {
      return this.topicOwnershipV2SectionCoverageGroups(row);
    }
    const rows = this.topicOwnershipV2SectionCoverageRows(row);
    if (mode === 'all') return rows;
    if (mode === 'not_on_page') return rows.filter((sc) => this.topicOwnershipV2SectionNotOnPage(sc));
    return rows.filter((sc) => this.topicOwnershipV2SectionIsGap(sc));
  }

  topicOwnershipV2CompareFilterEmptyMessage(): string {
    const mode = this.topicOwnershipV2CompareFilterMode();
    if (mode === 'not_on_page') {
      return 'Every competitor section is covered somewhere on your page — switch filter to review gaps or all sections.';
    }
    if (mode === 'gaps') {
      return 'No section gaps — switch to All sections to review full coverage.';
    }
    return '';
  }

  topicOwnershipV2SectionPositionChip(sc: SectionCoverageRow): string {
    const num = sc.sectionNumber;
    const end = sc.sectionNumberEnd;
    const total = sc.sectionTotal;
    const place = (sc.pagePosition || '').trim();
    if (num != null && total != null && total > 0) {
      const ordinal =
        end != null && end > num ? `${num}–${end}/${total}` : `${num}/${total}`;
      return place ? `${ordinal} · ${place}` : ordinal;
    }
    return place || '';
  }

  topicOwnershipV2ListicleSectionStatusLabel(sc: SectionCoverageRow): string {
    const tool = (sc.listicleToolLabel || 'tool').trim();
    if (sc.listicleSectionRole === 'tool_root') {
      return sc.listicleToolOnFocal ? `Listicle tool — ${tool}` : 'Listicle tool — not on your list';
    }
    if (sc.listicleSectionRole === 'tool_feature') {
      return sc.listicleToolOnFocal
        ? `Listicle feature — ${tool}`
        : `Listicle feature — ${tool} (use list inventory)`;
    }
    return 'Listicle section';
  }

  topicOwnershipV2ListicleSectionStatusTitle(sc: SectionCoverageRow): string {
    const tool = (sc.listicleToolLabel || 'tool').trim();
    if (sc.listicleSectionRole === 'tool_root' && !sc.listicleToolOnFocal) {
      return `Section is a numbered tool slot (${tool}) missing from your roundup — use list inventory, not section compare.`;
    }
    if (sc.listicleSectionRole === 'tool_feature' && !sc.listicleToolOnFocal) {
      return `Feature under ${tool}, which is not on your list — use list inventory for the tool gap.`;
    }
    if (sc.listicleSectionRole === 'tool_feature' && sc.listicleToolOnFocal) {
      return `Feature subsection for ${tool} — both pages list this tool; section compare is valid here.`;
    }
    if (sc.listicleSectionRole === 'tool_root' && sc.listicleToolOnFocal) {
      return `Numbered tool slot for ${tool} — on both roundups.`;
    }
    return 'Listicle-structured section.';
  }

  topicOwnershipV2SectionStatusTitle(sc: SectionCoverageRow): string | null {
    if (sc.isNonTopical) return this.topicOwnershipV2NonTopicalStatusTitle(sc);
    if (this.isDevView() && sc.listicleSectionRole) {
      return this.topicOwnershipV2ListicleSectionStatusTitle(sc);
    }
    if (sc.listicleSuppressed) {
      return 'Gap group suppressed in user view — tool not on your roundup (see list inventory).';
    }
    return null;
  }

  topicOwnershipV2SectionStatusLabel(sc: SectionCoverageRow): string {
    if (sc.isNonTopical) return this.topicOwnershipV2NonTopicalStatusLabel(sc);
    if (this.isDevView() && sc.listicleSectionRole) {
      return this.topicOwnershipV2ListicleSectionStatusLabel(sc);
    }
    if (sc.listicleSuppressed && this.isDevView()) return 'Listicle gap (user hidden)';
    if (!sc.sectionCovered) return 'Not on your page';
    if ((sc.absentTerms?.length ?? 0) > 0) return 'Covered — missing phrases';
    return 'Covered';
  }

  topicOwnershipV2NonTopicalStatusLabel(sc: SectionCoverageRow): string {
    if (sc.nonTopicalReason === 'author_bio') return 'Author bio (ignored)';
    if (sc.nonTopicalReason === 'newsletter_signup') return 'Newsletter signup (ignored)';
    if (sc.nonTopicalReason === 'product_cta') return 'Signup pitch (ignored)';
    if (sc.nonTopicalReason === 'generic_heading') return 'Non-article section (ignored)';
    if (sc.nonTopicalReason === 'rival_brand_heading') return 'Names their own brand (ignored)';
    if (sc.nonTopicalReason === 'rival_no_outline') return 'Rival has no outline (ignored)';
    return 'Non-article section (ignored)';
  }

  topicOwnershipV2NonTopicalStatusTitle(sc: SectionCoverageRow): string {
    if (sc.nonTopicalReason === 'author_bio') {
      return 'Detected as an author/byline block — not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'newsletter_signup') {
      return 'Detected as a newsletter / email signup CTA — not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'product_cta') {
      return 'Detected as the rival pitching their own product (free trial, demo) — not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'generic_heading') {
      return 'Structural section (intro, conclusion, FAQ, etc.) — not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'rival_brand_heading') {
      return 'The heading names the rival\u2019s own product, not the subject \u2014 not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'rival_no_outline') {
      return 'This rival page has no headings at all, so it cannot be compared section by section \u2014 not scored as a content gap.';
    }
    return 'Excluded from gap scoring — not part of the article body comparison.';
  }

  topicOwnershipV2SectionStatusClass(sc: SectionCoverageRow): string {
    if (sc.isNonTopical) return 'url-topic-ownership-v2-section-status--non-topical';
    if (
      this.isDevView() &&
      sc.listicleSectionRole &&
      !sc.listicleToolOnFocal
    ) {
      return 'url-topic-ownership-v2-section-status--listicle';
    }
    if (sc.listicleSuppressed && this.isDevView()) {
      return 'url-topic-ownership-v2-section-status--listicle';
    }
    if (!sc.sectionCovered) return 'url-topic-ownership-v2-section-status--missing';
    if ((sc.absentTerms?.length ?? 0) > 0) return 'url-topic-ownership-v2-section-status--shallow';
    return 'url-topic-ownership-v2-section-status--aligned';
  }

  topicOwnershipV2SectionBodyText(sc: SectionCoverageRow): string {
    return (sc.competitorText || '').trim();
  }

  topicOwnershipV2SectionHeadingPath(sc: SectionCoverageRow): string {
    return (sc.competitorHeadingPath || sc.competitorHeading || '').trim();
  }

  private headingPathSegmentsFromLegacyPath(
    path: string,
    leafLevel?: number | null
  ): HeadingPathSegment[] {
    const parts = path
      .split('>')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return [];
    return parts.map((heading, index) => ({
      heading,
      level: index === parts.length - 1 ? Math.round(Number(leafLevel) || 0) : 0
    }));
  }

  private mergeHeadingPathSegments(
    path: string,
    apiSegments: HeadingPathSegment[] | undefined | null,
    leafLevel?: number | null
  ): HeadingPathSegment[] {
    const parts = path
      .split('>')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) {
      return apiSegments?.length ? apiSegments : [];
    }

    const api = apiSegments || [];
    if (api.length === parts.length) {
      return parts.map((heading, index) => ({
        heading,
        level:
          api[index]?.level ??
          (index === parts.length - 1 ? Math.round(Number(leafLevel) || 0) : 0)
      }));
    }

    if (parts.length > api.length) {
      const leaf =
        api[api.length - 1]?.level ??
        Math.round(Number(leafLevel) || 0) ??
        0;
      return parts.map((heading, index) => {
        const matched = api.find((seg) => seg.heading === heading);
        if (matched) return matched;
        const depthFromLeaf = parts.length - 1 - index;
        const inferred = leaf > 0 ? Math.max(2, leaf - depthFromLeaf) : 0;
        return { heading, level: inferred };
      });
    }

    if (api.length) return api;
    return this.headingPathSegmentsFromLegacyPath(path, leafLevel);
  }

  topicOwnershipV2SectionHeadingSegments(sc: SectionCoverageRow): HeadingPathSegment[] {
    return this.mergeHeadingPathSegments(
      this.topicOwnershipV2SectionHeadingPath(sc),
      sc.competitorHeadingPathSegments,
      sc.competitorHeadingLevel
    );
  }

  private topicOwnershipV2HeadingSegmentKey(seg: HeadingPathSegment): string {
    return `${seg.level}::${(seg.heading || '').trim()}`;
  }

  topicOwnershipV2SectionDisplayHeadingSegments(
    sc: SectionCoverageRow
  ): Array<HeadingPathSegment & { displayRole?: string; treeDepth?: number }> {
    const memberPaths = (sc.gapMemberPaths || [])
      .map((member) => member.headingPathSegments || [])
      .filter((path) => path.length > 0);
    if (sc.isGapGroup && memberPaths.length) {
      const gapLeafKeys = new Set(
        memberPaths.map((path) => this.topicOwnershipV2HeadingSegmentKey(path[path.length - 1]))
      );
      const leafLevel = memberPaths[0][memberPaths[0].length - 1]?.level;
      const sameLeafLevel = memberPaths.every(
        (path) => path[path.length - 1]?.level === leafLevel
      );
      const parentKey = memberPaths[0]
        .slice(0, -1)
        .map((seg) => seg.heading)
        .join(' > ');
      const sameParent = memberPaths.every(
        (path) => path.slice(0, -1).map((seg) => seg.heading).join(' > ') === parentKey
      );

      if (sameLeafLevel && sameParent && memberPaths.length > 1) {
        const ancestors = memberPaths[0].slice(0, -1).map((seg, index) => ({
          ...seg,
          displayRole: 'ancestor',
          treeDepth: index
        }));
        const seen = new Set<string>();
        const leaves = memberPaths
          .map((path) => path[path.length - 1])
          .filter((leaf) => {
            const key = this.topicOwnershipV2HeadingSegmentKey(leaf);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .map((leaf) => ({
            ...leaf,
            displayRole: 'gap',
            treeDepth: ancestors.length
          }));
        return [...ancestors, ...leaves];
      }

      const spine = memberPaths.reduce(
        (longest, path) => (path.length > longest.length ? path : longest),
        memberPaths[0]
      );
      const rows: Array<HeadingPathSegment & { displayRole?: string; treeDepth?: number }> = [];
      const seenGap = new Set<string>();
      spine.forEach((seg, depth) => {
        const key = this.topicOwnershipV2HeadingSegmentKey(seg);
        rows.push({
          ...seg,
          displayRole: gapLeafKeys.has(key) ? 'gap' : 'ancestor',
          treeDepth: depth
        });
        if (gapLeafKeys.has(key)) seenGap.add(key);
      });
      for (const path of memberPaths) {
        const leaf = path[path.length - 1];
        const key = this.topicOwnershipV2HeadingSegmentKey(leaf);
        if (!seenGap.has(key)) {
          rows.push({
            ...leaf,
            displayRole: 'gap',
            treeDepth: path.length - 1
          });
          seenGap.add(key);
        }
      }
      return rows;
    }

    const segments = this.topicOwnershipV2SectionHeadingSegments(sc);
    if (!sc.sectionCovered && !sc.isNonTopical && segments.length) {
      return segments.map((seg, index) => ({
        ...seg,
        displayRole: index === segments.length - 1 ? 'gap' : 'ancestor',
        treeDepth: index
      }));
    }
    return segments.map((seg, index) => ({ ...seg, displayRole: 'default', treeDepth: index }));
  }

  topicOwnershipV2SectionDisplayHeadingRole(seg: {
    displayRole?: string;
  }): string {
    const role = (seg.displayRole || 'default').trim();
    if (role === 'gap') return 'url-topic-ownership-v2-heading-path-segment--gap';
    if (role === 'covered-sibling') {
      return 'url-topic-ownership-v2-heading-path-segment--covered-sibling';
    }
    return '';
  }

  topicOwnershipV2ClosestHeadingSegments(sc: SectionCoverageRow): HeadingPathSegment[] {
    if (!this.topicOwnershipV2HasClosestMatch(sc)) return [];
    return this.mergeHeadingPathSegments(
      this.topicOwnershipV2ClosestHeadingPath(sc),
      sc.closestMyHeadingPathSegments,
      sc.closestMyHeadingLevel
    );
  }

  topicOwnershipV2HeadingLevelChip(level?: number | null): string | null {
    const n = Math.round(Number(level) || 0);
    if (n >= 1 && n <= 6) return `H${n}`;
    return null;
  }

  topicOwnershipV2SectionHeadingLevelChip(sc: SectionCoverageRow): string | null {
    return this.topicOwnershipV2HeadingLevelChip(sc.competitorHeadingLevel);
  }

  topicOwnershipV2ClosestHeadingLevelChip(sc: SectionCoverageRow): string | null {
    return this.topicOwnershipV2HeadingLevelChip(sc.closestMyHeadingLevel);
  }

  topicOwnershipV2FocalDomain(): string {
    return this.topicOwnershipV2RowDomain(this.url || '');
  }

  topicOwnershipV2ClosestHeadingPath(sc: SectionCoverageRow): string {
    return (sc.closestMyHeadingPath || sc.closestMyHeading || '').trim() || '(Untitled)';
  }

  topicOwnershipV2SectionHighlightSegments(
    sc: SectionCoverageRow
  ): SectionCompareHighlightSegment[] {
    return splitSectionTextByAbsentTerms(
      this.topicOwnershipV2SectionBodyText(sc),
      sc.absentTerms || []
    );
  }

  topicOwnershipV2HasClosestMatch(sc: SectionCoverageRow): boolean {
    return !!(sc.closestMyHeading?.trim() || sc.closestMyText?.trim());
  }

  topicOwnershipV2SectionCompareAvailable(sc: SectionCoverageRow): boolean {
    if (sc.isNonTopical) return false;
    return (
      !!this.topicOwnershipV2SectionBodyText(sc) ||
      !!this.topicOwnershipV2ClosestMyBodyText(sc) ||
      !!sc.closestMyHeading?.trim()
    );
  }

  topicOwnershipV2ClosestMatchPct(sc: SectionCoverageRow): number {
    return Math.round((sc.sectionSimilarity || 0) * 100);
  }

  topicOwnershipV2ClosestMatchTitle(sc: SectionCoverageRow): string {
    const pct = this.topicOwnershipV2ClosestMatchPct(sc);
    return (
      `Embedding similarity ${pct}% between this competitor section and the closest block on your focal page. ` +
      `Sections need ≥55% to count as covered. Higher scores mean a closer match, not guaranteed topical coverage — read the text to verify.`
    );
  }

  topicOwnershipV2ClosestMyBodyText(sc: SectionCoverageRow): string {
    return (sc.closestMyText || '').trim();
  }

  topicOwnershipV2CompareClosestLabel(sc: SectionCoverageRow): string | null {
    if (!this.topicOwnershipV2HasClosestMatch(sc)) return null;
    if (sc.sectionCovered) return null;
    return `Closest on your page (${this.topicOwnershipV2ClosestMatchPct(sc)}% similar)`;
  }

  topicOwnershipV2CompareRightColumnTitle(sc: SectionCoverageRow): string {
    if (!this.topicOwnershipV2HasClosestMatch(sc)) {
      return 'No matching section';
    }
    return this.topicOwnershipV2ClosestHeadingPath(sc);
  }

  topicOwnershipV2CompareRightColumnSegments(sc: SectionCoverageRow): HeadingPathSegment[] {
    if (!this.topicOwnershipV2HasClosestMatch(sc)) {
      return [{ level: 0, heading: 'No matching section' }];
    }
    return this.topicOwnershipV2ClosestHeadingSegments(sc);
  }

  topicOwnershipV2SectionFullTextKey(competitorUrl: string, sc: SectionCoverageRow): string {
    if (sc.isGapGroup) {
      const end = sc.sectionNumberEnd ?? sc.sectionNumber ?? 0;
      return `${competitorUrl}::group::${sc.sectionNumber ?? 0}::${end}`;
    }
    return `${competitorUrl}::${sc.sectionNumber ?? 0}`;
  }

  isTopicOwnershipV2SectionFullTextExpanded(competitorUrl: string, sc: SectionCoverageRow): boolean {
    if (!this.topicOwnershipV2SectionCompareAvailable(sc)) return false;
    return !this.topicOwnershipV2SectionCompareCollapsedKeys.has(
      this.topicOwnershipV2SectionFullTextKey(competitorUrl, sc)
    );
  }

  toggleTopicOwnershipV2SectionFullText(competitorUrl: string, sc: SectionCoverageRow): void {
    const key = this.topicOwnershipV2SectionFullTextKey(competitorUrl, sc);
    if (this.topicOwnershipV2SectionCompareCollapsedKeys.has(key)) {
      this.topicOwnershipV2SectionCompareCollapsedKeys.delete(key);
    } else {
      this.topicOwnershipV2SectionCompareCollapsedKeys.add(key);
    }
  }

  topicOwnershipPagePath(pageUrl: string): string {
    return formatSupportingPagePath(pageUrl);
  }

  topicOwnershipPageTraffic(traffic: number | undefined): string {
    return formatSupportingPageTraffic(traffic);
  }

  getTopicOwnershipVisibleRows(block: TopicOwnershipDomain): DomainTopicUrlRow[] {
    const rows = this.getTopicOwnershipRowsForDisplay(block);
    if (this.useTopicOwnershipUserLayout()) {
      if (!block.isYourSite) return rows;
      if (this.topicOwnershipUserExpanded) return rows;
      return rows.slice(0, SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT);
    }
    if (this.isTopicOwnershipPagesExpanded(block.domain)) {
      return rows;
    }
    return rows.slice(0, SUPPORTING_PAGE_INITIAL_VISIBLE_COUNT);
  }

  /** Rows tagged with focal anchor topics (hides weak secondary-topic pages from diagnostics). */
  private getTopicOwnershipRowsForDisplay(block: TopicOwnershipDomain): DomainTopicUrlRow[] {
    if (!this.focalAnchorTopicSlugs.size) {
      return block.rows;
    }
    return filterRowsByFocalAnchorTopics(block.rows, this.focalAnchorTopicSlugs);
  }

  getYourSiteMatchPoolRows(): DomainTopicUrlRow[] {
    const yours = this.topicOwnershipDomains.find((d) => d.isYourSite);
    if (!yours?.rows.length) return [];
    if (!this.focalAnchorTopicSlugs.size) return yours.rows;
    return filterRowsByFocalAnchorTopics(yours.rows, this.focalAnchorTopicSlugs);
  }

  topicOwnershipAnchorHint(): string {
    if (!this.focalAnchorTopicLabels.length) return '';
    return this.focalAnchorTopicLabels.join(', ');
  }

  isTopicOwnershipRowOffAnchor(block: TopicOwnershipDomain, row: DomainTopicUrlRow): boolean {
    return (
      this.isDevView() &&
      block.isYourSite &&
      this.focalAnchorTopicSlugs.size > 0 &&
      !rowOverlapsFocalAnchorTopics(row, this.focalAnchorTopicSlugs)
    );
  }

  hasMoreTopicOwnershipPagesThanVisible(block: TopicOwnershipDomain): boolean {
    if (this.useTopicOwnershipUserLayout()) return false;
    return this.getTopicOwnershipRowsForDisplay(block).length > SUPPORTING_PAGE_INITIAL_VISIBLE_COUNT;
  }

  hasMoreTopicOwnershipPagesOnYourSite(): boolean {
    const yours = this.topicOwnershipDomains.find((d) => d.isYourSite);
    if (!yours) return false;
    return this.getTopicOwnershipRowsForDisplay(yours).length > SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT;
  }

  toggleTopicOwnershipUserExpanded(): void {
    this.topicOwnershipUserExpanded = !this.topicOwnershipUserExpanded;
  }

  topicOwnershipUserExpandLabel(): string {
    if (this.topicOwnershipUserExpanded) return 'Show fewer pages';
    const yours = this.topicOwnershipDomains.find((d) => d.isYourSite);
    const hidden = Math.max(
      0,
      (yours?.rows.length ?? 0) - SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT
    );
    return hidden > 0 ? `Show more pages (${hidden} more)` : 'Show more pages';
  }

  topicOwnershipLinkTitle(block: TopicOwnershipDomain, row: DomainTopicUrlRow): string {
    if (block.isYourSite) return row.url;
    if (row.similarOnYourSite?.url) {
      return `${row.url}\nCovered — similar: ${row.similarOnYourSite.url}`;
    }
    return `${row.url}\nGap — no similar page on your site`;
  }

  isTopicOwnershipPagesExpanded(domain: string): boolean {
    return this.topicOwnershipPagesExpandedByDomain.has(domain.toLowerCase().trim());
  }

  toggleTopicOwnershipPagesExpanded(domain: string): void {
    const key = domain.toLowerCase().trim();
    if (this.topicOwnershipPagesExpandedByDomain.has(key)) {
      this.topicOwnershipPagesExpandedByDomain.delete(key);
    } else {
      this.topicOwnershipPagesExpandedByDomain.add(key);
    }
  }

  topicOwnershipPagesExpandLabel(block: TopicOwnershipDomain): string {
    if (this.isTopicOwnershipPagesExpanded(block.domain)) {
      return 'Show fewer pages';
    }
    const rest = block.rows.length - SUPPORTING_PAGE_INITIAL_VISIBLE_COUNT;
    return rest > 0 ? `Show more pages (${rest} more)` : 'Show more pages';
  }

  isCurrentPageSupportingUrl(pageUrl: string): boolean {
    if (!this.url) return false;
    return normalizeUrlForCompare(pageUrl) === normalizeUrlForCompare(this.url);
  }

  private loadCompetitorTopicCoverage(): void {
    this.competitorCoverageStatus = 'idle';
    this.competitorCoverageMessage = '';
    this.competitorTopicMatchesBySlug = {};
    this.competitorTopicTooltipCache = {};

    const topicSignals = this.getPageTopicSignals();
    if (!topicSignals.length || !this.url) return;

    const activeDomain = this.extractDomainFromUrl(this.url);
    if (!activeDomain) return;

    this.competitorCoverageStatus = 'loading';

    this.compareContextService.getSettings(activeDomain).pipe(
      map((settingsRes) => {
        const domains = settingsRes?.data?.markedCompetitorDomains || [];
        this.markedCompetitorDomains = domains
          .map((d) => d.toLowerCase().trim().replace(/^www\./, ''))
          .filter((d) => !!d);
        this.ensureMarkedSerpCompetitorEvals();
        return domains
          .map((d) => d.toLowerCase().trim())
          .filter((d) => !!d && d !== activeDomain);
      }),
      map((domains) => Array.from(new Set(domains))),
      map((domains) => domains.slice(0, 10)),
      catchError(() => {
        this.competitorCoverageStatus = 'error';
        this.competitorCoverageMessage = 'Could not load marked competitors for this domain.';
        return of([] as string[]);
      })
    ).subscribe((competitorDomains) => {
      if (this.competitorCoverageStatus === 'error') return;

      if (!competitorDomains.length) {
        this.competitorCoverageStatus = 'ready';
        this.competitorCoverageMessage = 'No marked competitors configured for this domain.';
        return;
      }

      const requests = (competitorDomains as string[]).map((domain: string) =>
        this.domainTopicsService.getDomainTopics(domain, { compact: true }).pipe(
          map((res) => ({ domain, topics: (res?.data?.parentTopics || []) as ParentTopic[] })),
          catchError(() => of({ domain, topics: [] as ParentTopic[] }))
        )
      );

      forkJoin(requests).subscribe({
        next: (competitorTopics: { domain: string; topics: ParentTopic[] }[]) => {
          this.competitorTopicMatchesBySlug = this.buildCompetitorMatches(topicSignals, competitorTopics);
          this.competitorCoverageStatus = 'ready';
          this.competitorCoverageMessage = '';
        },
        error: () => {
          this.competitorCoverageStatus = 'error';
          this.competitorCoverageMessage = 'Could not load competitor topic coverage.';
        }
      });
    });
  }

  private buildCompetitorMatches(
    signals: PageTopicSignal[],
    competitorTopics: Array<{ domain: string; topics: ParentTopic[] }>
  ): Record<string, CompetitorTopicMatch[]> {
    const bySlug: Record<string, CompetitorTopicMatch[]> = {};

    for (const signal of signals) {
      const normalizedTopicSlug = signal.topicSlug.toLowerCase().trim();
      const matches: CompetitorTopicMatch[] = [];

      for (const competitor of competitorTopics) {
        const topic = competitor.topics.find((t) => {
          const parentMatch = t.parentTopicSlug?.toLowerCase().trim() === normalizedTopicSlug;
          const childMatch = Array.isArray(t.childTopicSlugs)
            && t.childTopicSlugs.some((child) => child.toLowerCase().trim() === normalizedTopicSlug);
          return parentMatch || childMatch;
        });

        if (!topic) continue;

        matches.push({
          domain: competitor.domain,
          authorityScore: Number.isFinite(topic.authorityScore) ? topic.authorityScore : 0,
          pageCount: Number.isFinite(topic.pageCount) ? topic.pageCount : 0,
          matchedParentTopicSlug: topic.parentTopicSlug
        });
      }

      bySlug[signal.topicSlug] = matches.sort((a, b) => {
        if (b.authorityScore !== a.authorityScore) return b.authorityScore - a.authorityScore;
        if (b.pageCount !== a.pageCount) return b.pageCount - a.pageCount;
        return a.domain.localeCompare(b.domain);
      });
    }

    return bySlug;
  }

  extractDomainFromUrl(url: string): string {
    try {
      const parsed = new URL(url);
      return parsed.hostname.toLowerCase().replace(/^www\./, '');
    } catch {
      return '';
    }
  }

  getCompetitorTopicMatches(signal: PageTopicSignal): CompetitorTopicMatch[] {
    return this.competitorTopicMatchesBySlug[signal.topicSlug] || [];
  }

  hasCompetitorMatches(signal: PageTopicSignal): boolean {
    return this.getCompetitorTopicMatches(signal).length > 0;
  }

  formatCompetitorPill(match: CompetitorTopicMatch): string {
    return `${match.domain} (${Math.round(match.authorityScore)} · ${match.pageCount})`;
  }

  private getCompetitorTopicTooltipKey(match: CompetitorTopicMatch): string {
    return `${match.domain}::${match.matchedParentTopicSlug}`;
  }

  onCompetitorPillHover(match: CompetitorTopicMatch): void {
    this.cancelCompetitorTooltipHide();
    const key = this.getCompetitorTopicTooltipKey(match);
    this.activeCompetitorTooltipKey = key;
    const cached = this.competitorTopicTooltipCache[key];
    if (cached && (cached.status === 'loading' || cached.status === 'ready' || cached.status === 'error')) {
      return;
    }

    this.competitorTopicTooltipCache[key] = { status: 'loading', urls: [] };

    this.domainTopicsService.getTopicDiagnostic(match.domain, match.matchedParentTopicSlug).pipe(
      map((res) => res?.data?.coverage?.directlyCoveredTopics || []),
      map((topics: any[]) => topics.find((t) => t?.parentTopicSlug === match.matchedParentTopicSlug)),
      map((topic: any) => {
        if (!topic) return [];
        const corePages = Array.isArray(topic.corePages) ? topic.corePages : [];
        const mentionPages = Array.isArray(topic.mentionPages) ? topic.mentionPages : [];
        const allPages = [...corePages, ...mentionPages];
        const byUrl = new Map<string, CompetitorTopicUrlPreview>();

        for (const page of allPages) {
          const pageUrl = typeof page?.url === 'string' ? page.url : '';
          if (!pageUrl) continue;
          const score = Number.isFinite(page?.seoIntentScore) ? page.seoIntentScore : 0;
          const salience = Number.isFinite(page?.salience) ? page.salience : undefined;
          const estimatedOrganicTraffic = typeof page?.estimatedOrganicTraffic === 'number'
            ? page.estimatedOrganicTraffic
            : undefined;
          const current = byUrl.get(pageUrl);
          if (!current || score > current.score) {
            byUrl.set(pageUrl, { url: pageUrl, score, salience, estimatedOrganicTraffic });
          }
        }

        return Array.from(byUrl.values())
          .sort((a, b) => b.score - a.score)
          .slice(0, 5);
      }),
      catchError(() => of([] as CompetitorTopicUrlPreview[]))
    ).subscribe((urls) => {
      this.competitorTopicTooltipCache[key] = {
        status: urls.length > 0 ? 'ready' : 'error',
        urls
      };
    });
  }

  onCompetitorPillLeave(): void {
    this.scheduleCompetitorTooltipHide();
  }

  onCompetitorPopoverEnter(match: CompetitorTopicMatch): void {
    this.cancelCompetitorTooltipHide();
    this.activeCompetitorTooltipKey = this.getCompetitorTopicTooltipKey(match);
  }

  onCompetitorPopoverLeave(): void {
    this.scheduleCompetitorTooltipHide();
  }

  private scheduleCompetitorTooltipHide(): void {
    this.cancelCompetitorTooltipHide();
    this.competitorTooltipHideTimer = setTimeout(() => {
      this.activeCompetitorTooltipKey = null;
      this.competitorTooltipHideTimer = null;
    }, 180);
  }

  private cancelCompetitorTooltipHide(): void {
    if (!this.competitorTooltipHideTimer) return;
    clearTimeout(this.competitorTooltipHideTimer);
    this.competitorTooltipHideTimer = null;
  }

  isCompetitorTooltipVisible(match: CompetitorTopicMatch): boolean {
    return this.activeCompetitorTooltipKey === this.getCompetitorTopicTooltipKey(match);
  }

  getCompetitorTooltipState(match: CompetitorTopicMatch): CompetitorTopicTooltipState {
    const key = this.getCompetitorTopicTooltipKey(match);
    return this.competitorTopicTooltipCache[key] || { status: 'idle', urls: [] };
  }

  getCompetitorPillTooltip(match: CompetitorTopicMatch): string {
    const key = this.getCompetitorTopicTooltipKey(match);
    const state = this.competitorTopicTooltipCache[key];
    const summary = `${match.domain}\nAuthority: ${Math.round(match.authorityScore)}\nURLs in topic: ${match.pageCount}`;

    if (!state || state.status === 'idle') {
      return `${summary}\n\nHover to load top URLs...`;
    }
    if (state.status === 'loading') {
      return `${summary}\n\nLoading URLs...`;
    }
    if (state.status === 'error') {
      return `${summary}\n\nNo URL-level preview available.`;
    }

    const lines = state.urls.map((item, i) => `${i + 1}. ${item.url} (${item.score.toFixed(3)})`);
    return `${summary}\n\nTop URLs:\n${lines.join('\n')}`;
  }

  onCompetitorPopoverUrlClick(match: CompetitorTopicMatch, competitorUrl: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.openQuickCompareWithUrl(competitorUrl || '');
  }

  onQuickCompareDrawerClose(): void {
    this.quickCompareOpen = false;
  }

  onOpenFullCompareFromDrawer(): void {
    const primary = (this.quickComparePrimaryUrl || '').trim();
    const compare = (this.quickCompareCompareUrl || '').trim();
    if (!primary || !compare) return;
    this.quickCompareOpen = false;
    void this.router.navigate(['/page-comparison'], {
      queryParams: {
        fullCompareYour: primary,
        fullCompareCompetitor: compare
      }
    });
  }

  private decodeUrlParam(raw: string): string {
    let value = (raw || '').trim();
    if (!value) return '';

    for (let i = 0; i < 3; i++) {
      try {
        const decoded = decodeURIComponent(value);
        if (decoded === value) break;
        value = decoded;
      } catch {
        break;
      }
    }

    return value;
  }

  private normalizeNavigableUrl(raw: string): string | null {
    const decoded = this.decodeUrlParam(raw);
    const trimmed = decoded.trim();
    if (!trimmed) return null;
    try {
      const u = new URL(trimmed);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
      return u.href;
    } catch {
      return null;
    }
  }
} 